import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  Layers, Hourglass, XCircle, MailWarning, CheckCircle2, Download, Upload, Send, Mail,
  RefreshCw, Ban, Banknote, Landmark, FileSpreadsheet, FileText, History, MessageSquare, PiggyBank, ListChecks,
  ChevronRight, ChevronDown, FilePlus2, Files, Receipt, AlertTriangle, Trash2, Undo2, ArrowRight, Info, Lock,
} from 'lucide-react';
import api from '../../../utils/api';
import {
  PageHeader, Panel, Money, Banner, Empty, LoadingBlock, ErrorBlock, Skeleton, Button, Tabs, TabPanel, Segmented,
  Sheet, Modal, ConfirmModal, ReasonModal, RowMenu, Field, Input, Textarea, Select, Checkbox, Tip, DataTable, Toolbar,
  SelectionBar, Facts, Steps, Meter, Pill, SearchInput, useLoad, downloadFile, errMsg, fmtDate, fmtDateTime, todayIso,
  money, words, hoursOf, tone as toneOf,
} from '../kit';
import { MoneySummary, useClaimFilters, FilterBar, ExportButtons, apiParams, sortingOf, onSortOf } from './claims/ClaimsParts';
import ManualLineModal from './claims/ManualLine';
import { useLogTime } from './LogTimeProvider';
import { dmy } from './timeLock';

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Claims and reconciliation (SC Workflow Specification v1.0, section 8; permissions 3.2; audit 3.3;
// numbering 3.5). One company-level page claims every participant in one batch: the NDIA bulk
// payment request file for NDIA-managed participants and one invoice per plan- or self-managed
// participant. Then: mark uploaded, import the NDIA results, re-claim or write off rejected lines,
// record payments and deposits, and follow up overdue invoices.
//
// ScClaims() — the Claims page. Ready to claim reads as three steps: 1 Check (the Fix list, grouped
//   by participant and root cause, and who is in the claim) → 2 Create batch (the claim period and
//   the one primary action) → 3 Generate & upload (in the batch).
// BudgetForecast({ coordinationId }) — the participant's support coordination budget forecast (8.9),
//   for the Plan & Budgets tab.
//
// Lines and Invoices are paged on the server (100 at a time, "Show more"); their filters, counts,
// search and footer totals come from the server. Payments: a bank statement is checked first (a dry
// run proposes matches; nothing is written), the user ticks what to record, and the receipts that did
// not match stay on a list to review until each is recorded or marked dealt with.
//
// Deep links: ?batch=<id> and ?invoice=<id> open those records; ?claims_tab= keeps the open tab.
// Every other param in the address is left alone. The server checks every permission again; the
// page only hides what the user cannot do (files with participant data download only with the
// matching permission, 3.3).
//
// Phones: nothing here may widen the page. Anything visually hidden (sr-only) sits inside a
// `relative` parent, so it can never escape a scrolling table and push the page sideways.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

// ── Vocabulary ────────────────────────────────────────────────────────────────────────────────
const FUNDING = {
  ndia: { label: 'NDIA-managed', tone: 'violet' },
  plan: { label: 'Plan-managed', tone: 'indigo' },
  self: { label: 'Self-managed', tone: 'slate' },
};
const OPEN_INVOICE = ['awaiting_payment', 'part_paid', 'overdue'];
const LINE_STATUS = {
  unclaimed: ['Unclaimed', 'amber'], in_batch: ['In batch', 'blue'], claimed: ['Claimed', 'blue'], paid: ['Paid', 'emerald'],
  capped: ['Capped', 'amber'], rejected: ['Rejected', 'red'], written_off: ['Written off', 'slate'], released: ['Claimed again', 'slate'],
};
// What a line is worth: "1.50 h", or "15 × $0.99" for a manual line claimed by quantity.
const qtyOf = (l) => (l.unit === 'each' ? `${Math.round(Number(l.quantity || 0) * 100) / 100} × ${money(l.unit_price != null ? l.unit_price : l.rate)}` : hrs(l.minutes));
const BATCH_STATUS = {
  draft: ['Draft', 'slate'], generated: ['Generated', 'blue'], uploaded: ['Uploaded', 'blue'],
  results_imported: ['Results imported', 'violet'], part_reconciled: ['Part reconciled', 'amber'], closed: ['Closed', 'emerald'],
};
const INVOICE_STATUS = {
  awaiting_payment: ['Awaiting payment', 'blue'], part_paid: ['Part paid', 'amber'], paid: ['Paid', 'emerald'],
  overdue: ['Overdue', 'red'], reissued: ['Reissued', 'slate'], written_off: ['Written off', 'slate'],
};
const CLAIM_TYPE = {
  '': 'Direct service', NF2F: 'Non-face-to-face', TRAN: 'Provider travel', REPW: 'NDIA-requested report',
  THLT: 'Telehealth', CANC: 'Short-notice cancellation',
};
const FOLLOWUP_METHODS = [
  { value: 'call', label: 'Phone call' }, { value: 'email', label: 'Email' }, { value: 'sms', label: 'SMS' },
  { value: 'letter', label: 'Letter' }, { value: 'portal', label: 'Portal or website' }, { value: 'query', label: 'Query or decline' },
  { value: 'other', label: 'Other' },
];
const FOLLOWUP_LABEL = Object.fromEntries(FOLLOWUP_METHODS.map((m) => [m.value, m.label]));
const PERIOD_KIND = { weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly' };
const READY_STEPS = ['Check', 'Create batch', 'Generate & upload'];
const STEP_LABELS = ['Draft', 'Generated', 'Uploaded', 'Results imported', 'Closed'];
const STEP_INDEX = { draft: 0, generated: 1, uploaded: 2, results_imported: 3, part_reconciled: 3, closed: STEP_LABELS.length };
const TAB_KEYS = ['ready', 'batches', 'lines', 'invoices', 'payments'];
const BATCH_PARAMS = { limit: 200 };
const DRAFT_PARAMS = { status: 'draft', limit: 50 };
const OPEN_INVOICE_PARAMS = { view: 'open', limit: 500 };
const IMPORT_PARAMS = { limit: 50 };
const NO_ROWS = [];
// Lines and Invoices load from the server a page at a time ("Show more").
const PAGE = 100;

// The server filters and counts these views (GET /sc/claims/lines?status=, /invoices?view=).
const LINE_VIEWS = [
  { value: 'rejected', label: 'Rejected', status: 'rejected', alert: true },
  { value: 'awaiting', label: 'Awaiting', status: 'awaiting' },
  { value: 'paid', label: 'Paid', status: 'paid' },
  { value: 'capped', label: 'Capped', status: 'capped' },
  { value: 'written_off', label: 'Written off', status: 'written_off' },
  { value: 'all', label: 'All' },
];
const lineViewCount = (counts, v) => {
  const c = counts || {};
  const n = (k) => Number(c[k]) || 0;
  if (v === 'awaiting') return n('in_batch') + n('claimed');
  if (v === 'all') return Object.keys(c).reduce((t, k) => t + n(k), 0);
  return n(v);
};
const INVOICE_VIEWS = [
  { value: 'open', label: 'Open' },
  { value: 'overdue', label: 'Overdue', alert: true },
  { value: 'paid', label: 'Paid' },
  { value: 'written_off', label: 'Written off' },
  { value: 'reissued', label: 'Reissued' },
  { value: 'all', label: 'All' },
];
// Why an unmatched bank receipt needs no match (8.8).
const CLEAR_REASONS = [
  { value: 'not_sc_payment', label: 'Not an SC payment', hint: 'For example a refund, a transfer between your accounts or another business’s money.' },
  { value: 'recorded_by_hand', label: 'Recorded by hand against an invoice', hint: 'The payment is already on the invoice.' },
  { value: 'other', label: 'Something else', hint: 'Say what was done with it.' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────────────────────
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const sumOf = (rows, f) => round2((rows || []).reduce((t, r) => t + (Number(f(r)) || 0), 0));
const plural = (n, one, many) => `${n} ${n === 1 ? one : (many || `${one}s`)}`;
const hrs = (mins) => `${hoursOf(mins)} h`;
const claimTypeText = (ct, reason) => `${CLAIM_TYPE[ct || ''] || ct}${ct === 'CANC' && reason ? ` (${reason})` : ''}`;
const periodText = (start, end) => (start ? `${fmtDate(start)} – ${fmtDate(end)}` : `Up to ${fmtDate(end)}`);
const errData = (e) => (e && e.response && e.response.data && typeof e.response.data === 'object' ? e.response.data : {});
// pg arrays arrive as arrays; tolerate the '{a,b}' text form too.
const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v !== 'string') return [];
  return v.replace(/^\{|\}$/g, '').split(',').map((x) => x.replace(/"/g, '').trim()).filter(Boolean);
};
const statusOf = (map, s) => map[s] || [words(s) || '—', 'slate'];
const isReadyable = (l) => !l.fix_only && !(l.errors || []).length;
const scrollToId = (id) => setTimeout(() => {
  const el = document.getElementById(id);
  if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}, 80);
const normRef = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const idSafe = (s) => String(s || '').replace(/[^A-Za-z0-9_-]/g, '');

// A value that settles after the user stops typing (server-side search).
function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

// A list the server pages: { rows, total, counts, totals }. The first page loads with the filters
// (any change starts again from the top); loadMore() appends the next page; reload() refreshes
// what is already shown. A stale response never overwrites a newer one.
function usePagedList(url, params, { max = 500 } = {}) {
  const key = url ? `${url}?${JSON.stringify(params || {})}` : '';
  const [st, setSt] = useState({ rows: NO_ROWS, total: 0, counts: null, totals: null, loading: !!url, more: false, error: null });
  const seq = useRef(0);
  const shown = useRef(0);
  shown.current = st.rows.length;
  const fetchPage = useCallback(async (offset, limit, append) => {
    if (!url) return;
    const my = ++seq.current;
    setSt((s) => ({ ...s, loading: !append, more: append, error: append ? s.error : null }));
    try {
      const r = await api.get(url, { params: { ...JSON.parse(key.slice(url.length + 1) || '{}'), limit, offset } });
      if (my !== seq.current) return;
      const d = r.data || {};
      const got = Array.isArray(d.rows) ? d.rows : [];
      setSt((s) => {
        const seen = new Set(s.rows.map((x) => x.id));
        return {
          rows: append ? [...s.rows, ...got.filter((x) => !seen.has(x.id))] : got,
          total: Number(d.total) || 0, counts: d.counts || null, totals: d.totals || null, loading: false, more: false, error: null,
        };
      });
    } catch (e) {
      if (my !== seq.current) return;
      if (append) { toast.error(errMsg(e, 'Could not load more. Please try again.')); setSt((s) => ({ ...s, more: false })); } else {
        setSt((s) => ({ ...s, loading: false, more: false, error: errMsg(e, 'Could not load this. Please try again.') }));
      }
    }
  }, [key, url]);
  useEffect(() => { fetchPage(0, PAGE, false); }, [fetchPage]);
  const reload = useCallback(() => fetchPage(0, Math.min(max, Math.max(PAGE, Math.ceil(shown.current / PAGE) * PAGE)), false), [fetchPage, max]);
  const loadMore = useCallback(() => fetchPage(shown.current, PAGE, true), [fetchPage]);
  return { ...st, reload, loadMore };
}

// ISO dates in UTC, so a period never drifts by a day.
const DEFAULT_ANCHOR = '2026-01-05';
const ymd = (iso) => String(iso || '').slice(0, 10).split('-').map(Number);
const utcOf = (iso) => { const [y, m, d] = ymd(iso); return Date.UTC(y, m - 1, d); };
const addDaysIso = (iso, n) => new Date(utcOf(iso) + n * 86400000).toISOString().slice(0, 10);
const daysBetweenIso = (a, b) => Math.round((utcOf(b) - utcOf(a)) / 86400000);
// Same rule as the server's claimPeriodFor (8.3): weekly or fortnightly from the anchor, or months.
function claimPeriodFor(iso, kind, anchor) {
  if (kind === 'monthly') {
    const [y, m] = ymd(iso);
    return { start: `${iso.slice(0, 7)}-01`, end: new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10) };
  }
  const len = kind === 'fortnightly' ? 14 : 7;
  const a = /^\d{4}-\d{2}-\d{2}/.test(String(anchor || '')) ? String(anchor).slice(0, 10) : DEFAULT_ANCHOR;
  const offset = ((daysBetweenIso(a, iso) % len) + len) % len;
  const start = addDaysIso(iso, -offset);
  return { start, end: addDaysIso(start, len - 1) };
}
function periodChoices(settings) {
  const today = todayIso();
  const kind = PERIOD_KIND[settings?.claim_period] ? settings.claim_period : 'weekly';
  const cur = claimPeriodFor(today, kind, settings?.claim_period_anchor);
  const last = claimPeriodFor(addDaysIso(cur.start, -1), kind, settings?.claim_period_anchor);
  return { kind, today, current: { start: cur.start, end: cur.end < today ? cur.end : today }, last };
}

// Ready-to-claim figures with the user's exclusions applied (8.4: excluded lines stay Unclaimed).
function claimMath(clients, fixList, isClientEx, isLineEx) {
  const m = {
    ready: 0, readyLines: 0, readyClients: 0, ndia: 0, ndiaLines: 0, plan: 0, planLines: 0, self: 0, selfLines: 0,
    invoices: 0, excluded: 0, excludedLines: 0, held: 0, heldLines: 0, beforePeriod: 0,
  };
  for (const c of clients || []) {
    let n = 0;
    for (const l of c.lines || []) {
      if (!isReadyable(l)) continue;
      const amt = Number(l.amount) || 0;
      if (isClientEx(c) || isLineEx(l)) { m.excluded += amt; m.excludedLines += 1; continue; }
      const ft = FUNDING[l.funding_type] ? l.funding_type : 'ndia';
      m.ready += amt; m.readyLines += 1; n += 1;
      m[ft] += amt; m[`${ft}Lines`] += 1;
      if (l.in_period === false) m.beforePeriod += 1;
    }
    if (n) { m.readyClients += 1; if (c.funding_type !== 'ndia') m.invoices += 1; }
  }
  for (const f of fixList || []) { m.held += Number(f.amount) || 0; m.heldLines += 1; }
  for (const k of ['ready', 'ndia', 'plan', 'self', 'excluded', 'held']) m[k] = round2(m[k]);
  return m;
}

// ── The Fix list: one root cause per held line ────────────────────────────────────────────────
// A line usually fails several checks for one reason: no service agreement also means no rate,
// no agreement covering the date, and an unfinished setup checklist. Each line is given the first
// cause below (most upstream first) that one of its reasons matches; its other reasons become
// "more checks", shown on request. Lines are then grouped by participant and cause, so the user
// sees one row and one Fix button per thing to fix.
const checklistSays = (re) => (c, m) => c === 'checklist' && re.test(m || '');
const CAUSES = [
  { id: 'adjustment', match: (c) => /^adjust_/.test(c || '') || c === 'no_time', title: 'A change to time that was already claimed', how: (c, m) => m,
    target: (c) => ({ adjust_paid: 'resolve', adjust_written_off: 'resolve', adjust_invoice: 'invoices', adjust_pending: 'batches' }[c] || 'notes') },
  { id: 'agreement', match: (c, m) => c === 'rate' || (c === 'dates' && /agreement/i.test(m || '')) || checklistSays(/agreement|hourly rate/i)(c, m),
    title: 'No service agreement with an hourly rate', how: 'Add the service agreement and its hourly rate in the participant’s setup checklist.', target: 'setup' },
  { id: 'ndis', match: (c, m) => c === 'ndis' || checklistSays(/NDIS number/i)(c, m),
    title: 'NDIS number missing or invalid', how: 'Add the 9-digit NDIS number in the participant’s setup checklist.', target: 'setup' },
  { id: 'plan_setup', match: checklistSays(/plan start/i),
    title: 'Plan dates missing', how: 'Add the plan start and end dates in the participant’s setup checklist.', target: 'setup' },
  { id: 'funding', match: checklistSays(/NDIA-managed|plan-managed|self-managed/i),
    title: 'How the plan is managed is not set', how: 'Choose NDIA-managed, plan-managed or self-managed in the setup checklist.', target: 'setup' },
  { id: 'recipient', match: (c, m) => c === 'pm_email' || checklistSays(/invoice goes to/i)(c, m),
    title: (c) => (c === 'pm_email' ? 'No plan manager email' : 'No one to send the invoice to'),
    how: 'Add who the invoice goes to, with their email, in the participant’s setup checklist.', target: 'setup' },
  { id: 'budget_line', match: (c, m) => c === 'budget_line' || checklistSays(/support item number/i)(c, m),
    title: 'No budget line for this support item', how: 'Add the support coordination support item and its budget in Plan & Budgets.', target: 'plan' },
  { id: 'coordinator', match: checklistSays(/support coordinator/i),
    title: 'No support coordinator assigned', how: 'Assign one in the participant’s setup checklist.', target: 'setup' },
  { id: 'setup', match: (c) => c === 'checklist',
    title: 'Setup checklist not finished', how: 'Finish the setup checklist on the participant’s file.', target: 'setup' },
  { id: 'plan_dates', match: (c) => c === 'dates',
    title: 'Service date is outside the plan dates', how: 'Check the plan dates, or the date on the time entry.', target: 'plan' },
  { id: 'price_limit', match: (c) => c === 'price_limit',
    title: 'Rate is above the NDIS price limit', how: (c, m) => `${m} Lower the hourly rate on the service agreement.`, target: 'setup' },
  { id: 'price_list', match: (c) => c === 'no_price_limit', title: 'Support item not in the price list', how: (c, m) => m, target: 'notes' },
  { id: 'budget', match: (c) => c === 'budget', title: 'Not enough budget left', how: (c, m) => m, target: 'plan' },
  { id: 'duplicate', match: (c) => c === 'duplicate', title: 'This time looks already claimed',
    how: (c, m) => `${m} Check the time entries for a double entry.`, target: 'notes' },
  { id: 'claim_type', match: (c) => c === 'claim_type', title: 'The activity type has no NDIA claim type', how: (c, m) => m, target: 'settings' },
  { id: 'entry', match: (c) => ['support_item', 'cancellation_reason', 'claim_file'].includes(c),
    title: 'The time entry needs correcting', how: (c, m) => m, target: 'notes' },
  { id: 'late', match: (c) => c === 'over_90', title: 'More than 90 days since the service',
    how: 'The NDIA expects claims within 90 days. Claiming users have been told.', target: 'notes' },
];
const PLAIN_REASON = {
  ndis: 'NDIS number missing or invalid.', budget_line: 'No budget line for this support item.',
  rate: 'No hourly rate on the service agreement.', pm_email: 'No plan manager email.', over_90: 'More than 90 days since the service.',
};
const plainReason = (code, msg) => (code === 'checklist'
  ? String(msg || '').replace(/^Client setup checklist not complete/i, 'Setup checklist not finished')
  : PLAIN_REASON[code] || msg);
const valueOf = (v, c, m) => (typeof v === 'function' ? v(c, m) : v);

// codes/reasons are parallel arrays (the Fix list's shape; a preview line's errors map to it).
function rootCause(codes, reasons) {
  const cs = codes || [];
  const ms = reasons || [];
  for (const cause of CAUSES) {
    const hits = cs.map((c, i) => i).filter((i) => cause.match(cs[i], ms[i]));
    if (!hits.length) continue;
    // Prefer the specific reason over the checklist's summary, so the summary (which may list
    // other missing setup items) stays in the follow-on checks.
    const named = hits.find((i) => cs[i] !== 'checklist');
    const i = named === undefined ? hits[0] : named;
    return { id: cause.id, index: i, title: valueOf(cause.title, cs[i], ms[i]), how: valueOf(cause.how, cs[i], ms[i]) || '', target: valueOf(cause.target, cs[i], ms[i]) };
  }
  return { id: `other-${cs[0] || 'check'}`, index: 0, title: ms[0] || 'Held by a pre-claim check', how: '', target: 'notes' };
}
const causeOfLine = (l) => rootCause((l.errors || []).map((e) => e.code), (l.errors || []).map((e) => e.message));

// minutesByKey: the preview lines' whole minutes, so a group's hours are summed before rounding.
function groupFixList(items, minutesByKey = {}) {
  const map = new Map();
  for (const it of items || []) {
    const root = rootCause(it.codes, it.reasons);
    const who = it.coordination_id || it.client_id || it.client_name;
    const key = `${who}|${root.id}${root.id === 'adjustment' ? `|${it.key}` : ''}`;
    let g = map.get(key);
    if (!g) {
      g = {
        key, cause: root, coordination_id: it.coordination_id, client_id: it.client_id, name: it.client_name || 'Participant',
        funding_type: it.funding_type, items: [], follow: [], escalate: false, minutes: 0, amount: 0, amountKnown: false,
        first: null, last: null, oldestDays: null,
      };
      map.set(key, g);
    }
    g.items.push(it);
    g.escalate = g.escalate || !!it.escalate;
    const mins = Number(minutesByKey[it.key]);
    g.minutes += Number.isFinite(mins) ? mins : Math.round((Number(it.hours) || 0) * 60);
    if (it.amount != null) { g.amount = round2(g.amount + (Number(it.amount) || 0)); g.amountKnown = true; }
    const d = String(it.service_date || '').slice(0, 10);
    if (d && (!g.first || d < g.first)) g.first = d;
    if (d && (!g.last || d > g.last)) g.last = d;
    if (it.days_since_service != null && (g.oldestDays == null || it.days_since_service > g.oldestDays)) g.oldestDays = it.days_since_service;
    const cs = it.codes || [];
    (it.reasons || []).forEach((m, i) => {
      if (i === root.index || (cs[i] === 'over_90' && it.escalate)) return;
      const text = plainReason(cs[i], m);
      if (text && !g.follow.includes(text)) g.follow.push(text);
    });
  }
  return [...map.values()].sort((a, b) => (Number(b.escalate) - Number(a.escalate))
    || ((b.oldestDays || 0) - (a.oldestDays || 0)) || a.name.localeCompare(b.name));
}

// Where each cause is fixed. Participant-file targets open the file (its setup banner leads to the
// checklist); company targets open another SC page; tab targets switch this page's tab.
const fileUrl = (coord, tab) => `/support-coordination/${coord}${tab ? `?tab=${tab}` : ''}`;
const TARGETS = {
  setup: { label: 'the setup checklist', url: (c) => `${fileUrl(c)}?setup=1` },
  notes: { label: 'the time entries', url: (c) => fileUrl(c, 'notes') },
  plan: { label: 'Plan & Budgets', url: (c) => fileUrl(c, 'plan') },
  hours: { label: 'SC Hours', url: () => '/support-coordination?view=hours' },
  settings: { label: 'Settings', url: () => '/support-coordination?view=settings' },
  invoices: { label: 'Invoices', tab: 'invoices' },
  batches: { label: 'Batches', tab: 'batches' },
};

// ── Small pieces ──────────────────────────────────────────────────────────────────────────────
function StatusPill({ map, status }) {
  const [label, t] = statusOf(map, status);
  return <Pill tone={t} label={label} />;
}
function FundingPill({ type }) {
  const f = FUNDING[type] || { label: words(type) || '—', tone: 'slate' };
  return <Pill tone={f.tone} label={f.label} />;
}
// Days since service (8.4): the NDIA's 90-day claiming limit, and the company's claim-age warning.
function DaysBadge({ days, warnDays = 60, settled = false }) {
  if (days == null) return null;
  if (!settled && days > 90) return <Pill tone="red" label={`${days} days · over 90`} />;
  if (!settled && days >= warnDays) return <Pill tone="amber" label={`${days} days · claim now`} />;
  return <span className="text-[12px] text-slate-500 tabular-nums">{plural(days, 'day')} ago</span>;
}
function TipText({ content, children }) {
  if (!content) return children;
  return (
    <Tip content={content}>
      <span tabIndex={0} className="inline-flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">{children}</span>
    </Tip>
  );
}
// The step number beside a Ready-to-claim heading, matching the Steps bar above it.
const StepNo = ({ n }) => (
  <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-[11px] font-bold inline-flex items-center justify-center flex-shrink-0" aria-hidden>{n}</span>
);
function Section({ title, sub, icon: Icon, actions, children, className, id }) {
  return (
    <section id={id} className={clsx('space-y-3 scroll-mt-4', className)}>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-[13.5px] font-semibold text-slate-900 flex items-center gap-1.5">
            {Icon && <Icon size={15} className="text-violet-600" aria-hidden />}{title}
          </h3>
          {sub && <p className="text-[12.5px] text-slate-500 mt-0.5 max-w-2xl">{sub}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}
// A compact row of figures for record panels (one strip, not a wall of tiles).
// items: [{ label, value, tone?, sub?, plain? }]; a zero value stays neutral.
function FigureStrip({ items }) {
  return (
    <dl className="flex flex-wrap gap-x-7 gap-y-3 rounded-xl bg-slate-50 px-4 py-3">
      {items.filter(Boolean).map((f) => {
        const n = Number(f.value) || 0;
        return (
          <div key={f.label} className="min-w-0">
            <dt className="text-[12px] font-medium text-slate-600">{f.label}</dt>
            <dd className={clsx('mt-0.5 text-[16px] font-semibold tabular-nums leading-tight', n && f.tone ? toneOf(f.tone).ink : 'text-slate-900')}>
              {f.plain ? f.value : <Money value={f.value} />}
            </dd>
            {f.sub && <dd className="text-[12px] text-slate-600">{f.sub}</dd>}
          </div>
        );
      })}
    </dl>
  );
}
// What happens next for a record: one sentence and the action buttons, in one place.
function NextStep({ tone: t = 'violet', title, children, actions }) {
  return (
    <div role="status" className={clsx('rounded-xl ring-1 ring-inset px-4 py-3.5', toneOf(t).tile)}>
      <p className="text-[14px] font-semibold text-slate-900">{title}</p>
      {children && <p className="mt-1 text-[13px] text-slate-700">{children}</p>}
      {actions && <div className="mt-3 flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
// The kit stepper on wider screens; on phones one line ("Step 2 of 5: Generated"), so no label is clipped.
function FlowSteps({ steps, current }) {
  const i = Math.min(current, steps.length - 1);
  return (
    <>
      <p className="sm:hidden text-[13px] text-slate-600">
        {current >= steps.length ? `All ${steps.length} steps done`
          : <>Step {i + 1} of {steps.length}: <span className="font-semibold text-slate-900">{steps[i]}</span></>}
      </p>
      <div className="max-sm:hidden min-w-0 max-w-full"><Steps steps={steps} current={current} /></div>
    </>
  );
}
// A filter of one list: a labelled picker on phones (it never widens the page), segments on wider screens.
let viewFilterSeq = 0;
function ViewFilter({ label, value, onChange, options }) {
  const [id] = useState(() => `sc-claims-vf-${++viewFilterSeq}`);
  return (
    <>
      <div className="sm:hidden w-full">
        <label htmlFor={id} className="block text-[12.5px] font-semibold text-slate-700 mb-1.5">{label}</label>
        <Select id={id} value={value} onChange={onChange}
          options={options.map((o) => ({ value: o.value, label: o.count > 0 ? `${o.label} (${o.count})` : o.label }))} />
      </div>
      <div className="max-sm:hidden max-w-full overflow-x-auto" role="group" aria-label={label}>
        <Segmented size="sm" value={value} onChange={onChange} options={options} />
      </div>
    </>
  );
}
function QuietLink({ onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className="font-semibold text-indigo-600 hover:text-indigo-800 underline-offset-2 hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
      {children}
    </button>
  );
}
// Under a server-paged list: how many are shown, and the next page.
function ShowMore({ list, noun }) {
  const shown = list.rows.length;
  const total = Number(list.total) || 0;
  if (!total || list.error || (list.loading && !shown)) return null;
  const nouns = (n) => (n === 1 ? noun : `${noun}s`);
  if (shown >= total) return total > PAGE ? <p className="mt-3 text-[12.5px] text-slate-500">All {total} {nouns(total)} shown.</p> : null;
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
      <p className="text-[12.5px] text-slate-600 tabular-nums" aria-live="polite">Showing {shown} of {total} {nouns(total)}</p>
      <Button size="sm" variant="secondary" icon={ChevronDown} loading={list.more} onClick={list.loadMore}>
        Show {Math.min(PAGE, total - shown)} more
      </Button>
    </div>
  );
}
// Files with participant data: the server names the file and checks the permission again.
function DownloadButton({ url, params, filename, children = 'Download', size = 'sm', variant = 'secondary', icon = Download, label, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    if (e) e.stopPropagation();
    setBusy(true);
    try {
      const name = await downloadFile(url, { params, filename });
      toast.success(`Downloaded ${name}`);
      if (onDone) onDone();
    } catch (err) {
      toast.error(errMsg(err, 'The download failed. Please try again.'));
    } finally { setBusy(false); }
  };
  return <Button size={size} variant={variant} icon={icon} loading={busy} onClick={go} aria-label={label}>{children}</Button>;
}
// Next to a claim file's Download button, with no extra click: how many entries the download locks
// (the server's lock_count), or when it was first downloaded. Re-downloading changes nothing.
function FileLockNote({ file }) {
  if (!file) return null;
  const n = Number(file.lock_count) || 0;
  if (!n && !file.downloaded_at) return null;
  return (
    <p className="basis-full flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-slate-600">
      {n > 0 && (
        <span className="inline-flex items-center gap-1 font-medium text-violet-800">
          <Lock size={12} aria-hidden />Downloading this file locks the time on {plural(n, 'entry', 'entries')}. Case notes stay editable.
        </span>
      )}
      {file.downloaded_at && <span>Downloaded {dmy(file.downloaded_at)}</span>}
    </p>
  );
}
// The same for a plan-managed or self-managed invoice's PDF download or email.
function InvoiceLockNote({ count }) {
  const n = Number(count) || 0;
  if (!n) return null;
  return (
    <p className="flex items-center gap-1 text-[12px] font-medium text-violet-800">
      <Lock size={12} aria-hidden />Downloading or sending this invoice locks the time on {plural(n, 'entry', 'entries')}. Case notes stay editable.
    </p>
  );
}
function FileInput({ label, hint, accept = '.csv,text/csv', onFile, resetKey, disabled }) {
  return (
    <Field label={label} hint={hint}>
      <input key={resetKey} type="file" accept={accept} disabled={disabled}
        onChange={(e) => onFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
        className="block w-full text-[13px] text-slate-600 rounded-xl file:mr-3 file:rounded-xl file:border-0 file:bg-violet-50 file:px-3.5 file:py-2 file:text-[13px] file:font-semibold file:text-violet-700 hover:file:bg-violet-100 file:cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50" />
    </Field>
  );
}
function OpenButton({ onClick, children, className, label }) {
  return (
    <button type="button" aria-label={label} onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={clsx('font-semibold text-slate-900 hover:text-indigo-700 text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500', className)}>
      {children}
    </button>
  );
}
// Email and Xero delivery for an invoice (colour plus words, never colour alone).
function DeliveryPills({ inv }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {inv.emailed_at ? <TipText content={`Emailed ${fmtDateTime(inv.emailed_at)}${inv.recipient_email ? ` to ${inv.recipient_email}` : ''}`}><Pill tone="emerald" label="Emailed" /></TipText>
        : inv.email_error ? <TipText content={inv.email_error}><Pill tone="red" label="Email failed" /></TipText>
          : <Pill tone="slate" label="Not emailed" />}
      {inv.xero_invoice_id ? <TipText content={`In Xero as ${words(inv.xero_status || 'draft').toLowerCase()}`}><Pill tone="emerald" label="Xero" /></TipText>
        : inv.xero_error ? <TipText content={inv.xero_error}><Pill tone="red" label="Xero failed" /></TipText> : null}
    </div>
  );
}
function Term({ label, value, cls }) {
  return (
    <span className="inline-flex flex-col">
      <span className="text-[12px] font-medium text-slate-600">{label}</span>
      <span className={clsx('tabular-nums font-semibold', cls)}>{money(value)}</span>
    </span>
  );
}
const Op = ({ children }) => <span className="text-slate-500 font-semibold self-end pb-px" aria-hidden>{children}</span>;
function TotalsCheck({ t, title = 'Totals check' }) {
  if (!t) return null;
  const off = !t.balanced && !(Number(t.pending) > 0);
  return (
    <div className={clsx('rounded-xl ring-1 ring-inset px-4 py-3', t.balanced ? 'bg-emerald-50/60 ring-emerald-100' : off ? 'bg-rose-50/60 ring-rose-100' : 'bg-sky-50/60 ring-sky-100')}>
      <p className="text-[12.5px] font-semibold text-slate-700">{title}</p>
      <p className="mt-1.5 flex flex-wrap items-end gap-x-2.5 gap-y-1.5 text-[13px] text-slate-700">
        <Term label="Paid" value={t.paid} cls="text-emerald-700" /><Op>+</Op>
        <Term label="Capped adjustments" value={t.cappedAdjustments} cls="text-amber-700" /><Op>+</Op>
        <Term label="Rejected or written off" value={t.rejected} cls="text-rose-700" />
        {Number(t.pending) > 0 && <><Op>+</Op><Term label="Awaiting a result" value={t.pending} cls="text-sky-700" /></>}
        <Op>=</Op><Term label="Claimed" value={t.claimed} cls="text-slate-900" />
      </p>
      <p className={clsx('mt-2 text-[12.5px] font-semibold flex items-center gap-1.5', t.balanced ? 'text-emerald-700' : off ? 'text-rose-700' : 'text-sky-700')}>
        {t.balanced ? <CheckCircle2 size={14} aria-hidden /> : off ? <AlertTriangle size={14} aria-hidden /> : <Hourglass size={14} aria-hidden />}
        {t.balanced ? 'Balanced: every line has a result.' : off ? 'The totals do not add up. Check the lines below.' : `${money(t.pending)} is still waiting for a result.`}
      </p>
    </div>
  );
}

// ── Budget forecast bar (8.9): paid emerald, pending blue, unclaimed amber, remaining slate ──
const SEGMENTS = [
  { k: 'paid', label: 'Paid', bar: 'bg-emerald-500' },
  { k: 'pending', label: 'Pending claims', bar: 'bg-sky-500' },
  { k: 'unclaimed', label: 'Unclaimed time', bar: 'bg-amber-400' },
  { k: 'remaining', label: 'Remaining', bar: 'bg-slate-300' },
];
function ForecastBar({ f, height = 'h-3', label }) {
  const pos = (v) => Math.max(0, Number(v) || 0);
  const budget = pos(f.budget);
  const vals = { paid: pos(f.paid), pending: pos(f.pending), unclaimed: pos(f.unclaimed), remaining: pos(f.remaining) };
  const used = vals.paid + vals.pending + vals.unclaimed;
  const scale = Math.max(budget, used) || 1;
  const over = budget > 0 && used > budget + 0.005;
  const text = `${label ? `${label}: ` : ''}budget ${money(budget)}, paid ${money(f.paid)}, pending ${money(f.pending)}, unclaimed ${money(f.unclaimed)}, `
    + (Number(f.remaining) < 0 ? `over budget by ${money(-Number(f.remaining))}` : `remaining ${money(f.remaining)}`);
  return (
    <div role="img" aria-label={text} className={clsx('relative w-full rounded-full bg-slate-100 overflow-hidden flex', height)}>
      {SEGMENTS.map((s) => (vals[s.k] > 0 ? (
        <div key={s.k} className={clsx('h-full transition-all duration-500', s.bar)} style={{ width: `${(vals[s.k] / scale) * 100}%` }} title={`${s.label}: ${money(f[s.k])}`} />
      ) : null))}
      {over && <div className="absolute inset-y-0 w-[2px] bg-rose-600" style={{ left: `calc(${(budget / scale) * 100}% - 1px)` }} title={`Budget ${money(budget)}`} aria-hidden />}
    </div>
  );
}
function ForecastLegend({ f }) {
  return (
    <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {SEGMENTS.map((s) => (
        <div key={s.k} className="min-w-0">
          <dt className="flex items-center gap-1.5 text-[12px] font-medium text-slate-600">
            <span className={clsx('w-2.5 h-2.5 rounded-sm flex-shrink-0', s.bar)} aria-hidden />{s.k === 'remaining' && Number(f.remaining) < 0 ? 'Over budget' : s.label}
          </dt>
          <dd className="mt-0.5 text-[15px] font-semibold tabular-nums text-slate-900"><Money value={f[s.k]} /></dd>
        </div>
      ))}
    </dl>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// The Claims page
// ═══════════════════════════════════════════════════════════════════════════════════════════════
export default function ScClaims() {
  const [params, setParams] = useSearchParams();
  const me = useLoad('/sc/me');
  const perms = me.data?.permissions;
  const can = useMemo(() => {
    const p = perms || [];
    return {
      claim: p.includes('claiming'), writeOff: p.includes('write_off'), recon: p.includes('reconciliation'),
      read: p.includes('claiming') || p.includes('reconciliation') || p.includes('view_all_hours'),
    };
  }, [perms]);
  const settings = useLoad(can.read ? '/sc/settings' : null);
  const warnDays = Number(settings.data?.claim_age_warning_days) || 60;

  // The claim period (8.1 step 1). All unclaimed time up to the period end is included.
  const [periodMode, setPeriodMode] = useState('last');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const choices = useMemo(() => periodChoices(settings.data), [settings.data]);
  const customError = periodMode === 'custom' && custom.start && custom.end && custom.start > custom.end ? 'The end date is before the start date.' : null;
  const period = periodMode === 'custom' ? { start: custom.start || null, end: custom.end || choices.today }
    : periodMode === 'current' ? choices.current : choices.last;
  const previewParams = useMemo(() => ({ period_start: period.start || undefined, period_end: period.end }), [period.start, period.end]);
  const settingsKnown = !!settings.data || !!settings.error;
  const preview = useLoad(can.read && settingsKnown && !customError ? '/sc/claims/preview' : null, previewParams);
  const overview = useLoad(can.read ? '/sc/claims/overview' : null);
  const batches = useLoad(can.read ? '/sc/claims/batches' : null, BATCH_PARAMS);
  // Bank statement imports (Payments tab): their receipts still to review count on the tab.
  const imports = useLoad(can.recon ? '/sc/claims/bank-imports' : null, IMPORT_PARAMS);

  const [exClients, setExClients] = useState(() => new Set());
  const [exLines, setExLines] = useState(() => new Set());
  const [signal, setSignal] = useState(0);
  const [resolving, setResolving] = useState(null);

  const { reload: reloadOverview } = overview;
  const { reload: reloadPreview } = preview;
  const { reload: reloadBatches } = batches;
  const { reload: reloadImports } = imports;
  const refresh = useCallback(() => {
    reloadOverview(); reloadPreview(); reloadBatches(); reloadImports(); setSignal((s) => s + 1);
  }, [reloadOverview, reloadPreview, reloadBatches, reloadImports]);

  const setParam = useCallback((patch) => setParams((p) => {
    const n = new URLSearchParams(p);
    Object.entries(patch).forEach(([k, v]) => { if (v == null || v === '') n.delete(k); else n.set(k, v); });
    return n;
  }, { replace: true }), [setParams]);

  const pv = preview.data;
  const math = useMemo(() => claimMath(pv?.clients, pv?.fixList, (c) => exClients.has(c.client_id), (l) => exLines.has(l.key)),
    [pv, exClients, exLines]);
  const pendingAdj = useMemo(() => {
    const m = {};
    for (const a of pv?.correctionPending || []) (m[a.claim_line_id] = m[a.claim_line_id] || []).push(a);
    return m;
  }, [pv]);

  const tabParam = params.get('claims_tab');
  const tab = TAB_KEYS.includes(tabParam) ? tabParam : 'ready';
  const batchId = params.get('batch');
  const invoiceId = params.get('invoice');
  const goTab = useCallback((t) => setParam({ claims_tab: t === 'ready' ? null : t }), [setParam]);
  const openBatch = useCallback((id) => setParam({ batch: id, invoice: null }), [setParam]);
  const openInvoice = useCallback((id) => setParam({ invoice: id }), [setParam]);

  const resolve = async (note) => {
    await api.post(`/sc/claims/adjustments/${resolving.adjustment_entry_id}/resolve`, { note });
    toast.success('Adjustment marked resolved. It stays on record and is never claimed.');
    refresh();
  };

  if (me.error) return <ErrorBlock error={me.error} onRetry={me.reload} />;
  if (!me.data) return <LoadingBlock rows={6} />;
  if (!can.read) {
    return (
      <div>
        <PageHeader title="Claims" />
        <Banner tone="amber" title="You can't open Claims">
          You need the Claiming, Reconciliation or View all hours permission. An admin can grant it in Support Coordination settings, under Permissions.
        </Banner>
      </div>
    );
  }

  const ov = overview.data;
  const o = ov || {};
  const allBatches = batches.data || NO_ROWS;
  const drafts = allBatches.filter((b) => b.status === 'draft');
  const inProgress = allBatches.filter((b) => !['draft', 'closed'].includes(b.status)).length;
  // Lines and Invoices are tabs only once there is something in them; until then the Batches tab
  // carries a quiet link to each.
  const hasLines = allBatches.some((b) => b.status !== 'draft' && Number(b.line_count) > 0) || Number(o.rejected) > 0;
  const hasInvoices = allBatches.some((b) => Number(b.invoice_count) > 0) || Number(o.overdueInvoices) > 0;
  const receiptsToReview = (imports.data || NO_ROWS).reduce((t, x) => t + (Number(x.to_review) || 0), 0);
  // The summary and the attention strip open the place to act, with its filter set.
  const onStrip = (where) => {
    if (where === 'rejected') { setParam({ claims_tab: 'lines', l_st: 'rejected' }); return; }
    if (where === 'overdue') { setParam({ claims_tab: 'invoices', i_view: 'overdue' }); return; }
    if (where === 'invoices') { setParam({ claims_tab: 'invoices', i_view: null }); return; }
    if (where === 'batches' || where === 'payments') { goTab(where); return; }
    goTab('ready');
    if (where === 'fix') scrollToId('sc-fix-list');
  };

  return (
    <div>
      <PageHeader title="Claims"
        sub="Claim every participant in one batch: one NDIA claim file, and an invoice for each plan- or self-managed participant."
        actions={<Button variant="secondary" icon={RefreshCw} loading={overview.loading || preview.loading} onClick={refresh}>Refresh</Button>} />

      <MoneySummary ready={math.ready} readyLoading={preview.loading && !preview.data} signal={signal} onGo={onStrip} />
      <ClaimsStrip ov={ov} loading={overview.loading} error={overview.error} onRetry={overview.reload} ready={0} onGo={onStrip} />

      <Tabs value={tab} onChange={goTab} label="Claims sections" tabs={[
        { value: 'ready', label: 'Ready to claim', icon: ListChecks, count: o.fixList, alert: Number(o.overLimit) > 0 },
        { value: 'batches', label: 'Batches', icon: Layers, count: drafts.length + inProgress },
        { value: 'lines', label: 'Lines', icon: Files, count: o.rejected, alert: true },
        { value: 'invoices', label: 'Invoices', icon: Receipt, count: o.overdueInvoices, alert: true },
        { value: 'payments', label: 'Payments', icon: Landmark, count: can.recon ? receiptsToReview : 0 },
      ].filter(Boolean)}>
        <TabPanel value="ready">
          <ReadyTab preview={preview} math={math} choices={choices} periodMode={periodMode} setPeriodMode={setPeriodMode}
            custom={custom} setCustom={setCustom} customError={customError} period={period} can={can} warnDays={warnDays}
            batches={batches.data} exClients={exClients} exLines={exLines} setExClients={setExClients} setExLines={setExLines}
            onOpenBatch={openBatch} onChanged={refresh} onResolve={setResolving} goTab={goTab} heldToDate={o.fixList} />
        </TabPanel>
        <TabPanel value="batches">
          <BatchesTab batches={batches} onOpen={openBatch} hasLines={hasLines} hasInvoices={hasInvoices} goTab={goTab} />
        </TabPanel>
        <TabPanel value="lines">
          <LinesTab can={can} warnDays={warnDays} batches={allBatches}
            signal={signal} preview={preview} pendingAdj={pendingAdj} onChanged={refresh} onOpenBatch={openBatch}
            onOpenInvoice={openInvoice} onResolve={setResolving} goTab={goTab} />
        </TabPanel>
        <TabPanel value="invoices">
          <InvoicesTab signal={signal} onOpen={openInvoice} can={can} onChanged={refresh} />
        </TabPanel>
        <TabPanel value="payments">
          <PaymentsLedger signal={signal} onOpenInvoice={openInvoice} onOpenBatch={openBatch} />
          {can.recon && <div className="mt-5"><PaymentsTab imports={imports} onChanged={refresh} onOpenInvoice={openInvoice} /></div>}
        </TabPanel>
      </Tabs>

      <BatchSheet id={batchId} open={!!batchId && !invoiceId} onClose={() => setParam({ batch: null })} can={can} warnDays={warnDays}
        onChanged={refresh} onOpenInvoice={openInvoice} pendingAdj={pendingAdj} />
      <InvoiceSheet id={invoiceId} open={!!invoiceId} onClose={() => setParam({ invoice: null })} can={can} onChanged={refresh}
        onOpenInvoice={openInvoice} onOpenBatch={openBatch} />

      <ReasonModal open={!!resolving} onClose={() => setResolving(null)} title="Mark the adjustment resolved"
        sub={resolving ? `${resolving.client_name || 'Participant'} · ${fmtDate(resolving.service_date)}. It stays on record and is never claimed.` : undefined}
        label="How was the original claim corrected?" confirmLabel="Mark resolved" onConfirm={resolve}
        extra={<p className="text-[12.5px] text-slate-500 mb-3">For example: “Cancelled in myplace on 14/10” or “Credit note sent to the plan manager”.</p>} />
    </div>
  );
}

// The page's counters as one compact strip. Only counts that need someone show; with none, one
// "All clear" line. Each item opens the place to act.
function ClaimsStrip({ ov, loading, error, onRetry, ready, onGo }) {
  if (error) return <div className="mb-5"><ErrorBlock error={error} onRetry={onRetry} /></div>;
  if (!ov && loading) return <Skeleton className="h-12 mb-5 rounded-2xl" />;
  const n = (k) => Number((ov || {})[k]) || 0;
  const items = [
    n('overLimit') && { key: 'over', tone: 'red', value: n('overLimit'), label: n('overLimit') === 1 ? 'line over 90 days' : 'lines over 90 days', go: 'fix' },
    n('rejected') && { key: 'rejected', tone: 'red', value: n('rejected'), label: n('rejected') === 1 ? 'rejected line' : 'rejected lines', go: 'rejected' },
    n('overdueInvoices') && { key: 'overdue', tone: 'red', value: n('overdueInvoices'), label: n('overdueInvoices') === 1 ? 'overdue invoice' : 'overdue invoices', go: 'overdue' },
    n('fixList') && { key: 'held', tone: 'amber', value: n('fixList'), label: n('fixList') === 1 ? 'held line to fix' : 'held lines to fix', go: 'fix' },
    n('nearLimit') && { key: 'near', tone: 'amber', value: n('nearLimit'), label: 'near the 90-day limit', go: 'ready' },
  ].filter(Boolean);
  return (
    <section aria-label="Claims at a glance" className="mb-5 flex flex-wrap items-center gap-1 rounded-2xl border border-slate-200/80 bg-white p-1.5">
      {ready > 0 && <StripItem tone="emerald" value={money(ready)} label="ready to claim" onClick={() => onGo('ready')} />}
      {items.map((it) => <StripItem key={it.key} tone={it.tone} value={it.value} label={it.label} onClick={() => onGo(it.go)} />)}
      {!items.length && (
        <p className="inline-flex items-center gap-2 px-2.5 py-2 text-[13px] text-slate-700">
          <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" aria-hidden />
          All clear: nothing held, late, rejected or overdue.
        </p>
      )}
    </section>
  );
}
function StripItem({ tone: t, value, label, onClick }) {
  const c = toneOf(t);
  return (
    <button type="button" onClick={onClick}
      className="inline-flex items-center gap-2 rounded-xl px-2.5 min-h-[44px] sm:min-h-[36px] text-[13px] hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
      <span className={clsx('w-2 h-2 rounded-full flex-shrink-0', c.dot)} aria-hidden />
      <span className={clsx('font-semibold tabular-nums', c.ink)}>{value}</span>
      <span className="text-slate-600">{label}</span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Ready to claim: 1 Check → 2 Create batch → 3 Generate & upload (8.1 steps 1-3, 8.4)
// ═══════════════════════════════════════════════════════════════════════════════════════════════
function ReadyTab({
  preview, math, choices, periodMode, setPeriodMode, custom, setCustom, customError, period, can, warnDays, batches,
  exClients, exLines, setExClients, setExLines, onOpenBatch, onChanged, onResolve, goTab, heldToDate,
}) {
  const pv = preview.data;
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(null);        // { line } | {} for a new manual line
  const [removing, setRemoving] = useState(null);
  const lineActions = useLineActions({ can, onChanged, onEditManual: (l) => setManual({ line: l }), onRemoveManual: setRemoving });
  const isClientEx = useCallback((c) => exClients.has(c.client_id), [exClients]);
  const isLineEx = useCallback((l) => exLines.has(l.key), [exLines]);
  const toggleClient = (c, include) => setExClients((s) => { const n = new Set(s); if (include) n.delete(c.client_id); else n.add(c.client_id); return n; });
  const toggleLine = (l, include) => setExLines((s) => { const n = new Set(s); if (include) n.delete(l.key); else n.add(l.key); return n; });
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post('/sc/claims/batches', {
        period_start: period.start || null, period_end: period.end, exclude_client_ids: [...exClients], exclude_line_keys: [...exLines],
      });
      toast.success(`Draft batch ${r.data.batch_code} created. Check it, then generate.`);
      setExClients(new Set()); setExLines(new Set());
      onChanged();
      onOpenBatch(r.data.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const pickMode = (v) => {
    if (v === 'custom' && !custom.end) setCustom({ start: period.start || '', end: period.end || '' });
    setPeriodMode(v);
  };

  const drafts = (batches || NO_ROWS).filter((b) => b.status === 'draft');
  const held = (pv?.fixList || []).length;
  const current = drafts.length ? 2 : math.readyLines > 0 ? 1 : 0;
  const next = drafts.length ? `Next: open draft ${drafts[0].batch_code} and generate it.`
    : math.readyLines > 0 ? 'Next: check who is in the claim, then create the batch.'
      : held ? 'Next: fix the held lines. Nothing is ready to claim yet.'
        : pv ? `Nothing to claim up to ${fmtDate(pv.period_end)}.` : '';
  const noCreate = customError
    || (preview.loading ? null
      : !math.readyLines ? (math.excludedLines ? 'Everything ready is left out. Tick a participant to include it.'
        : held ? 'Nothing is ready yet. Fix the lines on the Fix list first.' : 'Nothing is ready to claim in this period.')
        : null);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <FlowSteps steps={READY_STEPS} current={current} />
        {next && <p className="text-[13px] text-slate-600" aria-live="polite">{next}</p>}
      </div>
      {(pv?.blockers || []).length > 0 && (
        <Banner tone="red" title="Fix before generating">
          <ul className="list-disc pl-4 space-y-0.5">{pv.blockers.map((b) => <li key={b.code}>{b.message}</li>)}</ul>
        </Banner>
      )}
      {preview.error && <ErrorBlock error={preview.error} onRetry={preview.reload} />}

      {/* Wide screens: Check on the left, Create and Generate on the right (sticky). Phones: the
          Fix list, then Create, then the participant list, so the one primary action stays near. */}
      <div className="grid grid-cols-1 gap-5 items-start xl:grid-cols-[minmax(0,1fr)_340px] xl:grid-rows-[auto_1fr]">
        <div className="min-w-0 xl:col-start-1 xl:row-start-1">
          <FixList preview={preview} can={can} warnDays={warnDays} onResolve={onResolve} goTab={goTab} heldToDate={heldToDate} />
        </div>

        <aside className="min-w-0 grid grid-cols-1 gap-4 items-start md:grid-cols-2 xl:grid-cols-1 xl:col-start-2 xl:row-start-1 xl:row-span-2 xl:sticky xl:top-4">
          <section aria-labelledby="sc-claim-period" className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
            <div className="px-5 pt-4 pb-4 bg-violet-50/70 border-b border-violet-100">
              <h2 id="sc-claim-period" className="flex items-center gap-2 text-[13px] font-semibold text-slate-900">
                <StepNo n={2} />Claim period
              </h2>
              <div className="mt-3" role="group" aria-label="Claim period">
                <Segmented size="sm" value={periodMode} onChange={pickMode} options={[
                  { value: 'last', label: 'Last period' }, { value: 'current', label: 'This period' }, { value: 'custom', label: 'Custom' },
                ]} />
              </div>
              {periodMode === 'custom' ? (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Field label="From"><Input type="date" value={custom.start} max={choices.today} onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} /></Field>
                  <Field label="To" error={customError}><Input type="date" value={custom.end} max={choices.today} onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} /></Field>
                </div>
              ) : (
                <p className="mt-2.5 text-[14px] font-semibold text-slate-900 tabular-nums">{periodText(period.start, period.end)}</p>
              )}
              <p className="text-[12px] text-slate-600 mt-1">{PERIOD_KIND[choices.kind]} periods. Change them in Settings, under Claims & invoices.</p>
            </div>
            <div className="px-5 py-4">
              <p className="text-[12.5px] font-semibold text-slate-600">Ready to claim</p>
              <p className={clsx('mt-1 text-[34px] leading-none font-semibold tracking-[-0.02em]', math.ready ? 'text-emerald-700' : 'text-slate-500')}>
                <Money value={math.ready} animate />
              </p>
              <p className="mt-1.5 text-[12.5px] text-slate-500" aria-live="polite">
                {preview.loading ? 'Checking every unclaimed line…' : `${plural(math.readyLines, 'line')} for ${plural(math.readyClients, 'participant')}`}
              </p>
              <dl className="mt-4 space-y-2 text-[13px]">
                <SplitRow dot="bg-violet-500" label="NDIA claim file" count={math.ndiaLines} value={math.ndia} />
                <SplitRow dot="bg-indigo-500" label="Plan-managed invoices" count={math.planLines} value={math.plan} />
                <SplitRow dot="bg-slate-400" label="Self-managed invoices" count={math.selfLines} value={math.self} />
                <div className="border-t border-slate-100" aria-hidden />
                <SplitRow dot="bg-amber-500" label="Held on the Fix list" count={math.heldLines} value={math.held} muted />
                <SplitRow dot="bg-slate-300" label="Left out by you" count={math.excludedLines} value={math.excluded} muted />
              </dl>
              {can.claim ? (
                <>
                  <Button size="lg" className="w-full mt-5" icon={Layers} loading={busy} disabled={!math.readyLines || preview.loading || !!customError} onClick={create}>
                    Create claim batch
                  </Button>
                  <p className="mt-2 text-[12px] text-slate-500">
                    {noCreate || 'Creates a draft. Nothing is claimed until you generate it.'}
                    {!noCreate && math.beforePeriod > 0 && ` ${plural(math.beforePeriod, 'older line')} from before this period ${math.beforePeriod === 1 ? 'is' : 'are'} included so nothing is missed.`}
                  </p>
                </>
              ) : (
                <p className="mt-5 text-[12.5px] text-slate-600 rounded-xl bg-slate-50 px-3 py-2.5">You need the Claiming permission to create a claim batch.</p>
              )}
            </div>
          </section>
          <GenerateStep batches={batches} onOpenBatch={onOpenBatch} goTab={goTab} />
        </aside>

        <div className="min-w-0 xl:col-start-1 xl:row-start-2">
          <Panel title="Participants in this claim" icon={ListChecks} hue="violet"
            sub="Untick a participant or a line to leave it out. It stays unclaimed for next time. Use a line's menu to correct its time first."
            actions={can.claim ? <Button size="sm" variant="secondary" icon={FilePlus2} onClick={() => setManual({})}>Add a claim line</Button> : null}>
            {!pv && preview.loading ? <LoadingBlock rows={4} /> : !pv ? null : !(pv.clients || []).length ? (
              <Empty icon={CheckCircle2} title="Nothing to claim">There is no unclaimed billable time up to {fmtDate(pv.period_end)}.</Empty>
            ) : (
              <ParticipantGroups clients={pv.clients} isClientEx={isClientEx} isLineEx={isLineEx} onToggleClient={toggleClient}
                onToggleLine={toggleLine} readOnly={!can.claim} warnDays={warnDays} lineActions={can.claim ? lineActions : undefined} />
            )}
            {(pv?.correctionPending || []).length > 0 && (
              <p className="mt-4 text-[12.5px] text-slate-500 flex items-start gap-1.5">
                <Info size={14} className="mt-0.5 flex-shrink-0" aria-hidden />
                {plural(pv.correctionPending.length, 'time adjustment')} on rejected lines {pv.correctionPending.length === 1 ? 'goes' : 'go'} into the correction file when the line is re-claimed, not into a batch.
              </p>
            )}
          </Panel>
          <ManualLineModal open={!!manual} line={manual && manual.line} onClose={() => setManual(null)} onSaved={onChanged} />
          <ConfirmModal open={!!removing} onClose={() => setRemoving(null)} danger confirmLabel="Remove" title="Remove this manual line?"
            onConfirm={async () => { await api.delete(`/sc/claims/extras/${removing.extra_id}`); toast.success('Manual line removed'); onChanged(); }}>
            {removing ? `${removing.client_name || 'Participant'} · ${fmtDate(removing.service_date)} · ${money(removing.amount)}. It will not be claimed.` : null}
          </ConfirmModal>
        </div>
      </div>
    </div>
  );
}

function SplitRow({ dot, label, count, value, muted }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className={clsx('flex items-center gap-2 min-w-0', muted ? 'text-slate-500' : 'text-slate-700')}>
        <span className={clsx('w-2 h-2 rounded-full flex-shrink-0', dot)} aria-hidden />
        <span className="truncate">{label}</span>
        <span className="text-[12px] text-slate-500 tabular-nums">{count}</span>
      </dt>
      <dd className={clsx('tabular-nums font-semibold', muted ? 'text-slate-500' : 'text-slate-900')}><Money value={value} /></dd>
    </div>
  );
}

// Step 3: the batch that needs someone next (a draft, a file to upload, results to import).
function GenerateStep({ batches, onOpenBatch, goTab }) {
  const all = batches || NO_ROWS;
  const draft = all.find((b) => b.status === 'draft');
  const toUpload = all.find((b) => b.status === 'generated' && Number(b.ndia_total) > 0 && !b.uploaded_at);
  const toImport = all.find((b) => b.status === 'uploaded' && !b.results_imported_at);
  const b = draft || toUpload || toImport || null;
  const text = draft ? `Draft ${draft.batch_code} is waiting. Open it, check it, then generate the NDIA file and invoices.`
    : toUpload ? `Upload the NDIA file for ${toUpload.batch_code} in the myplace provider portal, then mark it uploaded.`
      : toImport ? `Import the NDIA results for ${toImport.batch_code} when the portal has them.`
        : 'After you create a batch, open it to generate the NDIA file and invoices. Then upload the file in the myplace provider portal.';
  return (
    <section aria-labelledby="sc-step-generate" className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4">
      <h2 id="sc-step-generate" className="flex items-center gap-2 text-[13px] font-semibold text-slate-900"><StepNo n={3} />Generate and upload</h2>
      <p className="mt-2 text-[13px] text-slate-600">{text}</p>
      {b ? (
        <Button variant="secondary" className="mt-3 w-full" icon={Layers} onClick={() => onOpenBatch(b.id)}>
          {b.status === 'draft' ? `Open draft ${b.batch_code}` : `Open ${b.batch_code}`}
        </Button>
      ) : (
        <p className="mt-2 text-[13px]"><QuietLink onClick={() => goTab('batches')}>View batches</QuietLink></p>
      )}
    </section>
  );
}

// Step 1, the Fix list (8.4): held lines grouped by participant and root cause, one Fix each.
function FixList({ preview, can, warnDays, onResolve, goTab, heldToDate }) {
  const items = preview.data?.fixList;
  const clients = preview.data?.clients;
  const groups = useMemo(() => {
    const minutes = {};
    for (const c of clients || []) for (const l of c.lines || []) minutes[l.key] = l.minutes;
    return groupFixList(items, minutes);
  }, [items, clients]);
  const [open, setOpen] = useState(() => new Set());
  const toggle = (k) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const list = items || NO_ROWS;
  const people = new Set(list.map((i) => i.client_id || i.coordination_id)).size;
  const late = list.filter((i) => i.escalate).length;
  const later = Math.max(0, (Number(heldToDate) || 0) - list.length);

  return (
    <section id="sc-fix-list" aria-labelledby="sc-fix-title" className="scroll-mt-4">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <StepNo n={1} />
        <h2 id="sc-fix-title" className="text-[15px] font-semibold text-slate-900">Fix list</h2>
        {list.length > 0 && <Pill tone="amber" label={`${plural(people, 'participant')} · ${plural(list.length, 'line')}`} />}
      </div>
      <p className="mt-1 mb-3 text-[13px] text-slate-500">Lines the checks held back. Fix each one and it joins the next batch.</p>

      {!preview.data ? (preview.loading ? <LoadingBlock rows={3} /> : null) : !groups.length ? (
        <p className="flex items-center gap-2 rounded-2xl border border-slate-200/80 bg-white px-4 py-3 text-[13.5px] text-slate-700">
          <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" aria-hidden />
          All clear. Every unclaimed line passes the checks.
        </p>
      ) : (
        <>
          {late > 0 && (
            <div className="mb-3">
              <Banner tone="red" title={`${plural(late, 'line is', 'lines are')} over 90 days old`}>
                Fix these first. The NDIA expects claims within 90 days of the service.
              </Banner>
            </div>
          )}
          <ul className="space-y-2">
            {groups.map((g) => {
              const isOpen = open.has(g.key);
              const moreId = `sc-fix-more-${idSafe(g.key)}`;
              const when = g.first === g.last ? fmtDate(g.first) : `${fmtDate(g.first)} – ${fmtDate(g.last)}`;
              return (
                <li key={g.key} className={clsx('rounded-xl border px-4 py-3.5', g.escalate ? 'border-rose-200 bg-rose-50/50' : 'border-slate-200/80 bg-white')}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <p className="text-[14px] font-semibold text-slate-900">{g.name}</p>
                        <FundingPill type={g.funding_type} />
                        {g.escalate ? <Pill tone="red" label="Over 90 days · escalated" />
                          : g.oldestDays != null && g.oldestDays >= warnDays ? <Pill tone="amber" label={`${g.oldestDays} days · claim now`} /> : null}
                      </div>
                      <p className="mt-0.5 text-[12.5px] text-slate-500 tabular-nums">
                        {plural(g.items.length, 'line')} · {hrs(g.minutes)} · {g.amountKnown ? money(g.amount) : 'No rate'} · {when}
                        {g.oldestDays != null && ` · ${plural(g.oldestDays, 'day')} ago`}
                      </p>
                      <div className="mt-2 flex items-start gap-2">
                        <AlertTriangle size={15} className={clsx('mt-0.5 flex-shrink-0', g.escalate ? 'text-rose-600' : 'text-amber-600')} aria-hidden />
                        <div className="min-w-0">
                          <p className="text-[13.5px] font-semibold text-slate-900">{g.cause.title}</p>
                          {g.cause.how && <p className="text-[13px] text-slate-600">{g.cause.how}</p>}
                        </div>
                      </div>
                      {g.follow.length > 0 && (
                        <div className="mt-1 pl-[23px]">
                          <button type="button" aria-expanded={isOpen} aria-controls={moreId} onClick={() => toggle(g.key)}
                            className="relative inline-flex items-center gap-1 py-1 text-[12.5px] font-semibold text-indigo-600 hover:text-indigo-800 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                            and {plural(g.follow.length, 'more check')}
                            <ChevronDown size={14} className={clsx('transition-transform', isOpen && 'rotate-180')} aria-hidden />
                          </button>
                          {isOpen && (
                            <ul id={moreId} className="mt-0.5 mb-1 space-y-1 list-disc pl-4 text-[12.5px] leading-snug text-slate-600">
                              {g.follow.map((m) => <li key={m}>{m}</li>)}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="sm:flex-shrink-0">
                      <FixAction group={g} can={can} onResolve={onResolve} goTab={goTab} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {later > 0 && preview.data && (
        <p className="mt-3 text-[12.5px] text-slate-500">
          {plural(later, 'more held line')} {later === 1 ? 'is' : 'are'} after this claim period. Choose This period to see {later === 1 ? 'it' : 'them'}.
        </p>
      )}
    </section>
  );
}

// The one action for a Fix list row: open the place the cause is fixed, or mark an adjustment resolved.
const FIX_BTN = 'relative inline-flex w-full sm:w-auto items-center justify-center gap-1.5 rounded-xl px-3.5 h-11 sm:h-9 text-[13px] font-semibold whitespace-nowrap bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 hover:ring-slate-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500';
function FixAction({ group, can, onResolve, goTab }) {
  if (group.cause.target === 'resolve') {
    if (!can.claim) return <p className="text-[12.5px] text-slate-500 sm:max-w-[180px]">Someone with the Claiming permission marks this resolved.</p>;
    return <Button variant="secondary" icon={CheckCircle2} className="w-full sm:w-auto" onClick={() => onResolve(group.items[0])}>Mark resolved</Button>;
  }
  const t = TARGETS[group.cause.target] || TARGETS.notes;
  const name = `Fix ${group.name}: open ${t.label}`;
  if (t.tab) {
    return <button type="button" className={FIX_BTN} aria-label={name} onClick={() => goTab(t.tab)}>Fix<ArrowRight size={14} aria-hidden /></button>;
  }
  return <Link to={t.url(group.coordination_id)} className={FIX_BTN} aria-label={name}>Fix<ArrowRight size={14} aria-hidden /></Link>;
}

// One row per participant and funding type (8.4), opening to its lines. Used by the Ready tab
// (exclusions held on the page) and a draft batch (exclusions saved on the batch).
// The menu on a line that is still to be claimed: correct its time (one entry opens the time sheet;
// several open the participant's Notes & Billing), or edit / remove a manual line. extra(l) adds more.
function useLineActions({ can, onChanged, onEditManual, onRemoveManual, extra }) {
  const logTime = useLogTime();
  const navigate = useNavigate();
  return useCallback((l) => {
    const items = [];
    if (l.manual) {
      items.push({ label: 'Edit manual line', icon: FileText, onClick: () => onEditManual(l) });
      items.push({ label: 'Remove manual line', icon: Trash2, danger: true, onClick: () => onRemoveManual(l) });
    } else if ((l.entry_ids || []).length === 1 && !l.fix_only) {
      items.push({ label: 'Edit the time entry', icon: FileText, onClick: async () => {
        try {
          const r = await api.get(`/sc/time-entries/${l.entry_ids[0]}`);
          logTime({ coordinationId: l.coordination_id, participantName: l.client_name, entry: r.data, onSaved: onChanged });
        } catch (e) { toast.error(errMsg(e)); }
      } });
    } else if ((l.entry_ids || []).length) {
      items.push({ label: `See the ${l.entry_ids.length} time entries`, icon: FileText, onClick: () => navigate(`/support-coordination/${l.coordination_id}`) });
    }
    return [...items, ...((extra && extra(l)) || [])];
  }, [logTime, navigate, onChanged, onEditManual, onRemoveManual, extra]);
}

function ParticipantGroups({ clients, isClientEx, isLineEx, onToggleClient, onToggleLine, readOnly, disabled, warnDays, lineActions }) {
  const [open, setOpen] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [view, setView] = useState('all');
  const rows = useMemo(() => (clients || []).map((c) => {
    const lines = c.lines || [];
    const readyable = lines.filter(isReadyable);
    const included = readyable.filter((l) => !isClientEx(c) && !isLineEx(l));
    return {
      c, key: `${c.client_id}|${c.funding_type}`, lines,
      readyable: readyable.length, held: lines.length - readyable.length, included: included.length,
      amount: sumOf(included, (l) => l.amount), minutes: included.reduce((t, l) => t + (Number(l.minutes) || 0), 0),
      warnings: lines.filter((l) => (l.warnings || []).length).length,
      budgetOver: (c.forecast || []).some((f) => f.over), budgetLow: (c.forecast || []).some((f) => f.warning),
    };
  }), [clients, isClientEx, isLineEx]);
  const counts = {
    all: rows.length, ready: rows.filter((r) => r.included).length, held: rows.filter((r) => r.held).length,
    warnings: rows.filter((r) => r.warnings || r.budgetLow).length, out: rows.filter((r) => r.readyable > r.included).length,
  };
  const readyLines = rows.reduce((t, r) => t + r.included, 0);
  const heldLines = rows.reduce((t, r) => t + r.held, 0);
  const shown = rows.filter((r) => {
    if (view === 'ready' && !r.included) return false;
    if (view === 'warnings' && !(r.warnings || r.budgetLow)) return false;
    if (view === 'held' && !r.held) return false;
    if (view === 'out' && !(r.readyable > r.included)) return false;
    const s = q.trim().toLowerCase();
    return !s || `${r.c.client_name || ''} ${r.c.ndis_number || ''}`.toLowerCase().includes(s);
  });
  const toggleOpen = (k) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between mb-2">
        <ViewFilter label="Show participants" value={view} onChange={setView} options={[
          { value: 'all', label: 'All', count: counts.all },
          { value: 'ready', label: 'Ready', count: counts.ready },
          { value: 'held', label: 'With held lines', count: counts.held },
          { value: 'warnings', label: 'Warnings', count: counts.warnings },
          { value: 'out', label: 'Left out', count: counts.out },
        ]} />
        <SearchInput value={q} onChange={setQ} placeholder="Search participants" className="w-full sm:w-60" />
      </div>
      <p className="mb-2 text-[12.5px] text-slate-500 tabular-nums">
        {plural(rows.length, 'participant')} · {plural(readyLines, 'line')} ready · {plural(heldLines, 'line')} held
      </p>
      {!shown.length ? <Empty title="No participants match">Try another filter or search.</Empty> : (
        <ul className="divide-y divide-slate-100 border-y border-slate-100">
          {shown.map((r) => {
            const { c } = r;
            const isOpen = open.has(r.key);
            const panelId = `sc-pg-${idSafe(r.key)}`;
            const name = c.client_name || 'Participant';
            const out = isClientEx(c);
            return (
              <li key={r.key}>
                <div className={clsx('flex flex-wrap items-center gap-x-3 gap-y-2 py-3 px-1', out && 'bg-slate-50/70')}>
                  <button type="button" aria-expanded={isOpen} aria-controls={panelId} onClick={() => toggleOpen(r.key)}
                    aria-label={`${isOpen ? 'Hide' : 'Show'} the lines for ${name}`}
                    className="p-2 sm:p-1 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                    <ChevronRight size={16} className={clsx('transition-transform', isOpen && 'rotate-90')} aria-hidden />
                  </button>
                  {!readOnly && (
                    <Checkbox aria-label={`Include ${name} in the batch`} checked={!out && r.readyable > 0} disabled={disabled || !r.readyable}
                      onChange={(v) => onToggleClient(c, v)} />
                  )}
                  <div className="min-w-[160px] flex-1">
                    <p className={clsx('font-semibold text-[13.5px]', out ? 'text-slate-500' : 'text-slate-900')}>{name}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      <FundingPill type={c.funding_type} />
                      {c.ndis_number && <span className="text-[12px] text-slate-500 tabular-nums">NDIS {c.ndis_number}</span>}
                      {out && <Pill tone="slate" label="Left out" />}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {r.included > 0 && <Pill tone="emerald" label={`${plural(r.included, 'line')} ready`} />}
                    {r.held > 0 && <Pill tone="amber" label={`${plural(r.held, 'line')} held`} />}
                    {r.warnings > 0 && <Pill tone="amber" label={plural(r.warnings, 'warning')} />}
                    {r.budgetOver ? <Pill tone="red" label="Over budget" /> : r.budgetLow ? <Pill tone="amber" label="Budget low" /> : null}
                  </div>
                  {r.included > 0 ? (
                    <div className="ml-auto flex items-center gap-4">
                      <span className="text-[13px] text-slate-600 tabular-nums">{hrs(r.minutes)}</span>
                      <Money value={r.amount} className="w-24 text-right font-semibold text-slate-900" />
                    </div>
                  ) : <span className="ml-auto text-[12.5px] text-slate-500">Nothing ready</span>}
                </div>
                {isOpen && (
                  <div id={panelId} className="pb-4 pl-2 sm:pl-10 pr-1 space-y-3">
                    {(c.forecast || []).length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {c.forecast.map((f) => (
                          <div key={`${f.coordination_id}-${f.key}`} className="rounded-xl bg-slate-50 px-3 py-2.5 space-y-1.5">
                            <div className="flex items-center justify-between gap-2 text-[12.5px]">
                              <span className="font-semibold text-slate-700 truncate">{f.name}</span>
                              <span className={clsx('tabular-nums font-semibold', Number(f.remaining) < 0 ? 'text-rose-600' : f.warning ? 'text-amber-700' : 'text-slate-700')}>
                                {Number(f.remaining) < 0 ? `${money(-Number(f.remaining))} over` : `${money(f.remaining)} left`}
                              </span>
                            </div>
                            <ForecastBar f={f} height="h-2" label={f.name} />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="overflow-x-auto">
                      <table className="w-full text-[13px] border-separate border-spacing-0">
                        <thead>
                          <tr className="text-left text-[12px] font-semibold text-slate-600">
                            {!readOnly && <th scope="col" className="relative w-8 pb-1.5"><span className="sr-only">Include</span></th>}
                            <th scope="col" className="pb-1.5 pr-3">Service date</th>
                            <th scope="col" className="pb-1.5 pr-3">Service</th>
                            <th scope="col" className="pb-1.5 pr-3 text-right">Hours</th>
                            <th scope="col" className="pb-1.5 pr-3 text-right">Rate</th>
                            <th scope="col" className="pb-1.5 pr-3 text-right">Amount</th>
                            <th scope="col" className="pb-1.5">Status</th>
                            {lineActions && <th scope="col" className="relative w-10 pb-1.5"><span className="sr-only">Actions</span></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {r.lines.map((l) => {
                            const ok = isReadyable(l);
                            const lineOut = ok && (out || isLineEx(l));
                            return (
                              <tr key={l.key} className={clsx('align-top', lineOut && 'opacity-60')}>
                                {!readOnly && (
                                  <td className="py-2 pr-2 border-t border-slate-100">
                                    {ok && <Checkbox aria-label={`Include the ${fmtDate(l.service_date)} line for ${name}`} checked={!isLineEx(l) && !out}
                                      disabled={disabled || out} onChange={(v) => onToggleLine(l, v)} />}
                                  </td>
                                )}
                                <td className="py-2 pr-3 border-t border-slate-100">
                                  <p className="whitespace-nowrap text-slate-800">{fmtDate(l.service_date)}</p>
                                  <div className="mt-0.5 flex flex-wrap gap-1">
                                    <DaysBadge days={l.days_since_service} warnDays={warnDays} />
                                    {l.in_period === false && <Pill tone="slate" label="Before this period" />}
                                  </div>
                                </td>
                                <td className="py-2 pr-3 border-t border-slate-100">
                                  <p className="text-slate-800">{claimTypeText(l.claim_type, l.cancellation_reason)}{l.adjustment ? ' · includes an adjustment' : ''}</p>
                                  <p className="text-[12px] text-slate-500 tabular-nums">{l.support_item || 'No support item'}</p>
                                  {l.manual && <p className="mt-0.5 flex flex-wrap items-center gap-1"><Pill tone="indigo" label="Manual line" size="xs" />{l.description && <span className="text-[12px] text-slate-500">{l.description}</span>}</p>}
                                </td>
                                <td className="py-2 pr-3 border-t border-slate-100 text-right tabular-nums whitespace-nowrap">{qtyOf(l)}</td>
                                <td className="py-2 pr-3 border-t border-slate-100 text-right tabular-nums">
                                  {l.rate != null ? money(l.rate) : <span className="text-slate-500">No rate</span>}
                                  {l.price_limit != null && <p className="text-[12px] text-slate-500 whitespace-nowrap">limit {money(l.price_limit)}</p>}
                                </td>
                                <td className="py-2 pr-3 border-t border-slate-100 text-right">{l.amount == null ? <span className="text-slate-500">—</span> : <Money value={l.amount} />}</td>
                                <td className="py-2 border-t border-slate-100 min-w-[200px]">
                                  {!ok ? <Pill tone="amber" label="Held" /> : lineOut ? <Pill tone="slate" label="Left out" /> : <Pill tone="emerald" label="Ready" />}
                                  {!ok && <p className="mt-1 text-[12px] leading-snug text-slate-600">{causeOfLine(l).title}. See the Fix list.</p>}
                                  {(l.warnings || []).map((w, i) => (
                                    <p key={i} className="mt-1 flex items-start gap-1 text-[12px] leading-snug text-amber-800">
                                      <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" aria-hidden />{w.message}
                                    </p>
                                  ))}
                                </td>
                                {lineActions && <td className="py-1 border-t border-slate-100 text-right"><RowMenu label={`Actions for the ${fmtDate(l.service_date)} line`} items={lineActions(l)} /></td>}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Batches (8.1, 8.2, 8.10)
// ═══════════════════════════════════════════════════════════════════════════════════════════════
const settledShare = (b) => {
  const lc = b.line_counts || {};
  const done = (Number(lc.paid) || 0) + (Number(lc.capped) || 0) + (Number(lc.written_off) || 0);
  return { done, total: Number(b.line_count) || 0 };
};

const BATCH_VIEWS = {
  all: () => true, draft: (b) => b.status === 'draft',
  open: (b) => !['draft', 'closed'].includes(b.status), closed: (b) => b.status === 'closed',
};

function BatchesTab({ batches, onOpen, hasLines, hasInvoices, goTab }) {
  const [view, setView] = useState('all');
  const all = batches.data || NO_ROWS;
  const rows = useMemo(() => all.filter(BATCH_VIEWS[view] || BATCH_VIEWS.all), [all, view]);
  const cols = [
    { accessorKey: 'batch_number', header: 'Batch', cell: ({ row: { original: b } }) => (
      <div>
        <OpenButton onClick={() => onOpen(b.id)}>{b.batch_code}</OpenButton>
        <p className="text-[12px] text-slate-500 whitespace-nowrap">Created {fmtDateTime(b.created_at)}</p>
      </div>
    ) },
    { id: 'period', header: 'Claim period', accessorFn: (b) => b.period_end, cell: ({ row: { original: b } }) => <span className="whitespace-nowrap">{periodText(b.period_start, b.period_end)}</span> },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <StatusPill map={BATCH_STATUS} status={getValue()} /> },
    { id: 'lines', header: 'Lines', accessorFn: (b) => Number(b.line_count) || 0, meta: { align: 'right' }, cell: ({ row: { original: b } }) => {
      const lc = b.line_counts || {};
      return (
        <div>
          <p>{b.status === 'draft' ? '—' : b.line_count}</p>
          {Number(lc.rejected) > 0 && <p className="text-[12px] font-semibold text-rose-700 whitespace-nowrap">{lc.rejected} rejected</p>}
        </div>
      );
    } },
    { id: 'ndia', header: 'NDIA file', accessorFn: (b) => Number(b.ndia_total) || 0, meta: { align: 'right' },
      cell: ({ row: { original: b } }) => (b.status === 'draft' ? '—' : <Money value={b.ndia_total} />) },
    { id: 'inv', header: 'Invoices', accessorFn: (b) => Number(b.invoiced_total) || 0, meta: { align: 'right' }, cell: ({ row: { original: b } }) => (b.status === 'draft' ? '—' : (
      <div><Money value={b.invoiced_total} /><p className="text-[12px] text-slate-500 whitespace-nowrap">{plural(Number(b.invoice_count) || 0, 'invoice')}</p></div>
    )) },
    { id: 'total', header: 'Claimed', accessorFn: (b) => Number(b.claimed_total) || 0, meta: { align: 'right' },
      cell: ({ row: { original: b } }) => (b.status === 'draft' ? '—' : <Money value={b.claimed_total} className="font-semibold text-slate-900" />) },
    { id: 'settled', header: 'Settled', enableSorting: false, meta: { width: 150 }, cell: ({ row: { original: b } }) => {
      if (b.status === 'draft') return <span className="text-[12px] text-slate-500 whitespace-nowrap">Not generated</span>;
      const s = settledShare(b);
      const pct = s.total ? Math.round((s.done / s.total) * 100) : 0;
      return (
        <div className="space-y-1">
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden" role="img" aria-label={`${s.done} of ${s.total} lines settled`}>
            <div className={clsx('h-full rounded-full', pct >= 100 ? 'bg-emerald-500' : 'bg-violet-500')} style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[12px] text-slate-500 tabular-nums whitespace-nowrap">{s.done} of {s.total} lines</p>
        </div>
      );
    } },
  ];
  const live = rows.filter((b) => b.status !== 'draft');
  const footer = rows.length ? (
    <>
      <td className="px-3 first:pl-4 py-2.5 border-t border-slate-200" colSpan={4}>Total ({plural(live.length, 'generated batch', 'generated batches')})</td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={sumOf(live, (b) => b.ndia_total)} /></td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={sumOf(live, (b) => b.invoiced_total)} /></td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={sumOf(live, (b) => b.claimed_total)} /></td>
      <td className="px-3 last:pr-4 py-2.5 border-t border-slate-200" />
    </>
  ) : null;
  return (
    <Panel title="Claim batches" icon={Layers} hue="violet"
      sub="Each batch claims everyone at once: one NDIA claim file and one invoice per plan- or self-managed participant.">
      <Toolbar>
        <div role="group" aria-label="Show batches" className="max-w-full overflow-x-auto">
          <Segmented size="sm" value={view} onChange={setView} options={[
            { value: 'all', label: 'All', count: all.length },
            { value: 'draft', label: 'Drafts', count: all.filter(BATCH_VIEWS.draft).length },
            { value: 'open', label: 'In progress', count: all.filter(BATCH_VIEWS.open).length },
            { value: 'closed', label: 'Closed', count: all.filter(BATCH_VIEWS.closed).length },
          ]} />
        </div>
      </Toolbar>
      <DataTable columns={cols} data={rows} loading={batches.loading} error={batches.error} onRetry={batches.reload}
        onRowClick={(b) => onOpen(b.id)} footer={footer} initialSort={[{ id: 'batch_number', desc: true }]}
        empty={<Empty icon={Layers} title={all.length ? 'No batches match' : 'No claim batches yet'}>
          {all.length ? 'Try another filter.' : 'Create the first batch from Ready to claim.'}
        </Empty>} />
      {batches.data && (!hasLines || !hasInvoices) && (
        <p className="mt-4 text-[12.5px] text-slate-500">
          {!hasLines ? 'Claim lines and invoices appear once a batch is generated.' : 'No invoices yet: everyone claimed so far is NDIA-managed.'}{' '}
          {!hasLines && <><QuietLink onClick={() => goTab('lines')}>View lines</QuietLink>{' · '}</>}
          <QuietLink onClick={() => goTab('invoices')}>View invoices</QuietLink>
        </p>
      )}
    </Panel>
  );
}

// ── A batch, in a side sheet ──────────────────────────────────────────────────────────────────
function BatchSheet({ id, open, onClose, can, warnDays, onChanged, onOpenInvoice, pendingAdj }) {
  const d = useLoad(id ? `/sc/claims/batches/${id}` : null);
  const { reload } = d;
  const [gen, setGen] = useState(null);           // the Generate response, shown until the sheet changes batch
  const [genError, setGenError] = useState(null);
  const [confirm, setConfirm] = useState(null);   // 'generate' | 'discard' | 'uploaded'
  const [patching, setPatching] = useState(false);
  useEffect(() => { setGen(null); setGenError(null); setConfirm(null); }, [id]);
  const changed = useCallback(() => { reload(); onChanged(); }, [reload, onChanged]);
  const b = d.data?.batch;
  const isDraft = b?.status === 'draft';
  const pv = isDraft ? d.data?.preview : null;
  const invoiceCount = (pv?.clients || []).filter((c) => c.funding_type !== 'ndia' && (c.lines || []).some((l) => l.ready)).length;
  const claimFileNames = (d.data?.files || []).filter((f) => f.kind === 'claim').map((f) => f.file_name).join(', ');

  const patch = async (body) => {
    setPatching(true);
    try { await api.patch(`/sc/claims/batches/${b.id}`, body); changed(); } catch (e) { toast.error(errMsg(e)); } finally { setPatching(false); }
  };
  // Generate: a refusal is shown in the draft (with its per-line reasons), so the modal closes either way.
  const generate = async () => {
    try {
      const r = await api.post(`/sc/claims/batches/${b.id}/generate`);
      setGen({ result: r.data, clients: pv?.clients || [] });
      setGenError(null);
      toast.success(`Batch ${b.batch_code} generated`);
      changed();
    } catch (e) { setGenError({ ...errData(e), message: errMsg(e) }); }
  };
  // Discard and Mark as uploaded: a refusal keeps the modal open with the reason (kit ConfirmModal).
  const discard = async () => {
    await api.delete(`/sc/claims/batches/${b.id}`);
    toast.success(`Draft ${b.batch_code} discarded. Its lines stay unclaimed.`);
    onChanged(); onClose();
  };
  const markUploaded = async () => {
    const r = await api.post(`/sc/claims/batches/${b.id}/uploaded`);
    toast.success(`Marked as uploaded: ${plural(Number(r.data?.lines_claimed) || 0, 'NDIA line')} now Claimed`);
    changed();
  };

  const [stLabel] = b ? statusOf(BATCH_STATUS, b.status) : [''];
  return (
    <Sheet open={open} onClose={onClose} icon={Layers} width="xl"
      title={b ? `Claim batch ${b.batch_code}` : 'Claim batch'}
      sub={b ? `${periodText(b.period_start, b.period_end)} · ${stLabel}` : undefined}
      footer={isDraft && can.claim ? (
        <>
          <Button variant="ghost" icon={Trash2} className="mr-auto text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => setConfirm('discard')}>Discard draft</Button>
          {/* Phones: the header's X closes the sheet, and the label shortens, so the footer fits on one line. */}
          <Button variant="secondary" className="max-sm:hidden" onClick={onClose}>Close</Button>
          <Button icon={Layers} disabled={!pv || !(pv.counts?.ready > 0) || patching} onClick={() => setConfirm('generate')}>
            <span className="sm:hidden">Generate</span><span className="max-sm:hidden">Generate NDIA file and invoices</span>
          </Button>
        </>
      ) : <Button variant="secondary" onClick={onClose}>Close</Button>}>
      {d.error ? <ErrorBlock error={d.error} onRetry={reload} /> : !d.data ? <LoadingBlock rows={6} /> : isDraft ? (
        <DraftBatch batch={b} preview={pv} can={can} warnDays={warnDays} patching={patching} onPatch={patch} genError={genError} onMoved={changed} />
      ) : (
        <LiveBatch key={b.id} data={d.data} can={can} warnDays={warnDays} gen={gen} onChanged={changed} onOpenInvoice={onOpenInvoice}
          onMarkUploaded={() => setConfirm('uploaded')} pendingAdj={pendingAdj} />
      )}

      <ConfirmModal open={confirm === 'generate'} onClose={() => setConfirm(null)} onConfirm={generate}
        title={b ? `Generate batch ${b.batch_code}?` : 'Generate this batch?'} confirmLabel="Generate">
        <p>This is the step that claims, and it can’t be undone. Corrections are made by re-claim, reissue or write-off.</p>
        {pv && (
          <ul className="mt-3 space-y-2.5 text-[13px] text-slate-700">
            <li className="flex gap-2.5"><FileSpreadsheet size={16} className="text-violet-600 mt-0.5 flex-shrink-0" aria-hidden />
              <span>{pv.counts?.ndia ? <>The NDIA claim file: <strong>{plural(pv.counts.ndia, 'line')}</strong>, <strong className="tabular-nums">{money(pv.totals?.ndia_ready_amount)}</strong>.</> : 'No NDIA-managed lines, so no NDIA file.'}</span></li>
            <li className="flex gap-2.5"><Receipt size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" aria-hidden />
              <span>{invoiceCount ? <><strong>{plural(invoiceCount, 'invoice')}</strong> ({money(pv.totals?.invoice_ready_amount)}), emailed as PDFs, saved to Communications and sent to Xero as drafts.</> : 'No plan- or self-managed lines, so no invoices.'}</span></li>
            <li className="flex gap-2.5"><History size={16} className="text-slate-500 mt-0.5 flex-shrink-0" aria-hidden />
              <span>Time locks when the NDIA file is downloaded and when each invoice is emailed or downloaded. Case notes stay editable. {pv.counts?.held ? `${plural(pv.counts.held, 'held line')} and ` : ''}{plural(pv.counts?.excluded || 0, 'left-out line')} stay unclaimed.</span></li>
          </ul>
        )}
      </ConfirmModal>
      <ConfirmModal open={confirm === 'discard'} onClose={() => setConfirm(null)} onConfirm={discard} danger
        title={b ? `Discard draft ${b.batch_code}?` : 'Discard this draft?'} confirmLabel="Discard draft">
        Nothing has been claimed. The draft is kept on record as discarded, and its lines stay unclaimed for the next batch.
      </ConfirmModal>
      <ConfirmModal open={confirm === 'uploaded'} onClose={() => setConfirm(null)} onConfirm={markUploaded}
        title="Mark the NDIA file as uploaded?" confirmLabel="Mark as uploaded">
        Do this after you have uploaded {claimFileNames || 'the claim file'} in the myplace provider portal. The NDIA lines change to Claimed while they wait for results.
      </ConfirmModal>
    </Sheet>
  );
}

function DraftBatch({ batch, preview, can, warnDays, patching, onPatch, genError, onMoved }) {
  const drafts = useLoad(can.claim ? '/sc/claims/batches' : null, DRAFT_PARAMS);
  const others = (drafts.data || NO_ROWS).filter((x) => x.id !== batch.id);
  const move = useCallback(async (l, to) => {
    try {
      const r = await api.post(`/sc/claims/batches/${batch.id}/move-lines`, { line_keys: [l.key], to_batch_id: to.id });
      toast.success(`Line moved to ${r.data.to}`);
      onMoved();
    } catch (e) { toast.error(errMsg(e)); }
  }, [batch.id, onMoved]);
  const extra = useCallback((l) => others.map((o) => ({ label: `Move to ${o.batch_code}`, icon: ArrowRight, onClick: () => move(l, o) })), [others, move]);
  const [manualDraft, setManualDraft] = useState(null);
  const [removingDraft, setRemovingDraft] = useState(null);
  const draftActions = useLineActions({ can, onChanged: onMoved, onEditManual: (l) => setManualDraft({ line: l }), onRemoveManual: setRemovingDraft, extra });
  const [p, setP] = useState({ start: '', end: '' });
  useEffect(() => { setP({ start: batch.period_start ? String(batch.period_start).slice(0, 10) : '', end: batch.period_end ? String(batch.period_end).slice(0, 10) : '' }); },
    [batch.period_start, batch.period_end]);
  const exC = useMemo(() => new Set(asArray(batch.exclude_client_ids)), [batch.exclude_client_ids]);
  const exL = useMemo(() => new Set(asArray(batch.exclude_line_keys)), [batch.exclude_line_keys]);
  const isClientEx = useCallback((c) => exC.has(c.client_id), [exC]);
  const isLineEx = useCallback((l) => exL.has(l.key), [exL]);
  const toggleClient = (c, include) => { const n = new Set(exC); if (include) n.delete(c.client_id); else n.add(c.client_id); onPatch({ exclude_client_ids: [...n] }); };
  const toggleLine = (l, include) => { const n = new Set(exL); if (include) n.delete(l.key); else n.add(l.key); onPatch({ exclude_line_keys: [...n] }); };
  const periodChanged = p.end && (p.start !== (batch.period_start ? String(batch.period_start).slice(0, 10) : '') || p.end !== String(batch.period_end || '').slice(0, 10));
  const badPeriod = p.start && p.end && p.start > p.end;
  const pv = preview || {};
  const counts = pv.counts || {};
  const totals = pv.totals || {};
  const invoiceClients = (pv.clients || []).filter((c) => c.funding_type !== 'ndia' && (c.lines || []).some((l) => l.ready)).length;

  return (
    <div className="space-y-5">
      <FlowSteps steps={STEP_LABELS} current={0} />
      {counts.ready > 0 ? (
        <NextStep title="Next: check this draft, then generate it">
          {can.claim
            ? 'Nothing is claimed yet. When the participants below look right, press Generate at the bottom.'
            : 'Nothing is claimed yet. Someone with the Claiming permission checks it and generates it.'}
        </NextStep>
      ) : (
        <NextStep tone="amber" title="Nothing in this draft is ready to claim">
          Fix the lines on the Fix list, include a participant, or change the period. Or discard the draft.
        </NextStep>
      )}
      {genError && (
        <Banner tone="red" title="Nothing was generated">
          <p>{genError.message}</p>
          {Array.isArray(genError.errors) && genError.errors.length > 0 && (
            <ul className="mt-2 list-disc pl-4 space-y-1">
              {genError.errors.map((e, i) => <li key={i}><strong>{e.participant || e.invoice_number || 'Line'}</strong>: {(e.problems || []).join(' ')}</li>)}
            </ul>
          )}
          {Array.isArray(genError.fixList) && genError.fixList.length > 0 && <p className="mt-1">{plural(genError.fixList.length, 'line is', 'lines are')} on the Fix list.</p>}
        </Banner>
      )}
      {(pv.blockers || []).length > 0 && (
        <Banner tone="red" title="Fix before generating">
          <ul className="list-disc pl-4 space-y-0.5">{pv.blockers.map((x) => <li key={x.code}>{x.message}</li>)}</ul>
        </Banner>
      )}

      <FigureStrip items={[
        { label: 'Ready to claim', value: totals.ready_amount, tone: 'emerald', sub: plural(counts.ready || 0, 'line') },
        { label: 'NDIA file', value: totals.ndia_ready_amount, sub: plural(counts.ndia || 0, 'line') },
        { label: 'Invoices', value: totals.invoice_ready_amount, sub: plural(invoiceClients, 'invoice') },
        { label: 'Held on the Fix list', value: counts.held || 0, plain: true, tone: 'amber', sub: 'Stay unclaimed' },
        { label: 'Left out', value: counts.excluded || 0, plain: true, sub: 'Stay unclaimed' },
      ]} />

      <Section title="Claim period" sub="All unclaimed time up to the period end is included, so older time is never left behind.">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="From" className="w-full sm:w-44"><Input type="date" value={p.start} disabled={!can.claim} max={todayIso()} onChange={(e) => setP((x) => ({ ...x, start: e.target.value }))} /></Field>
          <Field label="To" className="w-full sm:w-44" error={badPeriod ? 'Before the start date.' : undefined}>
            <Input type="date" value={p.end} disabled={!can.claim} max={todayIso()} onChange={(e) => setP((x) => ({ ...x, end: e.target.value }))} />
          </Field>
          {can.claim && <Button variant="secondary" loading={patching} disabled={!periodChanged || badPeriod} onClick={() => onPatch({ period_start: p.start || null, period_end: p.end })}>Update period</Button>}
        </div>
      </Section>

      <Section title="Participants" icon={ListChecks}
        sub={can.claim ? 'Untick to leave a participant or line out of this batch. Changes save straight away.' : 'What this batch will claim.'}>
        {!(pv.clients || []).length ? <Empty title="Nothing to claim in this period" /> : (
          <ParticipantGroups clients={pv.clients} isClientEx={isClientEx} isLineEx={isLineEx} onToggleClient={toggleClient}
            onToggleLine={toggleLine} readOnly={!can.claim} disabled={patching} warnDays={warnDays} lineActions={can.claim ? draftActions : undefined} />
        )}
      </Section>
      <ManualLineModal open={!!manualDraft} line={manualDraft && manualDraft.line} onClose={() => setManualDraft(null)} onSaved={onMoved} />
      <ConfirmModal open={!!removingDraft} onClose={() => setRemovingDraft(null)} danger confirmLabel="Remove" title="Remove this manual line?"
        onConfirm={async () => { await api.delete(`/sc/claims/extras/${removingDraft.extra_id}`); toast.success('Manual line removed'); onMoved(); }}>
        {removingDraft ? `${removingDraft.client_name || 'Participant'} · ${fmtDate(removingDraft.service_date)} · ${money(removingDraft.amount)}. It will not be claimed.` : null}
      </ConfirmModal>
    </div>
  );
}

// A generated batch: the stepper, then the one next action for its status.
function LiveBatch({ data, can, warnDays, gen, onChanged, onOpenInvoice, onMarkUploaded, pendingAdj }) {
  const b = data.batch;
  const files = data.files || NO_ROWS;
  const lines = data.lines || NO_ROWS;
  const invoices = data.invoices || NO_ROWS;
  const totals = data.totals || {};
  const hasNdia = useMemo(() => lines.some((l) => l.funding_type === 'ndia'), [lines]);
  const [sub, setSub] = useState(hasNdia ? 'ndia' : 'invoices');
  const rejected = lines.filter((l) => l.status === 'rejected').length;
  const all = totals.all || {};
  const claimFiles = files.filter((f) => f.kind === 'claim' || f.kind === 'correction');
  const results = files.filter((f) => f.kind === 'results');
  const go = (t, anchor) => { setSub(t); if (anchor) scrollToId(anchor); };

  const mainFiles = files.filter((f) => f.kind === 'claim');
  const names = mainFiles.map((f) => f.file_name).join(', ') || 'the claim file';
  const corrections = files.filter((f) => f.kind === 'correction' && !f.uploaded_at);
  const openInvoices = invoices.filter((i) => OPEN_INVOICE.includes(i.status));
  const depositGap = round2(totals.ndia_deposit_difference);
  let next;
  if (b.status === 'closed') {
    next = { tone: 'emerald', title: 'Batch closed', text: 'Every line is paid, capped or written off, and every invoice is settled.' };
  } else if (hasNdia && !b.uploaded_at) {
    next = {
      title: 'Next: upload the NDIA file', text: `Download ${names}, upload it in the myplace provider portal, then mark it as uploaded.`,
      actions: can.claim ? (
        <>
          {mainFiles.length === 1
            ? <DownloadButton url={`/sc/claims/files/${mainFiles[0].id}`} filename={mainFiles[0].file_name} size="md" label={`Download ${mainFiles[0].file_name}`} onDone={onChanged}>Download file</DownloadButton>
            : <Button variant="secondary" icon={FileSpreadsheet} onClick={() => go('ndia', 'sc-ndia-files')}>Go to the files</Button>}
          <Button icon={CheckCircle2} onClick={onMarkUploaded}>Mark as uploaded</Button>
          {mainFiles.length === 1 && <FileLockNote file={mainFiles[0]} />}
        </>
      ) : null,
    };
  } else if (corrections.length) {
    next = {
      title: 'Next: upload the correction file', text: `Upload ${corrections.map((f) => f.file_name).join(', ')} in the myplace provider portal, then mark it uploaded.`,
      actions: <Button icon={FileSpreadsheet} onClick={() => go('ndia', 'sc-ndia-files')}>Go to the files</Button>,
    };
  } else if (hasNdia && !b.results_imported_at) {
    next = {
      title: 'Next: import the NDIA results',
      text: 'When the portal has processed the file, download the results (View Payment Request › Uploaded Bulk Payment File › Download Results) and import them here.',
      actions: can.claim ? <Button icon={Upload} onClick={() => go('ndia', 'sc-results-import')}>Import results</Button> : null,
    };
  } else if (rejected) {
    next = {
      title: `Next: re-claim or write off ${plural(rejected, 'rejected line')}`,
      text: 'Fix the reason the NDIA gave, then re-claim the line in a correction file, or write it off with a reason.',
      actions: <Button icon={Files} onClick={() => setSub('lines')}>Open rejected lines</Button>,
    };
  } else if (hasNdia && depositGap > 0) {
    next = {
      title: 'Next: record the NDIA deposit', text: `${money(depositGap)} paid in the results is not matched to a deposit yet. Record it here, or match it in Xero.`,
      actions: can.recon ? <Button icon={Landmark} onClick={() => go('ndia', 'sc-deposits')}>Record deposit</Button> : null,
    };
  } else if (openInvoices.length) {
    next = {
      title: `Next: record payments for ${plural(openInvoices.length, 'invoice')}`, text: 'Payments come in through Xero, Record payment, or a bank statement import.',
      actions: <Button icon={Receipt} onClick={() => setSub('invoices')}>Open invoices</Button>,
    };
  } else {
    next = { title: 'Waiting for the last results', text: 'The batch closes when every line and invoice is settled.' };
  }

  return (
    <div className="space-y-5">
      <FlowSteps steps={STEP_LABELS} current={STEP_INDEX[b.status] ?? 1} />
      <NextStep tone={next.tone} title={next.title} actions={next.actions}>{next.text}</NextStep>
      {gen && <GenerateResult r={gen.result} clients={gen.clients} can={can} batchId={b.id} liveFiles={files} onChanged={onChanged} />}

      <FigureStrip items={[
        { label: 'Claimed', value: b.claimed_total, sub: `NDIA ${money(b.ndia_total)} · invoices ${money(b.invoiced_total)}` },
        { label: 'Paid', value: all.paid, tone: 'emerald' },
        { label: 'Capped adjustments', value: all.cappedAdjustments, tone: 'amber' },
        { label: 'Rejected or written off', value: all.rejected, tone: 'red' },
        { label: 'Awaiting', value: all.pending, tone: 'blue', sub: 'A result or a payment' },
      ]} />

      <Tabs variant="pill" value={sub} onChange={setSub} label="Batch sections" tabs={[
        hasNdia && { value: 'ndia', label: 'NDIA file and results', icon: FileSpreadsheet },
        { value: 'invoices', label: 'Invoices', icon: Receipt, count: invoices.filter((i) => i.status !== 'reissued').length },
        { value: 'lines', label: 'Lines', icon: Files, count: rejected || lines.length, alert: rejected > 0 },
        { value: 'history', label: 'History', icon: History },
      ].filter(Boolean)}>
        {hasNdia && (
          <TabPanel value="ndia">
            <div className="space-y-7">
              <TotalsCheck t={totals.ndia} title="NDIA totals check: Paid + Capped adjustments + Rejected = Claimed" />
              <Section id="sc-ndia-files" title="NDIA claim files" icon={FileSpreadsheet}
                sub="Upload each file in the myplace provider portal. A correction file holds only the re-claimed lines."
                actions={can.claim && !b.uploaded_at && <Button size="sm" variant="secondary" icon={CheckCircle2} onClick={onMarkUploaded}>Mark as uploaded</Button>}>
                <FilesList files={claimFiles} can={can} onChanged={onChanged} />
              </Section>
              <ResultsImport batch={b} can={can} onImported={onChanged} />
              {results.length > 0 && <ResultsHistory files={results} can={can} />}
              <Deposits batch={b} deposits={data.deposits || NO_ROWS} totals={totals} can={can} onChanged={onChanged} />
            </div>
          </TabPanel>
        )}
        <TabPanel value="invoices">
          <BatchInvoices batch={b} invoices={invoices} can={can} onOpenInvoice={onOpenInvoice} />
        </TabPanel>
        <TabPanel value="lines">
          <LinesTable lines={lines} can={can} warnDays={warnDays} pendingAdj={pendingAdj} onChanged={onChanged} onOpenInvoice={onOpenInvoice} showBatch={false}
            empty={<Empty icon={Files} title="No lines" />} />
        </TabPanel>
        <TabPanel value="history">
          <AuditList items={data.audit || NO_ROWS} />
        </TabPanel>
      </Tabs>
    </div>
  );
}

// What Generate did (8.1 step 3, 8.2 steps 3-4): files, invoices emailed and sent to Xero, errors.
function GenerateResult({ r, clients, can, batchId, liveFiles, onChanged }) {
  const liveOf = (f) => (liveFiles || []).find((x) => x.id === f.id) || f;
  const nameOf = (cid) => ((clients || []).find((c) => c.client_id === cid) || {}).client_name || 'Participant';
  const invoices = r.invoices || [];
  const emailFails = invoices.filter((i) => !i.emailed).length;
  const xeroFails = invoices.filter((i) => i.xero && !i.xero.ok).length;
  return (
    <div className="rounded-2xl ring-1 ring-inset ring-emerald-100 bg-emerald-50/40 p-4 space-y-4">
      <div className="flex items-start gap-3">
        <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" aria-hidden />
        <div className="text-[13px] text-slate-700">
          <p className="font-semibold text-slate-900 text-[14px]">Generated just now</p>
          <p className="mt-0.5">
            {r.ndia?.lines ? `NDIA file: ${plural(r.ndia.lines, 'line')}, ${money(r.ndia.total)}. ` : 'No NDIA-managed lines. '}
            {invoices.length ? `${plural(invoices.length, 'invoice')} created. ` : ''}
            {r.held ? `${plural(r.held, 'line')} stay on the Fix list. ` : ''}
            {r.excluded ? `${plural(r.excluded, 'left-out line')} stay unclaimed.` : ''}
          </p>
        </div>
      </div>
      {(r.files || []).length > 0 && (
        <ul className="space-y-2">
          {r.files.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-white ring-1 ring-inset ring-slate-200 px-3.5 py-2.5">
              <FileSpreadsheet size={17} className="text-violet-600" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[13px] text-slate-900 break-all">{f.file_name}</p>
                <p className="text-[12px] text-slate-500">{plural(Number(f.row_count) || 0, 'line')}{f.part > 1 ? ` · part ${f.part}` : ''}</p>
              </div>
              {can.claim && <DownloadButton url={`/sc/claims/files/${f.id}`} filename={f.file_name} label={`Download ${f.file_name}`} onDone={onChanged}>Download</DownloadButton>}
              {can.claim && <FileLockNote file={liveOf(f)} />}
            </li>
          ))}
        </ul>
      )}
      {invoices.length > 0 && (
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-inset ring-slate-200">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[12px] font-semibold text-slate-600 border-b border-slate-100">
                <th scope="col" className="px-3 py-2">Invoice</th><th scope="col" className="px-3 py-2">Participant</th>
                <th scope="col" className="px-3 py-2 text-right">Total</th><th scope="col" className="px-3 py-2">Email</th><th scope="col" className="px-3 py-2">Xero</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id} className="border-b border-slate-100 last:border-0 align-top">
                  <td className="px-3 py-2 font-semibold text-slate-900 whitespace-nowrap">{i.invoice_number}</td>
                  <td className="px-3 py-2"><p>{nameOf(i.client_id)}</p><FundingPill type={i.funding_type} /></td>
                  <td className="px-3 py-2 text-right"><Money value={i.total} /></td>
                  <td className="px-3 py-2">
                    {i.emailed ? <Pill tone="emerald" label="Emailed" /> : <Pill tone="red" label="Not emailed" />}
                    <p className="text-[12px] text-slate-500 mt-0.5 break-words">{i.emailed ? i.recipient_email : i.email_error}</p>
                  </td>
                  <td className="px-3 py-2">
                    {!i.xero ? <span className="text-slate-500">Not sent</span> : i.xero.ok ? <Pill tone="emerald" label="Draft in Xero" /> : (
                      <><Pill tone="red" label="Failed" /><p className="text-[12px] text-rose-700 mt-0.5 break-words">{i.xero.error}</p></>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {r.xero?.connected === false && invoices.length > 0 && (
        <Banner tone="blue" title="Xero is not connected"
          action={can.claim && <DownloadButton url={`/sc/claims/batches/${batchId}/xero-invoices.csv`} icon={FileSpreadsheet}>Xero import CSV</DownloadButton>}>
          {r.xero.message || 'Import the invoices into Xero with the sales invoice CSV.'}
        </Banner>
      )}
      {r.xero?.connected && r.xero.message && <Banner tone="amber" title="Xero">{r.xero.message}</Banner>}
      {emailFails + xeroFails > 0 && (
        <Banner tone="amber" title={`${plural(emailFails + xeroFails, 'delivery problem')} to fix`}>
          Open each invoice to correct the email address and send it again, or to retry Xero. The batch itself is safe.
        </Banner>
      )}
    </div>
  );
}

function FilesList({ files, can, onChanged }) {
  const [busy, setBusy] = useState(null);
  const claimParts = files.filter((f) => f.kind === 'claim').length;
  const markFile = async (f) => {
    setBusy(f.id);
    try {
      const r = await api.post(`/sc/claims/files/${f.id}/uploaded`);
      toast.success(`${r.data?.file || f.file_name} marked uploaded: ${plural(Number(r.data?.lines_claimed) || 0, 'line')} now Claimed`);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  if (!files.length) return <p className="text-[13px] text-slate-500">No NDIA files in this batch.</p>;
  return (
    <ul className="space-y-2">
      {files.map((f) => (
        <li key={f.id} className="flex flex-wrap items-center gap-3 rounded-xl ring-1 ring-inset ring-slate-200 px-3.5 py-2.5">
          <FileSpreadsheet size={18} className={f.kind === 'correction' ? 'text-amber-600' : 'text-violet-600'} aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-[13px] text-slate-900 break-all">{f.file_name}</p>
            <p className="text-[12px] text-slate-500">
              {f.kind === 'correction' ? 'Correction file' : claimParts > 1 ? `Claim file, part ${f.part}` : 'Claim file'} · {plural(Number(f.row_count) || 0, 'line')} · made {fmtDateTime(f.created_at)}
            </p>
          </div>
          {f.uploaded_at ? <Pill tone="violet" label={`Uploaded ${fmtDateTime(f.uploaded_at)}`} /> : <Pill tone="amber" label="Not uploaded yet" />}
          {can.claim && <DownloadButton url={`/sc/claims/files/${f.id}`} filename={f.file_name} label={`Download ${f.file_name}`} onDone={onChanged}>Download</DownloadButton>}
          {can.claim && !f.uploaded_at && (f.kind === 'correction' || claimParts > 1) && (
            <Button size="sm" variant="secondary" icon={CheckCircle2} loading={busy === f.id} onClick={() => markFile(f)} aria-label={`Mark ${f.file_name} uploaded`}>Mark uploaded</Button>
          )}
          {can.claim && <FileLockNote file={f} />}
        </li>
      ))}
    </ul>
  );
}

// 8.7: import the results file. Any OPEN row blocks the import (409) and nothing changes.
function ResultsImport({ batch, can, onImported }) {
  const [file, setFile] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { setOut(null); setErr(null); setFile(null); }, [batch.id]);
  const go = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true); setErr(null); setOut(null);
    try {
      const r = await api.post(`/sc/claims/batches/${batch.id}/results`, fd);
      setOut(r.data);
      setFile(null); setInputKey((k) => k + 1);
      const c = r.data?.counts || {};
      toast.success(`Results imported: ${c.paid || 0} paid, ${c.capped || 0} capped, ${c.rejected || 0} rejected`);
      onImported();
    } catch (e) {
      const dd = errData(e);
      setErr({ status: e.response?.status, message: errMsg(e), open: dd.open });
    } finally { setBusy(false); }
  };
  if (!can.claim) return null;
  return (
    <Section id="sc-results-import" title="Import the NDIA results" icon={Upload}
      sub="Import the file as you downloaded it, without opening it in Excel. A newer results file replaces the earlier results, and the change is logged.">
      <div className="rounded-xl ring-1 ring-inset ring-slate-200 p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-0 sm:min-w-[240px]"><FileInput label="Results file (CSV)" onFile={setFile} resetKey={inputKey} disabled={busy} /></div>
        <Button icon={Upload} loading={busy} disabled={!file} onClick={go}>Import results</Button>
      </div>
      {err && (err.status === 409 && err.open ? (
        <Banner tone="red" title={`Import blocked: ${plural(Number(err.open) || 0, 'row is', 'rows are')} still OPEN`}>
          Nothing was changed. The NDIA is still processing those requests. Download the results file again later and import the new file.
        </Banner>
      ) : <Banner tone="red" title="The results were not imported">{err.message}</Banner>)}
      {out && <ResultsOutcome out={out} />}
    </Section>
  );
}

function ResultsOutcome({ out }) {
  const c = out.counts || {};
  const unmatched = out.unmatched || [];
  const skipped = out.skipped || [];
  const flags = out.rate_flags || [];
  return (
    <div className="space-y-3" aria-live="polite">
      <FigureStrip items={[
        { label: 'Paid', value: c.paid || 0, plain: true, tone: 'emerald' },
        { label: 'Capped', value: c.capped || 0, plain: true, tone: 'amber' },
        { label: 'Rejected', value: c.rejected || 0, plain: true, tone: 'red' },
        { label: 'Not matched', value: c.unmatched || 0, plain: true, tone: 'amber', sub: c.skipped ? `${c.skipped} skipped` : undefined },
      ]} />
      <TotalsCheck t={out.totals} />
      {flags.length > 0 && (
        <Banner tone="amber" title={`${plural(flags.length, 'line was', 'lines were')} capped: check the rate setup`}>
          <ul className="list-disc pl-4 space-y-0.5">{flags.map((f) => <li key={f.line_id}>{f.message}</li>)}</ul>
        </Banner>
      )}
      {c.rejected > 0 && (
        <Banner tone="red" title={`${plural(c.rejected, 'line was', 'lines were')} rejected`}>
          Re-claim them in a correction file, or write them off, from the Lines tab.
        </Banner>
      )}
      {unmatched.length > 0 && (
        <div>
          <p className="text-[12.5px] font-semibold text-slate-700 mb-1.5">Rows not matched to this batch (no line was changed)</p>
          <div className="overflow-x-auto rounded-xl ring-1 ring-inset ring-slate-200">
            <table className="w-full text-[13px]">
              <thead><tr className="text-left text-[12px] font-semibold text-slate-600 border-b border-slate-100">
                <th scope="col" className="px-3 py-2">Row</th><th scope="col" className="px-3 py-2">Claim reference</th><th scope="col" className="px-3 py-2">NDIS number</th>
                <th scope="col" className="px-3 py-2">Support item</th><th scope="col" className="px-3 py-2">Why</th>
              </tr></thead>
              <tbody>
                {unmatched.map((u, i) => (
                  <tr key={`${u.row}-${i}`} className="border-b border-slate-100 last:border-0 align-top">
                    <td className="px-3 py-2 tabular-nums">{u.row}</td><td className="px-3 py-2">{u.claimReference || '—'}</td>
                    <td className="px-3 py-2 tabular-nums">{u.ndisNumber || '—'}</td><td className="px-3 py-2">{u.supportNumber || '—'}</td>
                    <td className="px-3 py-2 text-amber-800">{u.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {skipped.length > 0 && (
        <p className="text-[12.5px] text-slate-500">Skipped: {skipped.map((s) => `${s.claimReference} (${s.reason})`).join('; ')}</p>
      )}
    </div>
  );
}

function ResultsHistory({ files, can }) {
  return (
    <Section title="Results imported" icon={History} sub="The newest file decides each line’s result.">
      <ul className="space-y-2">
        {[...files].reverse().map((f) => {
          const s = (f.summary && typeof f.summary === 'object' ? f.summary.counts : null) || {};
          return (
            <li key={f.id} className="flex flex-wrap items-center gap-3 rounded-xl ring-1 ring-inset ring-slate-200 px-3.5 py-2.5">
              <FileText size={17} className="text-violet-600" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[13px] text-slate-900 break-all">{f.file_name}{f.original_name ? <span className="font-normal text-slate-500"> · {f.original_name}</span> : null}</p>
                <p className="text-[12px] text-slate-500">Imported {fmtDateTime(f.created_at)} · {plural(Number(f.row_count) || 0, 'row')}</p>
              </div>
              <div className="flex flex-wrap gap-1">
                <Pill tone="emerald" label={`${s.paid || 0} paid`} />
                {s.capped > 0 && <Pill tone="amber" label={`${s.capped} capped`} />}
                {s.rejected > 0 && <Pill tone="red" label={`${s.rejected} rejected`} />}
                {s.unmatched > 0 && <Pill tone="slate" label={`${s.unmatched} not matched`} />}
              </div>
              {can.claim && <DownloadButton url={`/sc/claims/files/${f.id}`} filename={f.file_name} label={`Download ${f.file_name}`}>Download</DownloadButton>}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

// 8.1 step 8 / 8.8: NDIA deposits recorded against the batch they pay.
function Deposits({ batch, deposits, totals, can, onChanged }) {
  const [f, setF] = useState({ date: todayIso(), amount: '', reference: '' });
  const [busy, setBusy] = useState(false);
  const [voiding, setVoiding] = useState(null);
  const diff = round2(totals.ndia_deposit_difference);
  const doVoid = async (reason) => {
    await api.post(`/sc/claims/payments/${voiding.id}/void`, { reason });
    toast.success('Deposit voided. It stays on record.');
    onChanged();
  };
  const save = async (e) => {
    e.preventDefault();
    const amount = round2(f.amount);
    if (!(amount > 0)) { toast.error('Enter the deposit amount.'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/sc/claims/batches/${batch.id}/deposits`, { date: f.date, amount, reference: f.reference || undefined });
      const left = round2(r.data?.difference);
      toast.success(left ? `Deposit recorded. ${money(Math.abs(left))} ${left > 0 ? 'still to arrive' : 'more than the results'}.` : 'Deposit recorded. Deposits now match the results.');
      setF({ date: todayIso(), amount: '', reference: '' });
      onChanged();
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Section id="sc-deposits" title="NDIA deposits" icon={Landmark}
      sub="Match the NDIA payment run to this batch, here or in Xero. The NDIA pays at least once a day, Monday to Friday.">
      <FigureStrip items={[
        { label: 'Paid in the results', value: totals.ndia_paid, tone: 'emerald' },
        { label: 'Deposits recorded', value: totals.ndia_deposits },
        { label: diff < 0 ? 'Deposited above results' : 'Still to arrive', value: Math.abs(diff), tone: 'amber' },
      ]} />
      {deposits.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-inset ring-slate-200">
          {deposits.map((p) => (
            <li key={p.id} className="flex items-center gap-3 px-3.5 py-2 text-[13px]">
              <span className="flex-1 min-w-0 text-slate-700">{fmtDate(p.payment_date)}{p.reference ? <span className="text-slate-500"> · {p.reference}</span> : null}</span>
              <Money value={p.amount} className="font-semibold text-slate-900" />
              {can.recon && (
                <Button size="sm" variant="ghost" icon={Undo2} onClick={() => setVoiding(p)} aria-label={`Void the ${money(p.amount)} deposit of ${fmtDate(p.payment_date)}`}>Void</Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ReasonModal open={!!voiding} onClose={() => setVoiding(null)} danger confirmLabel="Void deposit" title="Void this deposit"
        sub={voiding ? `${money(voiding.amount)} recorded for ${fmtDate(voiding.payment_date)}. It stays on record as voided.` : undefined}
        label="Why is it being voided?" onConfirm={doVoid} />
      {can.recon && (
        <form onSubmit={save} className="rounded-xl bg-slate-50/70 ring-1 ring-inset ring-slate-200/70 p-3.5 grid grid-cols-1 sm:grid-cols-[150px_150px_1fr_auto] gap-3 items-end">
          <Field label="Date received"><Input type="date" value={f.date} max={todayIso()} onChange={(e) => setF((x) => ({ ...x, date: e.target.value }))} /></Field>
          <Field label="Amount"><Input type="number" inputMode="decimal" min="0.01" step="0.01" value={f.amount} onChange={(e) => setF((x) => ({ ...x, amount: e.target.value }))} /></Field>
          <Field label="Reference"><Input value={f.reference} maxLength={200} placeholder="NDIA payment reference" onChange={(e) => setF((x) => ({ ...x, reference: e.target.value }))} /></Field>
          <Button type="submit" icon={Landmark} loading={busy}>Record deposit</Button>
        </form>
      )}
    </Section>
  );
}

function BatchInvoices({ batch, invoices, can, onOpenInvoice }) {
  const cols = [
    { accessorKey: 'invoice_number', header: 'Invoice', cell: ({ row: { original: i } }) => (
      <div>
        <OpenButton onClick={() => onOpenInvoice(i.id)}>{i.invoice_number}</OpenButton>
        {i.reissue_of && <p className="text-[12px] text-slate-500">Reissued invoice</p>}
      </div>
    ) },
    { accessorKey: 'client_name', header: 'Participant', cell: ({ row: { original: i } }) => (
      <div className="space-y-0.5"><p>{i.client_name || '—'}</p><FundingPill type={i.funding_type} /></div>
    ) },
    { accessorKey: 'total', header: 'Total', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} /> },
    { accessorKey: 'balance', header: 'Balance', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} className={Number(getValue()) ? 'font-semibold text-slate-900' : 'text-slate-500'} /> },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <StatusPill map={INVOICE_STATUS} status={getValue()} /> },
    { id: 'delivery', header: 'Delivery', enableSorting: false, cell: ({ row: { original: i } }) => <DeliveryPills inv={i} /> },
  ];
  return (
    <Section title="Invoices" icon={Receipt} sub="One invoice per plan- or self-managed participant. Open one to email it, record a payment or reissue it."
      actions={can.claim && invoices.length > 0 && (
        <DownloadButton url={`/sc/claims/batches/${batch.id}/xero-invoices.csv`} icon={FileSpreadsheet}>Xero import CSV</DownloadButton>
      )}>
      <DataTable columns={cols} data={invoices} dense onRowClick={(i) => onOpenInvoice(i.id)}
        empty={<Empty icon={Receipt} title="No invoices in this batch">Everyone in it is NDIA-managed.</Empty>} />
    </Section>
  );
}

function AuditList({ items }) {
  const [all, setAll] = useState(false);
  if (!items.length) return <Empty icon={History} title="No history yet" />;
  const shown = all ? items : items.slice(0, 30);
  const what = (a) => {
    const d = a.detail && typeof a.detail === 'object' ? a.detail : {};
    const nv = a.new_value && typeof a.new_value === 'object' ? a.new_value : {};
    const ov = a.old_value && typeof a.old_value === 'object' ? a.old_value : {};
    const bits = [d.claim_reference, d.invoice_number, d.results_file || d.file, d.correction_file && `correction file ${d.correction_file}`,
      d.reissued_as && `reissued as ${d.reissued_as}`].filter(Boolean);
    if (d.counts) bits.push(`${d.counts.paid || 0} paid, ${d.counts.capped || 0} capped, ${d.counts.rejected || 0} rejected`);
    if (Array.isArray(d.files) && d.files.length) bits.push(d.files.join(', '));
    if (nv.status && ov.status && nv.status !== ov.status) bits.push(`${words(ov.status)} → ${words(nv.status)}`);
    if (nv.written_off_reason) bits.push(`reason: ${nv.written_off_reason}`);
    return bits.join(' · ');
  };
  return (
    <div>
      <ol className="space-y-3">
        {shown.map((a) => (
          <li key={a.id} className="flex gap-3 text-[13px]">
            <span className="w-1.5 h-1.5 mt-2 rounded-full bg-violet-400 flex-shrink-0" aria-hidden />
            <div className="min-w-0">
              <p className="text-slate-800 break-words"><span className="font-semibold">{words(a.action)}</span>{' '}<span className="text-slate-600">{words(a.entity)}</span>{what(a) ? <span className="text-slate-600"> · {what(a)}</span> : null}</p>
              <p className="text-[12px] text-slate-500">{a.actor_name || 'System'} · {fmtDateTime(a.created_at)}</p>
            </div>
          </li>
        ))}
      </ol>
      {items.length > shown.length && <Button size="sm" variant="ghost" className="mt-3" onClick={() => setAll(true)}>Show all {items.length} events</Button>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Lines: rejected lines are re-claimed or written off (8.7)
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Participants and coordinators for the filter pickers (from the caseload list).
function usePickers() {
  const sc = useLoad('/support-coordination');
  return useMemo(() => {
    const rows = Array.isArray(sc.data) ? sc.data : [];
    const people = new Map(); const coords = new Map();
    for (const r of rows) {
      if (r.client_id && !people.has(r.client_id)) people.set(r.client_id, `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Participant');
      if (r.coordinator_id && !coords.has(r.coordinator_id)) coords.set(r.coordinator_id, `${r.coordinator_first || ''} ${r.coordinator_last || ''}`.trim() || 'Coordinator');
    }
    const opts = (m) => [...m].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    return { participants: opts(people), coordinators: opts(coords) };
  }, [sc.data]);
}
const LINE_CHIPS = [
  { value: 'rejected', label: 'Rejected', alert: true }, { value: 'in_batch', label: 'In batch' }, { value: 'claimed', label: 'Claimed' },
  { value: 'paid', label: 'Paid' }, { value: 'capped', label: 'Capped' }, { value: 'written_off', label: 'Written off' }, { value: 'released', label: 'Claimed again' },
];

function LinesTab({ can, warnDays, batches, signal, preview, pendingAdj, onChanged, onOpenBatch, onOpenInvoice, onResolve, goTab }) {
  const flt = useClaimFilters('l_');
  const { f } = flt;
  const term = useDebounced(f.q.trim(), 300);
  const pick = usePickers();
  // Every filter, the search and the sort run on the server; the list loads 100 at a time.
  const params = useMemo(() => apiParams({ ...f, q: term }), [f, term]);
  const l = usePagedList('/sc/claims/lines', params, { max: 1000 });
  const { reload } = l;
  useEffect(() => { if (signal) reload(); }, [signal, reload]);
  const anyLines = lineViewCount(l.counts, 'all') > 0;
  const filtered = flt.active || !!f.st;
  const changed = useCallback(() => { reload(); onChanged(); }, [reload, onChanged]);
  const batchOptions = batches.filter((b) => b.status !== 'draft').map((b) => ({ value: b.id, label: `${b.batch_code} · ${periodText(b.period_start, b.period_end)}` }));
  const counts = l.counts || {};

  return (
    <div className="space-y-5">
      <Panel title="Claim lines" icon={Files} hue="violet"
        sub="Every line from every batch. Filter, sort and export. Tick rejected lines to re-claim, claim again in the next batch or write off; tick capped lines to write off what was not paid."
        actions={<ExportButtons url="/sc/claims/lines/export" params={params} />}>
        <FilterBar {...flt} searchPlaceholder="Search reference, participant, item or invoice"
          statuses={LINE_CHIPS.map((c) => ({ ...c, count: Number(counts[c.value]) || 0, alert: c.alert && Number(counts[c.value]) > 0 }))}
          participants={pick.participants} coordinators={pick.coordinators} batches={batchOptions} dateLabel="Service dates" />
        <ResultLine list={l} noun="line" totals={l.totals && [`${hrs(l.totals.minutes)}`, `${money(l.totals.amount)} claimed`, `${money(l.totals.paid_amount)} paid`]} />
        <LinesTable lines={l.rows} loading={l.loading} error={l.error} onRetry={reload} can={can} warnDays={warnDays} pendingAdj={pendingAdj}
          onChanged={changed} onOpenBatch={onOpenBatch} onOpenInvoice={onOpenInvoice} showBatch total={l.total} totals={l.totals}
          sorting={sortingOf(f)} onSortingChange={onSortOf(flt.set)}
          empty={<Empty icon={Files} title={anyLines || filtered ? 'No lines match' : 'No claim lines yet'}>
            {filtered ? 'Try other filters, or clear them.' : 'Lines appear here when a batch is generated.'}
          </Empty>} />
        <ShowMore list={l} noun="line" />
      </Panel>
      <AdjustmentsPanel preview={preview} can={can} warnDays={warnDays} onResolve={onResolve} goTab={goTab} />
    </div>
  );
}

// "152 lines · 151.5 h · $15,170 claimed": the whole filtered set (every page), above the table.
function ResultLine({ list, noun, totals }) {
  if (list.error || (list.loading && !list.rows.length)) return null;
  const n = Number(list.total) || 0;
  return (
    <p className="text-[12.5px] text-slate-600 mb-2 tabular-nums">
      <span className="font-semibold text-slate-800">{plural(n, noun)}</span>
      {n > 0 && totals && totals.filter(Boolean).map((t) => <span key={t}> · {t}</span>)}
    </p>
  );
}

// total/totals: the whole filtered set when the list is server-paged (the footer counts every line,
// not only those shown); without them the footer adds up the lines given.
function LinesTable({ lines, loading, error, onRetry, can, warnDays, pendingAdj = {}, onChanged, onOpenBatch, onOpenInvoice, showBatch, empty, total, totals, sorting, onSortingChange }) {
  const [sel, setSel] = useState([]);
  const [resetKey, setResetKey] = useState(0);
  const [reclaim, setReclaim] = useState(null);
  const [writeOff, setWriteOff] = useState(null);
  const [release, setRelease] = useState(null);
  const [shortfall, setShortfall] = useState(null);
  // Rejected lines can be re-claimed, claimed again later or written off; a capped line's unpaid
  // part can be written off once.
  const canPick = (x) => x.status === 'rejected' || (x.status === 'capped' && !(Number(x.written_off_amount) > 0) && Number(x.amount) > Number(x.paid_amount || 0));
  const selectable = (can.claim || can.writeOff) && lines.some(canPick);
  const clear = () => { setResetKey((k) => k + 1); setSel([]); };
  const doWriteOff = async (reason) => {
    const r = await api.post('/sc/claims/lines/write-off', { line_ids: writeOff.map((x) => x.id), reason });
    toast.success(`${plural(Number(r.data?.written_off) || writeOff.length, 'line')} written off`);
    clear(); onChanged();
  };
  const doRelease = async (reason) => {
    const r = await api.post('/sc/claims/lines/release', { line_ids: release.map((x) => x.id), reason });
    toast.success(`${plural(Number(r.data?.released) || release.length, 'line')} back in Ready to claim, for the next batch`);
    clear(); onChanged();
  };
  const doShortfall = async (reason) => {
    const r = await api.post('/sc/claims/lines/write-off-shortfall', { line_ids: shortfall.map((x) => x.id), reason });
    toast.success(`${money(r.data?.written_off)} shortfall written off`);
    clear(); onChanged();
  };
  const rej = sel.filter((x) => x.status === 'rejected');
  const cap = sel.filter((x) => x.status === 'capped');
  const final = (s) => ['paid', 'capped', 'written_off'].includes(s);
  const cols = [
    { id: 'reference', accessorKey: 'claim_reference', header: 'Reference', cell: ({ row: { original: x } }) => (
      <div>
        <p className="font-semibold text-slate-900 tabular-nums whitespace-nowrap">{x.claim_reference}</p>
        {showBatch && x.batch_code && <OpenButton className="text-[12px] font-medium text-indigo-600 hover:text-indigo-800" onClick={() => onOpenBatch(x.batch_id)}>Batch {x.batch_code}</OpenButton>}
      </div>
    ) },
    { id: 'participant', accessorKey: 'client_name', header: 'Participant', cell: ({ row: { original: x } }) => (
      <div className="min-w-[130px] space-y-0.5"><p className="text-slate-800">{x.client_name || '—'}</p><FundingPill type={x.funding_type} />
        {x.coordinator_name && <p className="text-[11.5px] text-slate-500 truncate">{x.coordinator_name}</p>}</div>
    ) },
    { id: 'date', accessorKey: 'service_date', header: 'Service date', cell: ({ row: { original: x } }) => (
      <div className="space-y-0.5">
        <p className="whitespace-nowrap inline-flex items-center gap-1">
          {fmtDate(x.service_date)}
          {(x.time_locked || x.locked) && <Tip content={x.lock_message || 'The time on this line is locked. Use Add adjustment on the time entry to correct it.'}><span className="inline-flex"><Lock size={12} className="text-violet-600" aria-hidden /><span className="sr-only">Time locked</span></span></Tip>}
        </p>
        <DaysBadge days={x.days_since_service} warnDays={warnDays} settled={final(x.status)} /></div>
    ) },
    { accessorKey: 'support_item', header: 'Service', enableSorting: !onSortingChange, cell: ({ row: { original: x } }) => (
      <div><p className="whitespace-nowrap">{claimTypeText(x.claim_type, x.cancellation_reason)}</p><p className="text-[12px] text-slate-500 tabular-nums whitespace-nowrap">{x.support_item}</p></div>
    ) },
    { id: 'hours', accessorKey: 'minutes', header: 'Hours', meta: { align: 'right' }, cell: ({ row: { original: x } }) => <span className="whitespace-nowrap">{qtyOf(x)}</span> },
    { id: 'amount', accessorKey: 'amount', header: 'Amount', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} /> },
    { id: 'paid', accessorKey: 'paid_amount', header: 'Paid', meta: { align: 'right' }, cell: ({ row: { original: x } }) => (
      <div>
        <Money value={x.paid_amount} className={Number(x.paid_amount) ? 'text-emerald-700 font-semibold' : 'text-slate-500'} />
        {Number(x.capped_amount) > 0 && <p className="text-[12px] text-amber-700 tabular-nums whitespace-nowrap">−{money(x.capped_amount)} capped</p>}
        {Number(x.written_off_amount) > 0 && <p className="text-[12px] text-slate-500 tabular-nums whitespace-nowrap">{money(x.written_off_amount)} written off</p>}
      </div>
    ) },
    { id: 'status', accessorKey: 'status', header: 'Status', meta: { className: 'min-w-[200px]' }, cell: ({ row: { original: x } }) => {
      const adj = pendingAdj[x.id] || [];
      return (
        <div className="space-y-1">
          <div className="flex flex-wrap gap-1">
            <StatusPill map={LINE_STATUS} status={x.status} />
            {Number(x.reclaim_count) > 0 && <Pill tone="blue" label={`Re-claimed ${x.reclaim_count}×`} />}
          </div>
          {x.status === 'rejected' && x.reject_reason && <p className="text-[12px] leading-snug text-rose-700">{x.reject_reason}</p>}
          {x.status === 'written_off' && x.written_off_reason && <p className="text-[12px] leading-snug text-slate-500">{x.written_off_reason}</p>}
          {x.status === 'released' && <p className="text-[12px] leading-snug text-slate-500">Back in Ready to claim{x.release_reason ? `: ${x.release_reason}` : ''}</p>}
          {x.extra_id && <Pill tone="indigo" label="Manual line" size="xs" />}
          {adj.length > 0 && <p className="text-[12px] leading-snug text-amber-800">{plural(adj.length, 'time adjustment')} ({adj.map((a) => `${a.minutes > 0 ? '+' : ''}${a.minutes} min`).join(', ')}) go into the re-claim.</p>}
        </div>
      );
    } },
    { id: 'where', header: 'File or invoice', enableSorting: false, cell: ({ row: { original: x } }) => (x.invoice_id ? (
      <OpenButton className="text-[12.5px] text-indigo-600 hover:text-indigo-800" onClick={() => onOpenInvoice(x.invoice_id)}>{x.invoice_number || 'Invoice'}</OpenButton>
    ) : <span className="text-[12px] text-slate-500 break-all">{x.correction_file || x.claim_file || '—'}</span>) },
  ];
  const sums = totals ? { n: Number(total) || 0, minutes: Number(totals.minutes) || 0, amount: totals.amount, paid: totals.paid_amount }
    : { n: lines.length, minutes: lines.reduce((t, x) => t + (Number(x.minutes) || 0), 0), amount: sumOf(lines, (x) => x.amount), paid: sumOf(lines, (x) => x.paid_amount) };
  const footer = lines.length ? (
    <>
      <td className="px-3 first:pl-4 py-2.5 border-t border-slate-200" colSpan={(selectable ? 1 : 0) + 4}>
        Total ({plural(sums.n, 'line')}{sums.n > lines.length ? ', all pages' : ''})
      </td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums whitespace-nowrap">{hrs(sums.minutes)}</td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={sums.amount} /></td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={sums.paid} /></td>
      <td className="px-3 last:pr-4 py-2.5 border-t border-slate-200" colSpan={2} />
    </>
  ) : null;
  const selAmount = sumOf(sel, (x) => x.amount);

  return (
    <div>
      <DataTable key={resetKey} columns={cols} data={lines} loading={loading} error={error} onRetry={onRetry} dense
        selectable={selectable} isRowSelectable={(row) => canPick(row.original)} onSelectionChange={setSel}
        rowClassName={(x) => (x.status === 'rejected' ? 'bg-rose-50/30' : undefined)} footer={footer}
        initialSort={[{ id: 'date', desc: false }]} empty={empty} sorting={sorting} onSortingChange={onSortingChange}
        mobileCard={(x) => (
          <div className="space-y-1">
            <div className="flex items-start justify-between gap-3">
              <p className="font-semibold text-slate-900 truncate">{x.client_name || '—'}</p>
              <Money value={x.amount} className="font-semibold text-slate-900" />
            </div>
            <p className="text-[12.5px] text-slate-600">{fmtDate(x.service_date)} · {hrs(x.minutes)} · {claimTypeText(x.claim_type, x.cancellation_reason)}</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <StatusPill map={LINE_STATUS} status={x.status} /><FundingPill type={x.funding_type} />
              <span className="text-[12px] text-slate-500 tabular-nums">{x.claim_reference}</span>
            </div>
            {x.status === 'rejected' && x.reject_reason && <p className="text-[12px] text-rose-700">{x.reject_reason}</p>}
          </div>
        )} />
      <SelectionBar count={sel.length} onClear={clear}>
        <span className="text-[12px] text-white/80 tabular-nums mr-1">{money(selAmount)}</span>
        {can.claim && rej.length > 0 && <Button size="sm" variant="secondary" icon={FilePlus2} onClick={() => setReclaim(rej)}>Correction file{cap.length ? ` (${rej.length})` : ''}</Button>}
        {can.claim && rej.length > 0 && <Button size="sm" variant="secondary" icon={Undo2} onClick={() => setRelease(rej)}>Claim in next batch{cap.length ? ` (${rej.length})` : ''}</Button>}
        {can.writeOff && rej.length > 0 && <Button size="sm" variant="danger" icon={Ban} onClick={() => setWriteOff(rej)}>Write off{cap.length ? ` (${rej.length})` : ''}</Button>}
        {can.writeOff && cap.length > 0 && <Button size="sm" variant="secondary" icon={Ban} onClick={() => setShortfall(cap)}>Write off shortfall{rej.length ? ` (${cap.length})` : ''}</Button>}
      </SelectionBar>
      <ReasonModal open={!!release} onClose={() => setRelease(null)} confirmLabel="Claim in next batch"
        title={release ? `Claim ${plural(release.length, 'rejected line')} again in a later batch` : 'Claim again'}
        sub={release ? `The time (or manual line) goes back to Ready to claim and is claimed with a new reference in the next batch, through every check again. The rejected ${release.length === 1 ? 'line closes' : 'lines close'} as Claimed again.` : undefined}
        label="What was fixed?" onConfirm={doRelease} />
      <ReasonModal open={!!shortfall} onClose={() => setShortfall(null)} danger confirmLabel="Write off shortfall"
        title={shortfall ? `Write off the unpaid part of ${plural(shortfall.length, 'capped line')}` : 'Write off shortfall'}
        sub={shortfall ? `${money(sumOf(shortfall, (x) => Number(x.amount) - Number(x.paid_amount || 0)))} the NDIA did not pay will not be chased. The reason is kept on record.` : undefined}
        label="Reason" onConfirm={doShortfall} />
      <ReclaimModal lines={reclaim} onClose={() => setReclaim(null)} pendingAdj={pendingAdj} can={can} onDone={() => { clear(); onChanged(); }} />
      <ReasonModal open={!!writeOff} onClose={() => setWriteOff(null)} danger confirmLabel="Write off"
        title={writeOff ? `Write off ${plural(writeOff.length, 'rejected line')}` : 'Write off'}
        sub={writeOff ? `${money(sumOf(writeOff, (x) => x.amount))} will not be claimed. The lines close as Written off and the reason is kept on record.` : undefined}
        label="Reason" onConfirm={doWriteOff} />
    </div>
  );
}

// 8.7 re-claim: a correction file holds only the corrected lines, with their original ClaimReference.
function ReclaimModal({ lines, onClose, pendingAdj, can, onDone }) {
  const open = !!lines;
  const [items, setItems] = useState({});
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState(null);
  const [done, setDone] = useState(null);
  useEffect(() => { if (open) { setItems({}); setFail(null); setDone(null); } }, [open]);
  const failures = useMemo(() => Object.fromEntries(((fail && fail.failures) || []).map((f) => [f.line_id, f.reasons || []])), [fail]);
  const go = async (e) => {
    if (e) e.preventDefault();
    setBusy(true); setFail(null);
    const updates = {};
    Object.entries(items).forEach(([id, v]) => { if (String(v || '').trim()) updates[id] = { support_item: String(v).trim() }; });
    try {
      const r = await api.post('/sc/claims/lines/reclaim', { line_ids: lines.map((x) => x.id), updates });
      setDone(r.data);
      toast.success(`Correction file made for ${plural(Number(r.data?.lines) || lines.length, 'line')}`);
      onDone();
    } catch (ex) {
      const dd = errData(ex);
      setFail({ message: errMsg(ex), failures: dd.failures || [], errors: dd.errors || [] });
    } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} width="lg"
      title={done ? 'Correction file ready' : `Re-claim ${plural(lines ? lines.length : 0, 'rejected line')}`}
      sub={done ? 'Upload it in the myplace provider portal, then mark it uploaded in the batch.' : 'The lines are checked again with today’s details, then claimed again in a correction file under their original reference.'}
      footer={done ? <Button onClick={onClose}>Done</Button> : (
        <><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button type="submit" form="sc-reclaim-form" icon={FilePlus2} loading={busy}>Create correction file</Button></>
      )}>
      {done ? (
        <ul className="space-y-2">
          {(done.files || []).map((f) => (
            <li key={f.id} className="flex flex-wrap items-center gap-3 rounded-xl ring-1 ring-inset ring-slate-200 px-3.5 py-2.5">
              <FileSpreadsheet size={17} className="text-amber-600" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[13px] text-slate-900 break-all">{f.file_name}</p>
                <p className="text-[12px] text-slate-500">Batch {f.batch_code} · {plural(Number(f.row_count) || 0, 'line')}</p>
              </div>
              {can.claim && <DownloadButton url={`/sc/claims/files/${f.id}`} filename={f.file_name} label={`Download ${f.file_name}`}>Download</DownloadButton>}
              {can.claim && <FileLockNote file={f} />}
            </li>
          ))}
        </ul>
      ) : (
        <form id="sc-reclaim-form" onSubmit={go} className="space-y-3">
          {fail && (
            <Banner tone="red" title="Nothing was re-claimed">
              <p>{fail.message}</p>
              {fail.errors.length > 0 && <ul className="mt-1 list-disc pl-4">{fail.errors.map((x, i) => <li key={i}>{x.participant || 'Line'}: {(x.problems || []).join(' ')}</li>)}</ul>}
            </Banner>
          )}
          <ul className="space-y-2.5">
            {(lines || []).map((x) => {
              const adj = pendingAdj[x.id] || [];
              const why = failures[x.id];
              return (
                <li key={x.id} className={clsx('rounded-xl ring-1 ring-inset px-3.5 py-3', why ? 'ring-rose-200 bg-rose-50/40' : 'ring-slate-200')}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-[13px] font-semibold text-slate-900">{x.claim_reference} · {x.client_name}</p>
                    <p className="text-[12.5px] text-slate-600 tabular-nums">{fmtDate(x.service_date)} · {hrs(x.minutes)} · {money(x.amount)}</p>
                  </div>
                  {x.reject_reason && <p className="mt-1 text-[12.5px] text-rose-700">NDIA: {x.reject_reason}</p>}
                  {adj.length > 0 && <p className="mt-1 text-[12.5px] text-amber-800">Includes {plural(adj.length, 'time adjustment')} ({adj.map((a) => `${a.minutes > 0 ? '+' : ''}${a.minutes} min`).join(', ')}).</p>}
                  <div className="mt-2 max-w-xs">
                    <Field label="Correct the support item" hint={`Leave blank to keep ${x.support_item}.`}>
                      <Input value={items[x.id] || ''} placeholder={x.support_item} maxLength={40}
                        onChange={(ev) => { const v = ev.target.value; setItems((m) => ({ ...m, [x.id]: v })); }} />
                    </Field>
                  </div>
                  {why && <ul className="mt-2 space-y-0.5 text-[12.5px] text-rose-700">{why.map((m, i) => <li key={i} className="flex gap-1.5"><XCircle size={13} className="mt-0.5 flex-shrink-0" aria-hidden />{m}</li>)}</ul>}
                </li>
              );
            })}
          </ul>
        </form>
      )}
    </Modal>
  );
}

// 5.4 adjustments to claimed time: waiting for a re-claim, or held until resolved by hand.
function AdjustmentsPanel({ preview, can, warnDays, onResolve, goTab }) {
  const pv = preview.data;
  const toResolve = useMemo(() => groupFixList((pv?.fixList || []).filter((f) => f.adjustment_entry_id)), [pv]);
  const waiting = pv?.correctionPending || [];
  if (!toResolve.length && !waiting.length) return null;
  return (
    <Panel title="Time adjustments on claimed lines" icon={History} hue="amber"
      sub="A change to time that was already claimed is never claimed in a batch. It goes into a re-claim, or the original claim is corrected and the change marked resolved.">
      <div className="space-y-5">
        {waiting.length > 0 && (
          <Section title="Waiting for a re-claim" sub="These go into the correction file when the rejected line is re-claimed.">
            <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-inset ring-slate-200">
              {waiting.map((a) => (
                <li key={a.entry_id} className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5 text-[13px]">
                  <span className="text-slate-800">{a.client_name || 'Participant'} · {fmtDate(a.service_date)} · line {a.claim_reference}</span>
                  <span className={clsx('tabular-nums font-semibold', a.minutes < 0 ? 'text-rose-600' : 'text-emerald-700')}>{a.minutes > 0 ? '+' : ''}{a.minutes} min</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
        {toResolve.length > 0 && (
          <Section title="To correct and resolve" sub="These remove time that was already claimed. Correct the original claim where it was made, then mark the change resolved.">
            <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-inset ring-slate-200">
              {toResolve.map((g) => (
                <li key={g.key} className="px-3.5 py-3 flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[13px] font-semibold text-slate-900">{g.name} · {fmtDate(g.first)}</p>
                      <DaysBadge days={g.oldestDays} warnDays={warnDays} />
                    </div>
                    {g.cause.how && <p className="text-[13px] leading-snug text-slate-700">{g.cause.how}</p>}
                  </div>
                  <div className="sm:flex-shrink-0"><FixAction group={g} can={can} onResolve={onResolve} goTab={goTab} /></div>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </Panel>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Invoices: plan-managed and self-managed (8.2, 8.6, 8.8)
// ═══════════════════════════════════════════════════════════════════════════════════════════════
function InvoicesTab({ signal, onOpen, can, onChanged }) {
  const [sel, setSel] = useState([]);
  const [selKey, setSelKey] = useState(0);
  const [bulk, setBulk] = useState(null);      // 'pay' | 'write_off' (need input) — email/reminder go straight away
  const [bulkBusy, setBulkBusy] = useState(false);
  const clearSel = () => { setSelKey((k) => k + 1); setSel([]); };
  const flt = useClaimFilters('i_', { view: 'open' });
  const { f } = flt;
  const term = useDebounced(f.q.trim(), 300);
  const pick = usePickers();
  const view = INVOICE_VIEWS.find((v) => v.value === f.view) || INVOICE_VIEWS[0];
  // The view (Overdue = open and past the due date), filters, search and sort run on the server.
  const params = useMemo(() => apiParams({ ...f, q: term, st: '' }, { view: view.value }), [f, term, view.value]);
  const l = usePagedList('/sc/claims/invoices', params, { max: 500 });
  const { reload } = l;
  useEffect(() => { if (signal) reload(); }, [signal, reload]);
  const rows = l.rows;
  const counts = l.counts || {};
  const anyInvoices = Number(counts.all) > 0;
  // Many at once: each invoice is done on its own and the result says which ones failed and why.
  const runBulk = async (action, extra = {}) => {
    setBulkBusy(true);
    try {
      const r = await api.post('/sc/claims/invoices/bulk', { action, invoice_ids: sel.map((i) => i.id), ...extra });
      const d = r.data || {};
      const verb = { email: 'emailed', reminder: 'sent a reminder', pay: 'marked paid', write_off: 'written off' }[action];
      if (d.done) toast.success(`${plural(d.done, 'invoice')} ${verb}`);
      if (d.failed) toast.error(`${plural(d.failed, 'invoice')} not done: ${(d.results || []).filter((x) => !x.ok).map((x) => `${x.invoice_number || ''} ${x.error}`).slice(0, 3).join('; ')}`, { duration: 8000 });
      clearSel(); setBulk(null); reload(); onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBulkBusy(false); }
  };
  const openSel = sel.filter((i) => OPEN_INVOICE.includes(i.status));
  const anyOpen = rows.some((i) => OPEN_INVOICE.includes(i.status));
  const canBulk = !!(can && (can.claim || can.recon || can.writeOff)) && anyOpen;
  const cols = [
    { id: 'invoice', accessorKey: 'invoice_number', header: 'Invoice', cell: ({ row: { original: i } }) => (
      <div>
        <OpenButton onClick={() => onOpen(i.id)}>{i.invoice_number}</OpenButton>
        <p className="text-[12px] text-slate-500 whitespace-nowrap">{i.batch_code ? `Batch ${i.batch_code}` : ''}{i.reissue_of ? `${i.batch_code ? ' · ' : ''}reissue` : ''}</p>
      </div>
    ) },
    { id: 'participant', accessorKey: 'client_name', header: 'Participant', cell: ({ row: { original: i } }) => (
      <div className="min-w-[130px] space-y-0.5"><p className="text-slate-800">{i.client_name || '—'}</p><FundingPill type={i.funding_type} /></div>
    ) },
    { accessorKey: 'recipient_name', header: 'Bill to', enableSorting: false, cell: ({ row: { original: i } }) => (
      <div className="min-w-0 max-w-[220px]"><p className="truncate">{i.recipient_name || '—'}</p><p className="text-[12px] text-slate-500 truncate">{i.recipient_email || 'No email'}</p></div>
    ) },
    { id: 'issued', accessorKey: 'issue_date', header: 'Issued', cell: ({ getValue }) => <span className="whitespace-nowrap">{fmtDate(getValue())}</span> },
    { id: 'due', accessorKey: 'due_date', header: 'Due', cell: ({ row: { original: i } }) => (
      <div><p className="whitespace-nowrap">{fmtDate(i.due_date)}</p>{Number(i.days_overdue) > 0 && <p className="text-[12px] font-semibold text-rose-700 whitespace-nowrap">{plural(Number(i.days_overdue), 'day')} overdue</p>}</div>
    ) },
    { id: 'total', accessorKey: 'total', header: 'Total', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} /> },
    { id: 'paid', accessorKey: 'paid_amount', header: 'Paid', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} className={Number(getValue()) ? 'text-emerald-700' : 'text-slate-500'} /> },
    { id: 'balance', accessorKey: 'balance', header: 'Balance', meta: { align: 'right' }, cell: ({ row: { original: i } }) => (
      <Money value={i.balance} className={!Number(i.balance) ? 'text-slate-500' : Number(i.days_overdue) > 0 ? 'font-semibold text-rose-700' : 'font-semibold text-slate-900'} />
    ) },
    { id: 'status', accessorKey: 'status', header: 'Status', cell: ({ row: { original: i } }) => (
      <div className="flex flex-wrap gap-1">
        <StatusPill map={INVOICE_STATUS} status={i.status} />
        {i.query_note && OPEN_INVOICE.includes(i.status) && <TipText content={i.query_note}><Pill tone="amber" label="Queried" /></TipText>}
      </div>
    ) },
    { id: 'delivery', header: 'Delivery', enableSorting: false, cell: ({ row: { original: i } }) => <DeliveryPills inv={i} /> },
  ];
  // The server's totals cover every invoice in the view (all pages), reissued ones left out.
  const t = l.totals || {};
  const counted = Number(t.invoices) || 0;
  const footer = rows.length ? (
    <>
      <td className="px-3 first:pl-4 py-2.5 border-t border-slate-200" colSpan={5}>
        Total ({plural(counted, 'invoice')}{counted !== l.total ? ', reissued ones left out' : ''}{l.total > rows.length ? ', all pages' : ''})
      </td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={t.total} /></td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={t.paid_amount} /></td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={t.balance} /></td>
      <td className="px-3 last:pr-4 py-2.5 border-t border-slate-200" colSpan={2} />
    </>
  ) : null;
  return (
    <Panel title="Invoices" icon={Receipt} hue="violet"
      sub="Plan-managed invoices go to the plan manager; self-managed ones go to the participant or nominee. Open one to email it, record a payment, follow it up or reissue it."
      actions={<ExportButtons url="/sc/claims/invoices/export" params={params} />}>
      <div className="mb-3">
        <ViewFilter label="Show invoices" value={view.value} onChange={(v) => flt.set({ view: v })} options={INVOICE_VIEWS.map((v) => {
          const count = Number(counts[v.value]) || 0;
          return { value: v.value, label: v.label, count, alert: !!v.alert && count > 0 };
        })} />
      </div>
      <FilterBar {...flt} searchPlaceholder="Search number, participant or recipient" funding
        participants={pick.participants} coordinators={pick.coordinators} dateLabel="Issued" />
      <ResultLine list={l} noun="invoice" totals={[`${money(t.total)} invoiced`, `${money(t.paid_amount)} paid`, `${money(t.balance)} owing`]} />
      <DataTable key={selKey} columns={cols} data={rows} loading={l.loading} error={l.error} onRetry={reload} onRowClick={(i) => onOpen(i.id)}
        rowClassName={(i) => (Number(i.days_overdue) > 0 ? 'bg-rose-50/30' : undefined)} footer={canBulk ? null : footer}
        selectable={canBulk} isRowSelectable={(row) => OPEN_INVOICE.includes(row.original.status)} onSelectionChange={setSel}
        sorting={sortingOf(f)} onSortingChange={onSortOf(flt.set)}
        mobileCard={(i) => (
          <div className="space-y-1">
            <div className="flex items-start justify-between gap-3">
              <p className="font-semibold text-slate-900 truncate">{i.client_name || '—'}</p>
              <Money value={i.balance} className={Number(i.days_overdue) > 0 ? 'font-semibold text-rose-700' : 'font-semibold text-slate-900'} />
            </div>
            <p className="text-[12.5px] text-slate-600">{i.invoice_number} · due {fmtDate(i.due_date)}{Number(i.days_overdue) > 0 ? ` · ${plural(Number(i.days_overdue), 'day')} overdue` : ''}</p>
            <div className="flex flex-wrap gap-1.5"><StatusPill map={INVOICE_STATUS} status={i.status} /><FundingPill type={i.funding_type} /></div>
          </div>
        )}
        empty={<Empty icon={Receipt} title={anyInvoices || term || flt.active ? 'No invoices match' : 'No invoices yet'}>
          {anyInvoices || flt.active ? 'Try another view or filter.' : term ? 'Clear the search to see every invoice.' : 'Invoices are made when a batch with plan- or self-managed participants is generated.'}
        </Empty>} />
      <ShowMore list={l} noun="invoice" />
      <SelectionBar count={sel.length} onClear={clearSel}>
        <span className="text-[12px] text-white/80 tabular-nums mr-1">{money(sumOf(openSel, (i) => i.balance))} owing</span>
        {can && can.claim && <Button size="sm" variant="secondary" icon={Mail} loading={bulkBusy} onClick={() => runBulk('email')}>Email again</Button>}
        {can && can.claim && <Button size="sm" variant="secondary" icon={MailWarning} loading={bulkBusy} onClick={() => runBulk('reminder')}>Send reminder</Button>}
        {can && can.recon && <Button size="sm" variant="secondary" icon={Banknote} onClick={() => setBulk('pay')}>Mark paid in full</Button>}
        {can && can.writeOff && <Button size="sm" variant="danger" icon={Ban} onClick={() => setBulk('write_off')}>Write off</Button>}
      </SelectionBar>
      <BulkPayModal open={bulk === 'pay'} invoices={openSel} busy={bulkBusy} onClose={() => setBulk(null)} onConfirm={(x) => runBulk('pay', x)} />
      <ReasonModal open={bulk === 'write_off'} onClose={() => setBulk(null)} danger confirmLabel="Write off"
        title={`Write off ${plural(openSel.length, 'invoice')}`}
        sub={`${money(sumOf(openSel, (i) => i.balance))} still owing will not be chased. Each invoice keeps the reason on record.`}
        label="Reason" onConfirm={(reason) => runBulk('write_off', { reason })} />
    </Panel>
  );
}

// Record many invoices as paid in full: each gets a payment of its own balance on one date.
function BulkPayModal({ open, invoices, busy, onClose, onConfirm }) {
  const [date, setDate] = useState(todayIso());
  const [reference, setReference] = useState('');
  useEffect(() => { if (open) { setDate(todayIso()); setReference(''); } }, [open]);
  return (
    <Modal open={open} onClose={onClose} title={`Mark ${plural(invoices.length, 'invoice')} paid in full`}
      sub={`Each invoice gets a payment of its own balance: ${money(sumOf(invoices, (i) => i.balance))} in all.`}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} icon={Banknote} onClick={() => onConfirm({ date, reference })}>Record payments</Button></>}>
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Paid on"><Input type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Reference" hint="For example the remittance or EFT number."><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Paid in full" /></Field>
        </div>
        <ul className="max-h-48 overflow-y-auto divide-y divide-slate-100 rounded-xl ring-1 ring-inset ring-slate-200 text-[13px]">
          {invoices.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="truncate"><span className="font-semibold text-slate-800">{i.invoice_number}</span> · {i.client_name}</span>
              <Money value={i.balance} className="font-semibold" />
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

// The payments ledger: every invoice payment and NDIA deposit, filtered, totalled and exportable.
const PAY_KINDS = [{ value: 'invoice', label: 'Invoice payments' }, { value: 'ndia_deposit', label: 'NDIA deposits' }];
const PAY_SOURCE = { manual: 'By hand', xero: 'Xero', bank_import: 'Bank statement' };
function PaymentsLedger({ signal, onOpenInvoice, onOpenBatch }) {
  const flt = useClaimFilters('p_');
  const { f } = flt;
  const term = useDebounced(f.q.trim(), 300);
  const params = useMemo(() => apiParams({ ...f, q: term }), [f, term]);
  const l = usePagedList('/sc/claims/payments', params, { max: 500 });
  const { reload } = l;
  useEffect(() => { if (signal) reload(); }, [signal, reload]);
  const t = l.totals || {};
  const cols = [
    { id: 'date', accessorKey: 'payment_date', header: 'Date', cell: ({ getValue }) => <span className="whitespace-nowrap">{fmtDate(getValue())}</span> },
    { id: 'kind', accessorKey: 'kind', header: 'Type', cell: ({ row: { original: p } }) => (
      <div className="space-y-0.5">
        <Pill tone={p.kind === 'ndia_deposit' ? 'violet' : 'indigo'} label={p.kind === 'ndia_deposit' ? 'NDIA deposit' : 'Invoice payment'} />
        {p.voided_at && <p className="text-[12px] text-rose-700">Voided {fmtDate(p.voided_at)}</p>}
      </div>
    ) },
    { id: 'for', header: 'For', enableSorting: false, cell: ({ row: { original: p } }) => (
      <div className="min-w-[150px]">
        {p.invoice_id ? <OpenButton onClick={() => onOpenInvoice(p.invoice_id)}>{p.invoice_number}</OpenButton>
          : p.batch_id ? <OpenButton onClick={() => onOpenBatch(p.batch_id)}>Batch {p.batch_code}</OpenButton> : <span className="text-slate-500">—</span>}
        {p.client_name && <p className="text-[12px] text-slate-500 truncate">{p.client_name}</p>}
      </div>
    ) },
    { accessorKey: 'reference', header: 'Reference', enableSorting: false, cell: ({ getValue }) => <span className="block max-w-[240px] truncate text-slate-600">{getValue() || '—'}</span> },
    { id: 'source', accessorKey: 'source', header: 'Recorded from', cell: ({ getValue }) => <span className="text-slate-600 whitespace-nowrap">{PAY_SOURCE[getValue()] || getValue()}</span> },
    { id: 'amount', accessorKey: 'amount', header: 'Amount', meta: { align: 'right' }, cell: ({ row: { original: p } }) => (
      <Money value={p.amount} className={p.voided_at ? 'line-through text-slate-400' : 'font-semibold text-emerald-700'} />
    ) },
  ];
  return (
    <Panel title="Payments received" icon={Banknote} hue="violet"
      sub="Every invoice payment and NDIA deposit in one list. Open a row's invoice or batch to see more, or to void a payment."
      actions={<ExportButtons url="/sc/claims/payments/export" params={params} />}>
      <FilterBar {...flt} searchPlaceholder="Search reference, invoice, batch or participant" kinds={PAY_KINDS} dateLabel="Paid" />
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <ResultLine list={l} noun="payment" totals={[`${money(t.amount)} received`, `NDIA ${money(t.ndia_amount)}`, `invoices ${money(t.invoice_amount)}`]} />
        <Checkbox checked={f.voided === '1'} onChange={(v) => flt.set({ voided: v ? '1' : '' })} label="Show voided payments" />
      </div>
      <DataTable columns={cols} data={l.rows} loading={l.loading} error={l.error} onRetry={reload} dense
        sorting={sortingOf(f)} onSortingChange={onSortOf(flt.set)}
        mobileCard={(p) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 truncate">{p.kind === 'ndia_deposit' ? `NDIA deposit · ${p.batch_code || ''}` : `${p.invoice_number} · ${p.client_name || ''}`}</p>
              <p className="text-[12.5px] text-slate-600">{fmtDate(p.payment_date)} · {PAY_SOURCE[p.source] || p.source}{p.reference ? ` · ${p.reference}` : ''}</p>
            </div>
            <Money value={p.amount} className={p.voided_at ? 'line-through text-slate-400' : 'font-semibold text-emerald-700'} />
          </div>
        )}
        empty={<Empty icon={Banknote} title={flt.active || f.kind ? 'No payments match' : 'No payments yet'}>
          {flt.active || f.kind ? 'Try other filters, or clear them.' : 'Payments appear here when they are recorded on an invoice, come in from Xero or a bank statement, or an NDIA deposit is recorded on a batch.'}
        </Empty>} />
      <ShowMore list={l} noun="payment" />
    </Panel>
  );
}

function InvoiceSheet({ id, open, onClose, can, onChanged, onOpenInvoice, onOpenBatch }) {
  const d = useLoad(id ? `/sc/claims/invoices/${id}` : null);
  const { reload } = d;
  const [modal, setModal] = useState(null);       // 'email' | 'reminder' | 'payment' | 'reissue' | 'query' | 'writeoff'
  const [voiding, setVoiding] = useState(null);
  const [xBusy, setXBusy] = useState(false);
  useEffect(() => { setModal(null); setVoiding(null); }, [id]);
  const changed = useCallback(() => { reload(); onChanged(); }, [reload, onChanged]);
  const data = d.data;
  const inv = data?.invoice;
  const isOpen = !!inv && OPEN_INVOICE.includes(inv.status);
  const overdue = !!inv && (inv.status === 'overdue' || Number(inv.days_overdue) > 0);
  const paid = Number(inv?.paid_amount) || 0;

  const pushXero = async () => {
    setXBusy(true);
    try {
      const r = await api.post(`/sc/claims/invoices/${inv.id}/xero`);
      const x = r.data || {};
      const res = (x.results || [])[0];
      if (x.connected === false) toast(x.message || 'Xero is not connected. Use the Xero import CSV on the batch.', { duration: 7000 });
      else if (res && res.ok) toast.success(res.already_sent ? `${inv.invoice_number} is already in Xero` : `${inv.invoice_number} sent to Xero as a draft`);
      else toast.error((res && res.error) || x.error || 'Xero did not accept the invoice.');
      changed();
    } catch (e) { toast.error(errMsg(e)); } finally { setXBusy(false); }
  };
  const doQuery = async (note) => {
    await api.post(`/sc/claims/invoices/${inv.id}/query`, { note });
    toast.success('Query recorded and logged as a follow-up');
    changed();
  };
  const doWriteOff = async (reason) => {
    await api.post(`/sc/claims/invoices/${inv.id}/write-off`, { reason });
    toast.success(`${inv.invoice_number} written off`);
    changed();
  };
  const doVoid = async (reason) => {
    await api.post(`/sc/claims/payments/${voiding.id}/void`, { reason });
    toast.success('Payment voided. It stays on record.');
    changed();
  };

  const menu = inv ? [
    can.claim && isOpen && { label: 'Record a query or decline', icon: MessageSquare, onClick: () => setModal('query') },
    can.claim && isOpen && { label: paid > 0 ? 'Reissue (not possible: payments recorded)' : 'Correct and reissue', icon: FilePlus2, disabled: paid > 0, onClick: () => setModal('reissue') },
    can.writeOff && isOpen && 'sep',
    can.writeOff && isOpen && { label: 'Write off', icon: Ban, danger: true, onClick: () => setModal('writeoff') },
  ] : [];

  return (
    <Sheet open={open} onClose={onClose} icon={Receipt} width="lg"
      title={inv ? `Invoice ${inv.invoice_number}` : 'Invoice'}
      sub={inv ? `${data.participant?.name || inv.client_name || 'Participant'} · ${(FUNDING[inv.funding_type] || {}).label || words(inv.funding_type)}` : undefined}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
      {d.error ? <ErrorBlock error={d.error} onRetry={reload} /> : !inv ? <LoadingBlock rows={6} /> : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill map={INVOICE_STATUS} status={inv.status} />
            {Number(inv.days_overdue) > 0 && <Pill tone="red" label={`${plural(Number(inv.days_overdue), 'day')} overdue`} />}
            {inv.query_note && isOpen && <Pill tone="amber" label="Queried by the plan manager" />}
            {inv.reissue_of_number && <Pill tone="slate" label={`Reissue of ${inv.reissue_of_number}`} />}
          </div>

          <FigureStrip items={[
            { label: 'Total', value: inv.total, sub: Number(inv.gst) ? `incl. ${money(inv.gst)} GST` : 'No GST' },
            { label: 'Paid', value: inv.paid_amount, tone: 'emerald' },
            { label: 'Balance', value: inv.balance, tone: overdue ? 'red' : 'amber', sub: inv.status === 'written_off' ? 'Written off' : undefined },
          ]} />

          <div className="flex flex-wrap items-center gap-2">
            {can.recon && isOpen && <Button variant="success" icon={Banknote} onClick={() => setModal('payment')}>Record payment</Button>}
            {(can.claim || can.recon) && <DownloadButton url={`/sc/claims/invoices/${inv.id}/pdf`} params={{ download: 1 }} filename={`${inv.invoice_number}.pdf`} icon={FileText} size="md" onDone={changed}>Download PDF</DownloadButton>}
            {can.claim && inv.status !== 'reissued' && <Button variant="secondary" icon={Mail} onClick={() => setModal('email')}>Email invoice</Button>}
            {can.claim && isOpen && overdue && <Button variant="secondary" icon={MailWarning} onClick={() => setModal('reminder')}>Send overdue reminder</Button>}
            {can.claim && !inv.xero_invoice_id && !['reissued', 'written_off'].includes(inv.status) && (
              <Button variant="secondary" icon={Send} loading={xBusy} onClick={pushXero}>{inv.xero_error ? 'Retry Xero' : 'Send to Xero'}</Button>
            )}
            <RowMenu items={menu} label="More invoice actions" />
          </div>
          {(can.claim || can.recon) && inv.status !== 'reissued' && <InvoiceLockNote count={inv.lock_count ?? data.lock_count} />}

          {inv.email_error && <Banner tone="red" title="The last email was not sent">{inv.email_error}</Banner>}
          {inv.xero_error && !inv.xero_invoice_id && <Banner tone="amber" title="Not in Xero">{inv.xero_error}</Banner>}
          {inv.query_note && <Banner tone="amber" title={`Queried ${fmtDateTime(inv.queried_at)}`}>{inv.query_note}</Banner>}
          {inv.status === 'written_off' && <Banner tone="blue" title={`Written off ${fmtDateTime(inv.written_off_at)}`}>{inv.written_off_reason}</Banner>}
          {inv.short_paid_reason && <Banner tone="amber" title="Closed short-paid">{inv.short_paid_reason}</Banner>}

          <Facts items={[
            ['Bill to', inv.recipient_name || '—'], ['Email', inv.recipient_email || 'No email address'],
            ['Issued', fmtDate(inv.issue_date)], ['Due', fmtDate(inv.due_date)],
            ['Participant', `${data.participant?.name || inv.client_name || '—'}${data.participant?.ndis_number ? ` · NDIS ${data.participant.ndis_number}` : ''}`],
            ['Batch', inv.batch_id ? <button key="b" type="button" onClick={() => onOpenBatch(inv.batch_id)} className="font-semibold text-indigo-600 hover:text-indigo-800 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">{inv.batch_code || 'Open batch'}</button> : '—'],
            ['Emailed', inv.emailed_at ? fmtDateTime(inv.emailed_at) : 'Not yet'],
            ['Xero', inv.xero_invoice_id ? `${words(inv.xero_status || 'draft')}${inv.xero_synced_at ? ` · ${fmtDateTime(inv.xero_synced_at)}` : ''}` : 'Not sent'],
            ['Overdue reminders', Number(inv.reminder_count) ? `${inv.reminder_count} · last ${fmtDateTime(inv.last_reminder_at)}` : 'None sent'],
          ]} />

          <Section title="Invoice lines" icon={FileText}>
            <div className="overflow-x-auto rounded-xl ring-1 ring-inset ring-slate-200">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[12px] font-semibold text-slate-600 border-b border-slate-100">
                    <th scope="col" className="px-3 py-2">Date</th><th scope="col" className="px-3 py-2">Description</th><th scope="col" className="px-3 py-2">Support item</th>
                    <th scope="col" className="px-3 py-2 text-right">Hours</th><th scope="col" className="px-3 py-2 text-right">Rate</th>
                    <th scope="col" className="px-3 py-2">GST</th><th scope="col" className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.document_lines || []).map((x, i) => (
                    <tr key={`${x.claim_reference}-${i}`} className="border-b border-slate-100 align-top">
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(x.service_date)}</td>
                      <td className="px-3 py-2">{x.label || x.description}</td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap text-slate-500">{x.support_item}</td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{hrs(x.minutes)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(x.unit_price)}</td>
                      <td className="px-3 py-2">{x.gst_code}</td>
                      <td className="px-3 py-2 text-right"><Money value={x.amount} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="text-[13px]">
                  <tr><td colSpan={6} className="px-3 pt-2.5 text-right text-slate-500">Subtotal</td><td className="px-3 pt-2.5 text-right"><Money value={inv.subtotal} /></td></tr>
                  <tr><td colSpan={6} className="px-3 py-1 text-right text-slate-500">GST</td><td className="px-3 py-1 text-right"><Money value={inv.gst} /></td></tr>
                  <tr><td colSpan={6} className="px-3 pb-2.5 text-right font-semibold text-slate-900">Total</td><td className="px-3 pb-2.5 text-right font-semibold text-slate-900"><Money value={inv.total} /></td></tr>
                </tfoot>
              </table>
            </div>
          </Section>

          <Section title="Payments" icon={Banknote}
            actions={can.recon && isOpen && <Button size="sm" variant="soft" icon={Banknote} onClick={() => setModal('payment')}>Record payment</Button>}>
            {!(data.payments || []).length ? <p className="text-[13px] text-slate-500">No payments recorded.</p> : (
              <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-inset ring-slate-200">
                {data.payments.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-3 px-3.5 py-2.5 text-[13px]">
                    <div className="min-w-0 flex-1">
                      <p className={clsx('text-slate-800', p.voided_at && 'line-through text-slate-500')}>
                        {fmtDate(p.payment_date)}{p.reference ? ` · ${p.reference}` : ''}
                      </p>
                      <p className="text-[12px] text-slate-500">
                        {p.source === 'bank_import' ? 'Bank statement import' : p.source === 'xero' ? 'Xero' : 'Recorded by hand'}
                        {p.voided_at ? ` · voided ${fmtDateTime(p.voided_at)}: ${p.void_reason || ''}` : ''}
                      </p>
                    </div>
                    {p.voided_at ? <Pill tone="slate" label="Voided" /> : null}
                    <Money value={p.amount} className={clsx('font-semibold', p.voided_at ? 'text-slate-500 line-through' : 'text-emerald-700')} />
                    {can.recon && !p.voided_at && (
                      <Button size="sm" variant="ghost" icon={Undo2} onClick={() => setVoiding(p)} aria-label={`Void the ${money(p.amount)} payment of ${fmtDate(p.payment_date)}`}>Void</Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <FollowUps invoiceId={inv.id} items={data.followups || NO_ROWS} can={can} onAdded={changed} />

          {(data.versions || []).length > 1 && (
            <Section title="Versions" icon={History} sub="A reissued invoice keeps the number with -R1, -R2 and so on.">
              <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-inset ring-slate-200">
                {data.versions.map((v) => (
                  <li key={v.id} className="flex flex-wrap items-center gap-3 px-3.5 py-2.5 text-[13px]">
                    {v.id === inv.id ? <span className="font-semibold text-slate-900">{v.invoice_number} (this one)</span>
                      : <OpenButton className="text-indigo-600 hover:text-indigo-800" onClick={() => onOpenInvoice(v.id)}>{v.invoice_number}</OpenButton>}
                    <span className="text-slate-500">{fmtDate(v.issue_date)}</span>
                    <span className="flex-1" />
                    <Money value={v.total} />
                    <StatusPill map={INVOICE_STATUS} status={v.status} />
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}

      <EmailModal open={modal === 'email' || modal === 'reminder'} reminder={modal === 'reminder'} invoice={inv} onClose={() => setModal(null)} onSent={changed} />
      <PaymentModal open={modal === 'payment'} invoice={inv} onClose={() => setModal(null)} onSaved={changed} />
      <ReissueModal open={modal === 'reissue'} data={data} onClose={() => setModal(null)}
        onDone={(newId) => { setModal(null); onChanged(); onOpenInvoice(newId); }} />
      <ReasonModal open={modal === 'query'} onClose={() => setModal(null)} title="Record a query or decline" confirmLabel="Record"
        sub="What the plan manager or participant said. It is logged as a follow-up; then correct and reissue, or write off." label="What did they say?" onConfirm={doQuery} />
      <ReasonModal open={modal === 'writeoff'} onClose={() => setModal(null)} danger confirmLabel="Write off"
        title={inv ? `Write off ${inv.invoice_number}` : 'Write off'} sub={inv ? `${money(inv.balance)} will not be collected. Its lines close as Written off.` : undefined}
        label="Reason" onConfirm={doWriteOff} />
      <ReasonModal open={!!voiding} onClose={() => setVoiding(null)} danger confirmLabel="Void payment"
        title="Void this payment" sub={voiding ? `${money(voiding.amount)} received ${fmtDate(voiding.payment_date)}. It stays on record as voided and the balance reopens.` : undefined}
        label="Why is it being voided?" onConfirm={doVoid} />
    </Sheet>
  );
}

function EmailModal({ open, onClose, invoice, reminder, onSent }) {
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const invId = invoice ? invoice.id : null;
  const seed = useRef(invoice);
  seed.current = invoice;
  useEffect(() => { if (open) { setTo(seed.current?.recipient_email || ''); setErr(null); } }, [open, invId]);
  const send = async (e) => {
    if (e) e.preventDefault();
    const addr = to.trim();
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/sc/claims/invoices/${invoice.id}/email`, {
        to: addr && addr !== (invoice.recipient_email || '') ? addr : undefined, reminder: reminder ? true : undefined,
      });
      onSent();
      if (r.data?.sent) { toast.success(`${reminder ? 'Reminder' : invoice.invoice_number} emailed to ${r.data.to}`); onClose(); } else setErr(r.data?.error || 'The email was not sent.');
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  if (!invoice) return null;
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} width="sm"
      title={reminder ? `Send an overdue reminder for ${invoice.invoice_number}` : `Email ${invoice.invoice_number}`}
      sub={reminder ? 'Emails the overdue reminder with the invoice PDF attached, and logs a follow-up.' : 'Emails the invoice PDF and saves it to the participant’s Communications.'}
      footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button type="submit" form="sc-email-form" icon={reminder ? MailWarning : Mail} loading={busy}>{reminder ? 'Send reminder' : 'Send email'}</Button></>}>
      <form id="sc-email-form" onSubmit={send} className="space-y-3">
        <Field label="Send to" hint={invoice.recipient_email ? `The invoice recipient is ${invoice.recipient_email}. Change it here to send this one copy elsewhere.` : 'There is no recipient email on the participant file. Add one there for future invoices.'}>
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@example.com" autoComplete="off" />
        </Field>
        {err && <Banner tone="red" title="Not sent">{err}</Banner>}
      </form>
    </Modal>
  );
}

// Record a payment (8.8). With `choices` (and no invoice) the user picks the invoice: a bank receipt
// that did not match is recorded this way. With `bank` ({ importId, row }) the payment is linked to
// that receipt and the receipt leaves the list to review in the same step.
function PaymentModal({ open, onClose, invoice, choices, choicesLoading, prefill, onSaved, bank }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const invId = invoice ? invoice.id : null;
  const preInv = prefill ? prefill.invoiceId || '' : '';
  const seed = useRef({ invoice, prefill });
  seed.current = { invoice, prefill };
  useEffect(() => {
    if (!open) return;
    const { invoice: i, prefill: p } = seed.current;
    setErr(null);
    setF({
      invoiceId: i?.id || p?.invoiceId || '', date: p?.date || todayIso(),
      amount: p?.amount != null ? String(p.amount) : i ? String(i.balance ?? '') : '',
      reference: p?.reference || '', final: false, short: '',
    });
  }, [open, invId, preInv]);
  const target = invoice || (choices || []).find((i) => i.id === f.invoiceId) || null;
  const balance = target ? round2(target.balance) : 0;
  const amt = round2(f.amount);
  const short = !!f.final && amt > 0 && amt < balance;
  const set = (k) => (e) => { const v = e && e.target ? e.target.value : e; setF((p) => ({ ...p, [k]: v })); };
  const save = async (e) => {
    if (e) e.preventDefault();
    if (!target) { setErr('Choose the invoice this payment is for.'); return; }
    if (!(amt > 0)) { setErr('Enter the amount received.'); return; }
    if (amt > balance) { setErr(`${money(amt)} is more than the ${money(balance)} balance.`); return; }
    if (short && !String(f.short || '').trim()) { setErr('Give the short-paid reason, or untick “final payment” to record a part payment.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/sc/claims/invoices/${target.id}/payments`, {
        date: f.date, amount: amt, reference: f.reference || undefined, final: !!f.final, short_paid_reason: short ? f.short.trim() : undefined,
        ...(bank ? { bank_import_id: bank.importId, bank_row: bank.row } : {}),
      });
      const st = r.data?.invoice?.status;
      toast.success(`Payment recorded against ${target.invoice_number}${st ? `: now ${statusOf(INVOICE_STATUS, st)[0].toLowerCase()}` : ''}${bank ? '. The receipt is off the list to review.' : ''}`);
      onSaved?.(r.data);
      onClose();
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} width="md"
      title={target ? `Record a payment for ${target.invoice_number}` : 'Record a payment'}
      sub={target ? `Balance ${money(balance)}${target.client_name ? ` · ${target.client_name}` : ''}` : 'Match the receipt to an open invoice.'}
      footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button type="submit" form="sc-pay-form" variant="success" icon={Banknote} loading={busy}>Record payment</Button></>}>
      <form id="sc-pay-form" onSubmit={save} className="space-y-4">
        {!invoice && (
          <Field label="Invoice" required hint={choicesLoading ? 'Loading open invoices…' : !(choices || []).length ? 'There are no open invoices with a balance.' : undefined}>
            <Select value={f.invoiceId || ''} onChange={set('invoiceId')} placeholder="Choose an open invoice"
              options={(choices || []).map((i) => ({ value: i.id, label: `${i.invoice_number} · ${i.client_name || 'Participant'}`, hint: `Balance ${money(i.balance)}${i.recipient_name ? ` · ${i.recipient_name}` : ''}` }))} />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date received" required><Input type="date" value={f.date || ''} max={todayIso()} onChange={set('date')} /></Field>
          <Field label="Amount" required hint={target ? `Balance ${money(balance)}` : undefined}>
            <Input type="number" inputMode="decimal" min="0.01" step="0.01" value={f.amount ?? ''} onChange={set('amount')} />
          </Field>
        </div>
        <Field label="Reference" hint="The bank reference or remittance number."><Input value={f.reference || ''} maxLength={200} onChange={set('reference')} /></Field>
        <Checkbox checked={!!f.final} onChange={(v) => setF((p) => ({ ...p, final: v }))} label="This is the final payment for this invoice" />
        {short && (
          <Field label="Short-paid reason" required hint={`${money(balance - amt)} will not be paid. The invoice closes as Paid with this reason on record.`}>
            <Textarea rows={2} value={f.short || ''} onChange={set('short')} />
          </Field>
        )}
        {!f.final && target && amt > 0 && amt < balance && (
          <p className="text-[12.5px] text-slate-500">Recorded as a part payment: {money(round2(balance - amt))} stays outstanding.</p>
        )}
        {err && <Banner tone="red">{err}</Banner>}
      </form>
    </Modal>
  );
}

// 8.6: correct and reissue as -R1, -R2 …; the original changes to Reissued.
function ReissueModal({ open, onClose, data, onDone }) {
  const lines = useMemo(() => data?.claim_lines || NO_ROWS, [data]);
  const inv = data?.invoice;
  const [fix, setFix] = useState({});
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { if (open) { setFix({}); setReason(''); setErr(null); } }, [open]);
  const setLine = (lid, patch) => setFix((m) => ({ ...m, [lid]: { ...(m[lid] || {}), ...patch } }));
  const kept = lines.filter((x) => !fix[x.id]?.remove);
  const failures = useMemo(() => Object.fromEntries(((err && err.failures) || []).map((f) => [f.line_id, f.reasons || []])), [err]);
  const go = async (e) => {
    if (e) e.preventDefault();
    if (!kept.length) { setErr({ message: 'Keep at least one line. To cancel the whole invoice, write it off instead.' }); return; }
    setBusy(true); setErr(null);
    try {
      const body = {
        reason: reason.trim() || undefined,
        lines: lines.filter((x) => fix[x.id]?.remove || String(fix[x.id]?.support_item || '').trim()).map((x) => ({
          line_id: x.id, remove: fix[x.id]?.remove ? true : undefined, support_item: String(fix[x.id]?.support_item || '').trim() || undefined,
        })),
      };
      const r = await api.post(`/sc/claims/invoices/${inv.id}/reissue`, body);
      const out = r.data || {};
      toast.success(`Reissued as ${out.invoice?.invoice_number}. ${inv.invoice_number} is now Reissued.`);
      if (out.email && !out.email.sent) toast.error(`${out.invoice?.invoice_number} was not emailed: ${out.email.error || 'unknown reason'}`, { duration: 8000 });
      (out.warnings || []).forEach((w) => toast(w, { duration: 9000 }));
      if (out.invoice?.id) onDone(out.invoice.id); else onClose();
    } catch (ex) {
      const dd = errData(ex);
      setErr({ message: errMsg(ex), failures: dd.failures || [] });
    } finally { setBusy(false); }
  };
  if (!inv) return null;
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} width="lg" title={`Correct and reissue ${inv.invoice_number}`}
      sub="The new invoice keeps the number with -R1, -R2 and so on, and the original changes to Reissued. Kept lines are checked again with today’s details; removed lines are written off. The new invoice is emailed and sent to Xero."
      footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button type="submit" form="sc-reissue-form" icon={FilePlus2} loading={busy}>Reissue invoice</Button></>}>
      <form id="sc-reissue-form" onSubmit={go} className="space-y-4">
        {err && <Banner tone="red" title="Not reissued">{err.message}</Banner>}
        <ul className="space-y-2.5">
          {lines.map((x) => {
            const removed = !!fix[x.id]?.remove;
            const why = failures[x.id];
            return (
              <li key={x.id} className={clsx('rounded-xl ring-1 ring-inset px-3.5 py-3', why ? 'ring-rose-200 bg-rose-50/40' : removed ? 'ring-slate-200 bg-slate-50' : 'ring-slate-200')}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className={clsx('text-[13px] font-semibold', removed ? 'text-slate-500 line-through' : 'text-slate-900')}>{fmtDate(x.service_date)} · {claimTypeText(x.claim_type, x.cancellation_reason)}</p>
                  <p className="text-[12.5px] text-slate-600 tabular-nums">{hrs(x.minutes)} · {money(x.amount)}</p>
                </div>
                <p className="text-[12px] text-slate-500 tabular-nums">{x.support_item}</p>
                <div className="mt-2 flex flex-wrap items-end gap-4">
                  <Checkbox checked={removed} onChange={(v) => setLine(x.id, { remove: v })} label="Remove this line" />
                  {!removed && (
                    <div className="w-full sm:w-56">
                      <Field label="Correct the support item" hint={`Blank keeps ${x.support_item}.`}>
                        <Input value={fix[x.id]?.support_item || ''} placeholder={x.support_item} maxLength={40} onChange={(ev) => setLine(x.id, { support_item: ev.target.value })} />
                      </Field>
                    </div>
                  )}
                </div>
                {why && <ul className="mt-2 space-y-0.5 text-[12.5px] text-rose-700">{why.map((m, i) => <li key={i}>{m}</li>)}</ul>}
              </li>
            );
          })}
        </ul>
        <Field label="Reason for the reissue" hint="Kept on the audit log and on removed lines.">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="The plan manager asked for the support item to be corrected" />
        </Field>
      </form>
    </Modal>
  );
}

function FollowUps({ invoiceId, items, can, onAdded }) {
  const [f, setF] = useState({ date: todayIso(), method: 'call', note: '' });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!f.note.trim()) { toast.error('Add a note about the follow-up.'); return; }
    setBusy(true);
    try {
      await api.post(`/sc/claims/invoices/${invoiceId}/followups`, { date: f.date, method: f.method, note: f.note.trim() });
      toast.success('Follow-up logged');
      setF((x) => ({ ...x, date: todayIso(), note: '' }));
      onAdded();
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Section title="Follow-ups" icon={MessageSquare} sub="Calls, emails and reminders about this invoice. Overdue reminders are logged here automatically.">
      {!items.length ? <p className="text-[13px] text-slate-500">No follow-ups yet.</p> : (
        <ol className="space-y-2.5">
          {items.map((x) => (
            <li key={x.id} className="rounded-xl bg-slate-50 px-3.5 py-2.5">
              <div className="flex flex-wrap items-center gap-2 text-[12px] text-slate-600">
                <Pill tone={x.method === 'query' ? 'amber' : 'violet'} label={FOLLOWUP_LABEL[x.method] || words(x.method)} />
                <span>{fmtDate(x.followup_date)}</span>
                {x.created_by_name && <span>· {x.created_by_name}</span>}
              </div>
              <p className="mt-1 text-[13px] text-slate-800 whitespace-pre-wrap break-words">{x.note}</p>
            </li>
          ))}
        </ol>
      )}
      {can.recon && (
        <form onSubmit={add} className="rounded-xl ring-1 ring-inset ring-slate-200 p-3.5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><Input type="date" value={f.date} max={todayIso()} onChange={(e) => setF((x) => ({ ...x, date: e.target.value }))} /></Field>
            <Field label="How"><Select value={f.method} onChange={(v) => setF((x) => ({ ...x, method: v }))} options={FOLLOWUP_METHODS} /></Field>
          </div>
          <Field label="Note"><Textarea rows={2} value={f.note} onChange={(e) => setF((x) => ({ ...x, note: e.target.value }))} placeholder="Called the plan manager; payment is in Friday’s run." /></Field>
          <div className="flex justify-end"><Button type="submit" size="sm" icon={MessageSquare} loading={busy}>Log follow-up</Button></div>
        </form>
      )}
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Payments: bank statement import (8.8)
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Upload → check the proposed matches (nothing is written) → tick → "Record N payments" (the import,
// with only the ticked matches) → review the receipts that did not match: record each against an
// invoice, or mark it dealt with and say why. `imports` (loaded by the page) carries each import's
// receipts with their review, and to_review.
function PaymentsTab({ imports, onChanged, onOpenInvoice }) {
  const { reload: reloadImports } = imports;
  const [file, setFile] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [checking, setChecking] = useState(false);
  const [recording, setRecording] = useState(false);
  const [pv, setPv] = useState(null);                    // the preview (dry run) of `file`
  const [accepted, setAccepted] = useState(() => new Set());
  const [out, setOut] = useState(null);                  // what the import recorded
  const [err, setErr] = useState(null);
  const [reviewId, setReviewId] = useState(null);        // the import whose receipts are open
  const [match, setMatch] = useState(null);              // { importId, receipt, back }: record against an invoice
  const [clearing, setClearing] = useState(null);        // { importId, receipt, back }: mark dealt with
  const [reopening, setReopening] = useState(null);      // { importId, receipt, back }
  const invoices = useLoad(match ? '/sc/claims/invoices' : null, OPEN_INVOICE_PARAMS);
  const openInvoices = useMemo(() => ((invoices.data && invoices.data.rows) || NO_ROWS).filter((i) => OPEN_INVOICE.includes(i.status) && Number(i.balance) > 0), [invoices.data]);
  const guess = useMemo(() => {
    if (!match) return null;
    const r = match.receipt;
    if (r.invoice_number) { const named = openInvoices.find((i) => i.invoice_number === r.invoice_number); if (named) return named; }
    const d = normRef(r.description);
    return openInvoices.filter((i) => d.includes(normRef(i.invoice_number))).sort((a, b) => b.invoice_number.length - a.invoice_number.length)[0] || null;
  }, [match, openInvoices]);
  const list = imports.data || NO_ROWS;
  const reviewing = reviewId ? list.find((x) => x.id === reviewId) || null : null;

  const pick = (fl) => { setFile(fl); setPv(null); setOut(null); setErr(null); };
  const form = (fields) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
    return fd;
  };
  const check = async () => {
    if (!file) return;
    setChecking(true); setErr(null); setPv(null); setOut(null);
    try {
      const r = await api.post('/sc/claims/bank-import', form({ dry_run: '1' }));
      setPv(r.data);
      setAccepted(new Set((r.data?.proposed || []).filter((p) => p.accept).map((p) => p.key)));
    } catch (e) { setErr(errMsg(e)); } finally { setChecking(false); }
  };
  const record = async () => {
    if (!file) return;
    setRecording(true); setErr(null);
    try {
      const r = await api.post('/sc/claims/bank-import', form({ accept: JSON.stringify([...accepted]) }));
      const d = r.data || {};
      setOut({ ...d, file_name: file.name }); setPv(null); setFile(null); setInputKey((k) => k + 1);
      toast.success(`${plural((d.matched || []).length, 'payment')} recorded${d.to_review ? `; ${plural(d.to_review, 'receipt')} to review` : ''}`);
      onChanged();
    } catch (e) { setErr(errMsg(e)); } finally { setRecording(false); }
  };
  const cancel = () => { setPv(null); setFile(null); setInputKey((k) => k + 1); setErr(null); };
  // An action on one receipt; from the review list, the list opens again afterwards.
  const [depositing, setDepositing] = useState(null);   // { importId, receipt, back }: record as an NDIA deposit
  const act = (setter) => (importId, receipt) => { const back = reviewId === importId; setReviewId(null); setter({ importId, receipt, back }); };
  const done = (state, setter) => { setter(null); if (state && state.back) setReviewId(state.importId); };
  const reopen = async () => {
    const r = await api.post(`/sc/claims/bank-imports/${reopening.importId}/receipts/${reopening.receipt.row}/reopen`, {});
    toast.success(`Back on the list to review (${plural(Number(r.data?.to_review) || 0, 'receipt')} to review in that statement)`);
    reloadImports();
  };

  const pastCols = [
    { accessorKey: 'created_at', header: 'Imported', cell: ({ row: { original: x } }) => (
      <div><p className="whitespace-nowrap">{fmtDateTime(x.created_at)}</p>{x.created_by_name && <p className="text-[12px] text-slate-500">{x.created_by_name}</p>}</div>
    ) },
    { accessorKey: 'file_name', header: 'File', cell: ({ getValue }) => <span className="break-all">{getValue() || '—'}</span> },
    { accessorKey: 'row_count', header: 'Receipts', meta: { align: 'right' } },
    { accessorKey: 'matched_count', header: 'Recorded', meta: { align: 'right' }, cell: ({ getValue }) => <span className={Number(getValue()) ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>{getValue()}</span> },
    { id: 'review', header: 'To review', meta: { align: 'right' }, accessorFn: (x) => Number(x.to_review) || 0, cell: ({ row: { original: x } }) => {
      const n = Number(x.to_review) || 0;
      const listed = Array.isArray(x.unmatched) ? x.unmatched.length : 0;
      if (n) return <OpenButton className="text-amber-700 hover:text-amber-900 whitespace-nowrap" label={`Review ${plural(n, 'receipt')} from ${x.file_name || 'this statement'}`} onClick={() => setReviewId(x.id)}>{n} to review</OpenButton>;
      if (listed) return <OpenButton className="text-emerald-700 hover:text-emerald-900 whitespace-nowrap" label={`See the ${plural(listed, 'receipt')} dealt with from ${x.file_name || 'this statement'}`} onClick={() => setReviewId(x.id)}>All dealt with</OpenButton>;
      return <span className="text-slate-500">—</span>;
    } },
  ];

  return (
    <div className="space-y-5">
      <Panel title="Import a bank statement" icon={Landmark} hue="violet"
        sub="Plan-managed and self-managed payments. Receipts are matched to open invoices by the invoice number in the description and the amount.">
        <ol className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 text-[13px] text-slate-600">
          {['Export a CSV of money received from your bank (date, amount or credit, and description).',
            'Check it here. Matches are proposed and nothing is recorded until you confirm; importing the same statement again records nothing twice.',
            'Record the payments you tick, then review the receipts that did not match.'].map((t, i) => (
            <li key={i} className="flex gap-2.5 rounded-xl bg-slate-50 px-3 py-2.5">
              <span className="w-5 h-5 rounded-full bg-violet-600 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" aria-hidden>{i + 1}</span>{t}
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-0 sm:min-w-[240px]"><FileInput label="Bank statement (CSV)" onFile={pick} resetKey={inputKey} disabled={checking || recording} /></div>
        </div>
        {file && !pv && (
          <div className="mt-4">
            <Banner tone="blue" title={`Ready to check ${file.name}`}
              action={<Button icon={ListChecks} loading={checking} onClick={check}>Check matches</Button>}>
              You see the proposed matches first. Nothing is recorded until you confirm.
            </Banner>
          </div>
        )}
        {err && <div className="mt-4"><Banner tone="red" title={pv ? 'The payments were not recorded' : 'The statement was not checked'}>{err}</Banner></div>}
        {pv && (
          <div className="mt-5">
            <BankPreview pv={pv} accepted={accepted} setAccepted={setAccepted} recording={recording} onRecord={record} onCancel={cancel} onOpenInvoice={onOpenInvoice} />
          </div>
        )}
        {out && !pv && <div className="mt-5"><BankImportOutcome out={out} onOpenInvoice={onOpenInvoice} onReview={() => setReviewId(out.import_id)} /></div>}
      </Panel>

      <Panel title="Past imports" icon={History} hue="violet" sub="Each statement imported, with the receipts that still need a look.">
        <DataTable columns={pastCols} data={list} loading={imports.loading} error={imports.error} onRetry={reloadImports} dense
          onRowClick={(x) => (Array.isArray(x.unmatched) && x.unmatched.length ? setReviewId(x.id) : undefined)}
          empty={<Empty icon={Landmark} title="No statements imported yet">Import a bank statement above to record plan- and self-managed payments.</Empty>} />
      </Panel>

      <ReceiptsReview imp={reviewing} onClose={() => setReviewId(null)} onRecord={act(setMatch)} onClear={act(setClearing)} onReopen={act(setReopening)} onDeposit={act(setDepositing)} />
      <DepositFromReceiptModal target={depositing} onClose={() => done(depositing, setDepositing)} onSaved={() => { reloadImports(); onChanged(); }} />
      <PaymentModal open={!!match} onClose={() => done(match, setMatch)} choices={openInvoices} choicesLoading={invoices.loading}
        prefill={match ? { invoiceId: guess ? guess.id : '', date: match.receipt.date, amount: match.receipt.amount, reference: String(match.receipt.description || '').slice(0, 200) } : null}
        bank={match ? { importId: match.importId, row: match.receipt.row } : null} onSaved={onChanged} />
      <ClearReceiptModal target={clearing} onClose={() => done(clearing, setClearing)} onDone={reloadImports} />
      <ConfirmModal open={!!reopening} onClose={() => done(reopening, setReopening)} title="Put this receipt back on the list to review?" confirmLabel="Reopen" onConfirm={reopen}>
        {reopening ? `${money(reopening.receipt.amount)} received ${fmtDate(reopening.receipt.date)}. The reason it was dealt with stays on the audit trail.` : null}
      </ConfirmModal>
    </div>
  );
}

// The dry run: the proposed matches to tick (exact ones ticked), and what will go on the list to review.
function BankPreview({ pv, accepted, setAccepted, recording, onRecord, onCancel, onOpenInvoice }) {
  const proposed = pv.proposed || NO_ROWS;
  const unmatched = pv.unmatched || NO_ROWS;
  const dups = pv.duplicates || NO_ROWS;
  const ticked = proposed.filter((p) => accepted.has(p.key));
  const n = ticked.length;
  const unticked = proposed.length - n;
  const newToReview = unmatched.filter((r) => !r.seen_before).length + unticked;
  const toggle = (key, on) => setAccepted((s) => { const x = new Set(s); if (on) x.add(key); else x.delete(key); return x; });
  return (
    <div className="space-y-4" aria-live="polite">
      {pv.previous_import && (
        <Banner tone="amber" title="This statement was imported before">
          {pv.previous_import.file_name || 'It'} was imported {fmtDateTime(pv.previous_import.created_at)}. Payments already recorded are skipped, so nothing is recorded twice.
        </Banner>
      )}
      <FigureStrip items={[
        { label: 'Receipts in the file', value: pv.receipts || 0, plain: true },
        { label: 'Proposed matches', value: proposed.length, plain: true, tone: 'emerald' },
        { label: 'Not matched', value: unmatched.length, plain: true, tone: 'amber' },
        { label: 'Already recorded', value: dups.length, plain: true },
        { label: 'Rows ignored', value: pv.ignored_rows || 0, plain: true, sub: 'Debits or unreadable' },
      ]} />
      {proposed.length > 0 ? (
        <Section title="Proposed matches" icon={ListChecks} sub="Tick the payments to record. Receipts that pay an invoice’s balance are ticked for you; check part payments before you tick them.">
          <div className="overflow-x-auto rounded-xl ring-1 ring-inset ring-slate-200">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[12px] font-semibold text-slate-600 border-b border-slate-100">
                  <th scope="col" className="relative px-3 py-2 w-10">
                    <Checkbox aria-label="Tick every proposed match" checked={n === proposed.length ? true : n ? 'indeterminate' : false}
                      onChange={(v) => setAccepted(new Set(v ? proposed.map((p) => p.key) : []))} />
                  </th>
                  <th scope="col" className="px-3 py-2">Received</th>
                  <th scope="col" className="px-3 py-2 text-right">Amount</th>
                  <th scope="col" className="px-3 py-2">Description</th>
                  <th scope="col" className="px-3 py-2">Invoice</th>
                  <th scope="col" className="px-3 py-2">Match</th>
                </tr>
              </thead>
              <tbody>
                {proposed.map((p) => (
                  <tr key={p.key} className={clsx('border-b border-slate-100 last:border-0 align-top', accepted.has(p.key) && 'bg-emerald-50/40')}>
                    <td className="px-3 py-2.5"><Checkbox aria-label={`Record ${money(p.amount)} against ${p.invoice_number}`} checked={accepted.has(p.key)} onChange={(v) => toggle(p.key, v)} /></td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(p.date)}<p className="text-[12px] text-slate-500">Row {p.row}</p></td>
                    <td className="px-3 py-2.5 text-right"><Money value={p.amount} /></td>
                    <td className="px-3 py-2.5 break-words min-w-[160px] max-w-[260px]">{p.description || '—'}</td>
                    <td className="px-3 py-2.5">
                      <OpenButton className="text-indigo-600 hover:text-indigo-800 whitespace-nowrap" onClick={() => onOpenInvoice(p.invoice_id)}>{p.invoice_number}</OpenButton>
                      <p className="text-[12px] text-slate-500">{p.client_name ? `${p.client_name} · ` : ''}balance {money(p.balance)}</p>
                    </td>
                    <td className="px-3 py-2.5 min-w-[200px]">
                      {p.confidence === 'high' ? <Pill tone="emerald" label="Pays the balance" /> : <Pill tone="amber" label="Part payment" />}
                      <p className="mt-1 text-[12px] leading-snug text-slate-600">{p.reason}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ) : <p className="text-[13px] text-slate-600">No receipt in this statement matches an open invoice.</p>}
      {unmatched.length > 0 && (
        <Section title="Not matched" icon={AlertTriangle} sub="These go on the list to review, so you can record each by hand or say why it needs no match.">
          <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-inset ring-slate-200">
            {unmatched.map((r) => (
              <li key={r.key || r.row} className="px-3.5 py-2.5 text-[13px]">
                <p className="font-semibold text-slate-900 tabular-nums">{money(r.amount)} <span className="font-normal text-slate-600">· {fmtDate(r.date)} · row {r.row}</span></p>
                <p className="text-slate-800 break-words">{r.description || 'No description'}</p>
                <p className="text-[12.5px] text-amber-800">{r.reason}</p>
                {r.seen_before && (
                  <p className="text-[12.5px] text-slate-600">
                    Already listed by an earlier import{r.seen_before.file_name ? ` (${r.seen_before.file_name})` : ''}
                    {r.seen_before.cleared ? `, dealt with there: ${r.seen_before.reason || 'cleared'}` : ', still to review there'}, so it is not listed again.
                  </p>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}
      {dups.length > 0 && (
        <p className="text-[12.5px] text-slate-600">Already recorded, so skipped: {dups.map((x) => `row ${x.row} (${x.invoice_number}, ${money(x.amount)})`).join('; ')}.</p>
      )}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button variant="success" icon={Banknote} loading={recording} disabled={!n && !newToReview} onClick={onRecord}>
          {n ? `Record ${plural(n, 'payment')} (${money(sumOf(ticked, (p) => p.amount))})` : 'Save the receipts to review'}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={recording}>Cancel</Button>
        {!n && !newToReview && <p className="text-[12.5px] text-slate-600">Nothing new to record or review in this statement.</p>}
        {n > 0 && unticked > 0 && <p className="text-[12.5px] text-slate-600">{plural(unticked, 'unticked match', 'unticked matches')} will go on the list to review, not be recorded.</p>}
      </div>
    </div>
  );
}

function BankImportOutcome({ out, onOpenInvoice, onReview }) {
  const matched = out.matched || NO_ROWS;
  const dups = out.duplicates || NO_ROWS;
  const toReview = Number(out.to_review) || 0;
  const listedBefore = (out.unmatched || NO_ROWS).length - toReview;
  return (
    <div className="space-y-4" aria-live="polite">
      <FigureStrip items={[
        { label: 'Receipts in the file', value: out.receipts || 0, plain: true },
        { label: 'Recorded', value: matched.length, plain: true, tone: 'emerald' },
        { label: 'To review', value: toReview, plain: true, tone: 'amber' },
        { label: 'Already recorded', value: dups.length, plain: true },
        { label: 'Rows ignored', value: out.ignored_rows || 0, plain: true, sub: 'Debits or unreadable' },
      ]} />
      {matched.length > 0 && (
        <Section title="Recorded" icon={CheckCircle2}>
          <div className="overflow-x-auto rounded-xl ring-1 ring-inset ring-slate-200">
            <table className="w-full text-[13px]">
              <thead><tr className="text-left text-[12px] font-semibold text-slate-600 border-b border-slate-100">
                <th scope="col" className="px-3 py-2">Row</th><th scope="col" className="px-3 py-2">Date</th><th scope="col" className="px-3 py-2 text-right">Amount</th>
                <th scope="col" className="px-3 py-2">Invoice</th><th scope="col" className="px-3 py-2">Match</th>
              </tr></thead>
              <tbody>
                {matched.map((m) => (
                  <tr key={`${m.row}-${m.invoice_id}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2 tabular-nums">{m.row}</td><td className="px-3 py-2 whitespace-nowrap">{fmtDate(m.date)}</td>
                    <td className="px-3 py-2 text-right"><Money value={m.amount} /></td>
                    <td className="px-3 py-2">
                      <OpenButton className="text-indigo-600 hover:text-indigo-800" onClick={() => onOpenInvoice(m.invoice_id)}>{m.invoice_number}</OpenButton>
                      {m.client_name && <p className="text-[12px] text-slate-500">{m.client_name}</p>}
                    </td>
                    <td className="px-3 py-2">{m.exact ? <Pill tone="emerald" label="Paid the balance" /> : <Pill tone="amber" label="Part payment" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
      {toReview > 0 && (
        <NextStep tone="amber" title={`${plural(toReview, 'receipt')} to review`}
          actions={<Button icon={ListChecks} onClick={onReview}>Review receipts</Button>}>
          Record each against the right invoice, or mark it dealt with and say why.
        </NextStep>
      )}
      {listedBefore > 0 && (
        <p className="text-[12.5px] text-slate-600">{plural(listedBefore, 'receipt')} that did not match {listedBefore === 1 ? 'was' : 'were'} already listed by an earlier import, so {listedBefore === 1 ? 'it is' : 'they are'} not listed again.</p>
      )}
      {dups.length > 0 && (
        <p className="text-[12.5px] text-slate-600">Already recorded, so skipped: {dups.map((x) => `row ${x.row} (${x.invoice_number}, ${money(x.amount)})`).join('; ')}.</p>
      )}
    </div>
  );
}

// One statement's receipts that did not match: those still to review first, each with its actions;
// the ones dealt with show why, who and when.
function ReceiptsReview({ imp, onClose, onRecord, onClear, onReopen, onDeposit }) {
  const [show, setShow] = useState('open');
  const impId = imp ? imp.id : null;
  const toReview = imp ? Number(imp.to_review) || 0 : 0;
  // Each time a statement opens: its receipts to review, or all of them when none is left.
  const openCount = useRef(toReview);
  openCount.current = toReview;
  useEffect(() => { if (impId) setShow(openCount.current ? 'open' : 'all'); }, [impId]);
  const rows = useMemo(() => {
    const all = (imp && imp.unmatched) || NO_ROWS;
    const sorted = [...all].sort((a, b) => Number(!!a.review) - Number(!!b.review) || a.row - b.row);
    return show === 'open' ? sorted.filter((r) => !r.review) : sorted;
  }, [imp, show]);
  const total = imp && Array.isArray(imp.unmatched) ? imp.unmatched.length : 0;
  return (
    <Modal open={!!imp} onClose={onClose} width="lg"
      title={imp ? `Receipts to review · ${imp.file_name || 'statement'}` : 'Receipts to review'}
      sub="Record each against the right invoice, or mark it dealt with and say why."
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}>
      {imp && (
        <div className="space-y-3">
          <div className="max-w-full overflow-x-auto" role="group" aria-label="Show receipts">
            <Segmented size="sm" value={show} onChange={setShow} options={[
              { value: 'open', label: 'To review', count: toReview, alert: false },
              { value: 'all', label: 'All', count: total },
            ]} />
          </div>
          {!rows.length ? (
            <p className="flex items-center gap-2 text-[13px] text-slate-700"><CheckCircle2 size={15} className="text-emerald-600" aria-hidden />Every receipt from this statement is dealt with.</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-inset ring-slate-200">
              {rows.map((r) => (
                <li key={r.row} className="px-3.5 py-3 flex flex-col gap-2.5 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1 text-[13px]">
                    <p className="font-semibold text-slate-900 tabular-nums">{money(r.amount)} <span className="font-normal text-slate-600">· {fmtDate(r.date)} · row {r.row}</span></p>
                    <p className="text-slate-800 break-words">{r.description || 'No description'}</p>
                    {r.review ? (
                      <p className="mt-1 text-[12.5px] text-emerald-800 flex items-start gap-1.5">
                        <CheckCircle2 size={13} className="mt-0.5 flex-shrink-0" aria-hidden />
                        <span>
                          Dealt with: {r.review.reason}{r.review.note ? `. ${r.review.note}` : ''}
                          <span className="text-slate-600"> · {r.review.automatic ? 'automatically' : r.review.cleared_by_name || 'someone'}, {fmtDateTime(r.review.cleared_at)}</span>
                        </span>
                      </p>
                    ) : (
                      <>
                        <p className="mt-0.5 text-[12.5px] text-amber-800">{r.reason}</p>
                        {r.reopened && <p className="text-[12px] text-slate-600">Reopened {fmtDateTime(r.reopened.reopened_at)}{r.reopened.reopened_by_name ? ` by ${r.reopened.reopened_by_name}` : ''}{r.reopened.reason ? `: ${r.reopened.reason}` : ''}</p>}
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
                    {r.review ? (!r.review.automatic && (
                      <Button size="sm" variant="ghost" icon={Undo2} onClick={() => onReopen(imp.id, r)}
                        aria-label={`Put the ${money(r.amount)} receipt of ${fmtDate(r.date)} back on the list to review`}>Reopen</Button>
                    )) : (
                      <>
                        <Button size="sm" variant="secondary" icon={Banknote} onClick={() => onRecord(imp.id, r)}
                          aria-label={`Record the ${money(r.amount)} receipt of ${fmtDate(r.date)} against an invoice`}>Record payment</Button>
                        {onDeposit && <Button size="sm" variant="secondary" icon={Landmark} onClick={() => onDeposit(imp.id, r)}
                          aria-label={`Record the ${money(r.amount)} receipt of ${fmtDate(r.date)} as an NDIA deposit`}>NDIA deposit</Button>}
                        <Button size="sm" variant="ghost" icon={CheckCircle2} onClick={() => onClear(imp.id, r)}
                          aria-label={`Mark the ${money(r.amount)} receipt of ${fmtDate(r.date)} dealt with`}>Mark dealt with</Button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}

// A receipt from the bank statement that is an NDIA payment: choose the batch it pays (the one
// whose outstanding amount matches comes first); the deposit is recorded and the receipt cleared.
function DepositFromReceiptModal({ target, onClose, onSaved }) {
  const amount = target ? target.receipt.amount : null;
  const c = useLoad(target ? '/sc/claims/deposit-candidates' : null, useMemo(() => ({ amount }), [amount]));
  const [batchId, setBatchId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const list = c.data || NO_ROWS;
  useEffect(() => { setErr(null); setBatchId(''); }, [target]);
  useEffect(() => { if (list.length && !batchId) setBatchId((list.find((x) => x.exact) || list[0]).id); }, [list, batchId]);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = target.receipt;
      await api.post(`/sc/claims/batches/${batchId}/deposits`, { date: r.date, amount: r.amount, reference: String(r.description || '').slice(0, 200), bank_import_id: target.importId, bank_row: r.row });
      toast.success('NDIA deposit recorded and the receipt cleared');
      onSaved(); onClose();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open={!!target} onClose={onClose} width="lg" title="Record as an NDIA deposit"
      sub={target ? `${money(target.receipt.amount)} received ${fmtDate(target.receipt.date)} · ${target.receipt.description || 'No description'}` : undefined}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} disabled={!batchId} icon={Landmark} onClick={save}>Record deposit</Button></>}>
      {c.loading && !c.data ? <LoadingBlock rows={3} /> : c.error ? <ErrorBlock error={c.error} onRetry={c.reload} /> : !list.length ? (
        <Empty icon={Landmark} title="No batch is waiting on an NDIA payment">Generate and upload a batch, and import its results, before recording its deposit.</Empty>
      ) : (
        <div role="radiogroup" aria-label="Which batch this deposit pays" className="space-y-1.5">
          {list.map((b) => (
            <label key={b.id} className={clsx('flex items-center gap-3 rounded-xl px-3.5 py-3 cursor-pointer ring-1 ring-inset', batchId === b.id ? 'ring-indigo-500 bg-indigo-50/50' : 'ring-slate-200 hover:bg-slate-50')}>
              <input type="radio" name="sc-dep-batch" className="w-4 h-4 accent-indigo-600" checked={batchId === b.id} onChange={() => setBatchId(b.id)} />
              <span className="flex-1 min-w-0">
                <span className="block text-[13.5px] font-semibold text-slate-900">Batch {b.batch_code} <span className="font-normal text-slate-500">· {periodText(b.period_start, b.period_end)}</span></span>
                <span className="block text-[12.5px] text-slate-600">{b.results_imported ? 'Paid per the NDIA results' : 'Claimed (results not imported yet)'}{b.deposits > 0 ? ` · ${money(b.deposits)} already deposited` : ''}</span>
              </span>
              <span className="text-right">
                <span className="block text-[13.5px] font-semibold tabular-nums">{money(b.expected)}</span>
                {b.exact && <Pill tone="emerald" label="Amount matches" size="xs" />}
              </span>
            </label>
          ))}
        </div>
      )}
      {err && <p className="mt-3 text-[13px] text-rose-600" role="alert">{err}</p>}
    </Modal>
  );
}

// Mark an unmatched receipt dealt with (8.8), with the reason kept on the audit trail.
function ClearReceiptModal({ target, onClose, onDone }) {
  const [code, setCode] = useState('not_sc_payment');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const r = target ? target.receipt : null;
  const seed = useRef(target);
  seed.current = target;
  const rowKey = target ? `${target.importId}|${target.receipt.row}` : '';
  useEffect(() => {
    if (!rowKey) return;
    const t = seed.current;
    setCode(t.receipt.invoice_number ? 'recorded_by_hand' : 'not_sc_payment');
    setInvoiceNo(t.receipt.invoice_number || ''); setNote(''); setErr(null);
  }, [rowKey]);
  const reason = CLEAR_REASONS.find((x) => x.value === code) || CLEAR_REASONS[0];
  const save = async (e) => {
    if (e) e.preventDefault();
    if (code === 'recorded_by_hand' && !invoiceNo.trim()) { setErr('Give the number of the invoice the payment is recorded against.'); return; }
    if (code === 'other' && !note.trim()) { setErr('Say what was done with this receipt.'); return; }
    setBusy(true); setErr(null);
    try {
      const res = await api.post(`/sc/claims/bank-imports/${target.importId}/receipts/${r.row}/clear`, {
        reason_code: code, note: note.trim() || undefined, invoice_number: code === 'recorded_by_hand' ? invoiceNo.trim() : undefined,
      });
      const left = Number(res.data?.to_review) || 0;
      toast.success(`Receipt marked dealt with. ${left ? `${plural(left, 'receipt')} left to review in this statement.` : 'Nothing left to review in this statement.'}`);
      if (res.data?.warning) toast(res.data.warning, { duration: 9000 });
      onDone();
      onClose();
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Modal open={!!target} onClose={busy ? undefined : onClose} width="md" title="Mark the receipt dealt with"
      sub={r ? `${money(r.amount)} received ${fmtDate(r.date)}: ${r.description || 'no description'}` : undefined}
      footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button type="submit" form="sc-clear-receipt-form" icon={CheckCircle2} loading={busy}>Mark dealt with</Button></>}>
      <form id="sc-clear-receipt-form" onSubmit={save} className="space-y-4">
        <Field label="Why it needs no match" required hint={reason.hint}>
          <Select value={code} onChange={(v) => { setCode(v); setErr(null); }} options={CLEAR_REASONS.map((x) => ({ value: x.value, label: x.label }))} />
        </Field>
        {code === 'recorded_by_hand' && (
          <Field label="Invoice number" required hint="The invoice the payment is recorded against, for example SCI00042.">
            <Input value={invoiceNo} maxLength={60} autoComplete="off" onChange={(e) => setInvoiceNo(e.target.value)} />
          </Field>
        )}
        <Field label={code === 'other' ? 'What was done with it' : 'Note'} required={code === 'other'} hint={code === 'other' ? 'Kept on the audit trail.' : 'Optional. Kept on the audit trail.'}>
          <Textarea rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {err && <Banner tone="red">{err}</Banner>}
      </form>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Budget forecast for the participant's Plan & Budgets tab (8.9)
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// rowActions(line) -> RowMenu items for a budget line (Plan & Budgets gives Edit and Delete);
// reloadSignal: change it to load the forecast again after a budget or service changed.
export function BudgetForecast({ coordinationId, rowActions, reloadSignal }) {
  const f = useLoad(coordinationId ? `/sc/claims/forecast/${coordinationId}` : null);
  const { reload: reloadForecast } = f;
  const firstSignal = useRef(reloadSignal);
  useEffect(() => { if (reloadSignal !== firstSignal.current) reloadForecast(); }, [reloadSignal, reloadForecast]);
  const s = useLoad(coordinationId ? '/sc/settings' : null);
  const rawPct = s.data ? s.data.budget_warning_pct : null;
  const pct = rawPct != null && rawPct !== '' && Number.isFinite(Number(rawPct)) ? Number(rawPct) : 10;
  const lines = useMemo(() => f.data?.lines || NO_ROWS, [f.data]);
  const t = f.data?.totals;
  const noPerm = !!f.error && /permission/i.test(f.error);
  const over = !!t && Number(t.remaining) < 0;
  const low = !!t && !over && Number(t.budget) > 0 && Number(t.remaining) < (Number(t.budget) * pct) / 100;
  const flagged = lines.filter((l) => l.warning || l.over);
  const leftPct = t && Number(t.budget) > 0 ? Math.round((Number(t.remaining) / Number(t.budget)) * 1000) / 10 : 0;

  const cols = [
    { id: 'name', header: 'Budget line', accessorFn: (l) => l.name, cell: ({ row: { original: l } }) => (
      <div className="min-w-[200px] space-y-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="font-semibold text-slate-900">{l.name}</p>
          {l.over ? <Pill tone="red" label="Over budget" /> : l.warning ? <Pill tone="amber" label={l.key === 'none' ? 'No budget line' : 'Budget low'} /> : null}
        </div>
        <p className="text-[12px] text-slate-500">
          {(l.support_items || []).filter(Boolean).join(', ') || 'No support item'}
          {l.budget_source === 'none' && l.key !== 'none' ? ' · no budget amount set' : ''}
        </p>
        <ForecastBar f={l} height="h-2" label={l.name} />
      </div>
    ) },
    { accessorKey: 'budget', header: 'Budget', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} /> },
    { accessorKey: 'paid', header: 'Paid', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} className="text-emerald-700" /> },
    { accessorKey: 'pending', header: 'Pending', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} className="text-sky-700" /> },
    { accessorKey: 'unclaimed', header: 'Unclaimed', meta: { align: 'right' }, cell: ({ getValue }) => <Money value={getValue()} className="text-amber-700" /> },
    { accessorKey: 'remaining', header: 'Remaining', meta: { align: 'right' }, cell: ({ row: { original: l } }) => (
      <Money value={l.remaining} className={clsx('font-semibold', Number(l.remaining) < 0 ? 'text-rose-600' : l.warning ? 'text-amber-700' : 'text-slate-900')} />
    ) },
    { id: 'used', header: 'Used', meta: { align: 'right', width: 110 }, accessorFn: (l) => Number(l.usedPct) || 0, cell: ({ row: { original: l } }) => (Number(l.budget) > 0 ? (
      <div className="space-y-1">
        <p className="tabular-nums">{l.usedPct}%</p>
        <Meter value={Number(l.usedPct) || 0} max={100} label={`${l.name}: ${l.usedPct}% used or committed`} />
      </div>
    ) : <span className="text-slate-500">—</span>) },
    ...(rowActions ? [{ id: 'menu', header: () => <span className="sr-only">Actions</span>, enableSorting: false, meta: { width: 44 }, cell: ({ row: { original: l } }) => {
      const items = rowActions(l).filter(Boolean);
      return items.length ? <RowMenu label={`Actions for ${l.name}`} items={items} /> : null;
    } }] : []),
  ];
  const footer = t ? (
    <>
      <td className="px-3 first:pl-4 py-2.5 border-t border-slate-200">Total</td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={t.budget} /></td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={t.paid} /></td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={t.pending} /></td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={t.unclaimed} /></td>
      <td className="px-3 py-2.5 border-t border-slate-200 text-right tabular-nums"><Money value={t.remaining} /></td>
      <td className="px-3 last:pr-4 py-2.5 border-t border-slate-200 text-right tabular-nums">{Number(t.budget) > 0 ? `${t.usedPct}%` : '—'}</td>
      {rowActions && <td className="border-t border-slate-200" />}
    </>
  ) : null;

  return (
    <Panel title="Budget forecast" icon={PiggyBank} hue="violet"
      sub="The support coordination budget against what is paid, waiting on claims, and logged but not yet claimed. Claims are checked against this forecast."
      actions={!noPerm && <Button size="sm" variant="ghost" icon={RefreshCw} loading={f.loading} onClick={f.reload}>Refresh</Button>}>
      {noPerm ? (
        <p className="text-[13px] text-slate-500">You need the Claiming, Reconciliation or View all hours permission to see the budget forecast.</p>
      ) : f.error ? <ErrorBlock error={f.error} onRetry={f.reload} /> : !f.data ? <LoadingBlock rows={3} /> : !lines.length ? (
        <Empty icon={PiggyBank} title="No support coordination budget line">
          Add the support coordination support item and its budget below. Claims are checked against it, and this forecast shows what is left.
        </Empty>
      ) : (
        <div className="space-y-5">
          {over ? (
            <Banner tone="red" title="Over budget">
              The forecast is {money(-Number(t.remaining))} over the support coordination budget. New lines are held on the Fix list until the budget is updated or time is corrected.
            </Banner>
          ) : low ? (
            <Banner tone="amber" title={`Less than ${pct}% of the budget left`}>
              The forecast leaves {money(t.remaining)} ({leftPct}% of {money(t.budget)}). Time can still be claimed; plan the remaining support with the participant.
            </Banner>
          ) : flagged.length > 0 ? (
            <Banner tone="amber" title="Check these budget lines">
              {flagged.map((l) => l.name).join(', ')} {flagged.length === 1 ? 'is' : 'are'} low, over, or not on a support coordination budget line.
            </Banner>
          ) : null}
          <div className="rounded-2xl bg-slate-50/70 px-4 py-4 space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <p className="text-[12.5px] font-semibold text-slate-600">Support coordination budget</p>
                <p className="text-[24px] font-semibold tabular-nums text-slate-900 leading-tight"><Money value={t.budget} /></p>
              </div>
              <p className={clsx('text-[12.5px] font-semibold tabular-nums', over ? 'text-rose-600' : low ? 'text-amber-700' : 'text-slate-600')}>
                {Number(t.budget) > 0 ? `${t.usedPct}% used or committed` : 'No budget amount set'}
              </p>
            </div>
            <ForecastBar f={t} height="h-4" label="Support coordination budget" />
            <ForecastLegend f={t} />
          </div>
          <DataTable columns={cols} data={lines} getRowId={(l) => l.key} dense footer={lines.length > 1 ? footer : null} />
          <p className="text-[12px] text-slate-500">
            Paid: money received. Pending: claimed and waiting for a result or payment (a rejected line waits for a re-claim or write-off). Unclaimed: billable time logged but not yet in a batch, at the service agreement rate. The warning level ({pct}%) is set in Settings, under Claims & invoices.
          </p>
        </div>
      )}
    </Panel>
  );
}
